/*
 * bc-tracking-async.js — deferred half of the Beyond Carat storefront tracker.
 * ---------------------------------------------------------------------------
 * Split out of snippets/datalayer-init.liquid (2026-07-02) to cut parse-blocking
 * inline JS from the <head>. Everything here is TIME-SHIFTED or EVENT-DRIVEN and
 * therefore does NOT need to run before GTM's first page_view:
 *
 *   - BcIdentityOutbox — the one send path every sensor below writes through
 *   - Identifier snapshot beacon (1.5s setTimeout to let async SDKs set cookies)
 *   - Cross-device Klaviyo id probe (retry loop)
 *   - bc_aid + Meta cookie → product-form carriers + cart.attributes sync
 *   - Scroll-depth tier listener (fires on actual scroll)
 *   - BcIdentify universal identity bridge (polling watchers, event hooks)
 *   - BcKlaviyoFormWatcher abandonment detection (form-interaction / unload)
 *
 * The critical core (dataLayer seed, bc_aid create, click-ID capture, gtag
 * user_properties) stays inline in datalayer-init.liquid and runs first.
 *
 * PAGE-CONTEXT CONTRACT: Liquid values are handed across via window.__bcCfg
 *   { customer: { id, email, phone, firstName, lastName } | null }
 * and identity state via window.__bcAid / window.__bcAidCandidate — set by the
 * inline core BEFORE this deferred script executes. No Liquid in this file.
 */
(function () {
  'use strict';

  var cfg = window.__bcCfg || {};
  var customer = cfg.customer || null;
  var bcAid = null;

  // ---- Shared constants + cookie helpers (copies of the inline core's; the
  //      bytes are free after this asset is cached once) ---------------------
  var ANON_ID_COOKIE = 'bc_aid';
  var BOOTSTRAP_KEY = 'bc_aid_bootstrap';
  var COOKIE_MAX_AGE = 60 * 60 * 24 * 730; // 2 years
  var UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  // The only origins the door's /handoff/mint allowlist accepts (bc-tracking
  // src/worker/cors.ts). Minting from anywhere else can only ever 403.
  var BC_MINT_ORIGINS = ['https://beyondcarat.com', 'https://www.beyondcarat.com'];
  var ENDPOINTS = {
    collect: 'https://events.beyondcarat.com/collect',
    // -v2, not the v1 path. v1 is served by bcrm-edge, whose CORS response
    // carries no `access-control-allow-credentials` and a stale allow-headers
    // list, so a credentialed fetch never survives its own preflight: the rich
    // snapshot bag has never actually landed from this asset. v2 is the
    // Ledger door's own route, sets allow-credentials, and is proven end to end
    // into the ledger. Note the trap this hides — the outbox's sendBeacon path
    // would incidentally dodge the broken preflight (a no-cors POST asks no
    // permission), so beacon-capable visitors would look fine while every
    // fetch-FALLBACK visitor kept dying silently on v1. Fixing the endpoint is
    // the fix; relying on the transport accident is not.
    identifierSnapshot: 'https://events.beyondcarat.com/identifier-snapshot-v2',
    handoff: 'https://events.beyondcarat.com/handoff',
    identifyToken: '/apps/bc/identify-token'
  };

  // ═══════════════════════════════════════════════════════════════════════
  // BcIdentityOutbox — the ONE send path (identity-capture rebuild, phase B)
  //
  // Eight capture mechanisms had grown eight transports, five dedup keys and
  // three different commit orders. A lost request was invisible in all of them
  // and, for the first-touch mint, permanent for the rest of the session.
  // Everything that talks to OUR door now goes through here; the sensors below
  // only observe and enqueue.
  //
  //   enqueue({kind, fields, ts, ctx})  — synchronous, never throws, never awaited
  //     · queue persisted to sessionStorage so it survives navigation and the
  //       popup-close/unload paths that used to eat the request outright
  //     · drains on enqueue, on pageshow, and every 5s while non-empty
  //     · transport: navigator.sendBeacon first (no preflight, and the browser
  //       owns the request once queued — the exact loss mode of a credentialed
  //       JSON fetch in an in-app webview), keepalive fetch as the fallback.
  //       The write_key rides in the BODY because a beacon cannot set headers;
  //       the door already accepts it there and strips it before enqueue.
  //     · EXCEPTION (D15): with no bc_aid cookie yet the credentialed fetch runs
  //       as well, because only a real response carries the Set-Cookie and the
  //       {bc_aid, canonical} echo that mints the durable id. The beacon still
  //       goes first for survivability; that pair genuinely collapses, because
  //       both sends transmit the SAME body string built once — the door's
  //       whole-payload hash sees one payload.
  //       Since the head core writes bc_aid on page 1 (2026-10-03) an aid
  //       always exists, so first_touch rides `needsResponse` (a credentialed
  //       fetch, never a beacon) instead of relying on this exception.
  //     · RETRIES are a different problem and are pinned separately. A retry
  //       REBUILDS the envelope: telemetry has moved on, href and cookies are
  //       re-read, the snapshot's `bt` token may have been attached. So the
  //       payload hash does NOT collapse a resend — a review probe confirmed an
  //       _fbp cookie landing between attempts changes the body. Every /collect
  //       envelope therefore carries `event_id` = the outbox entry id, minted
  //       once and persisted with the entry; the door prefers a supplied
  //       event_id over the body hash when deriving its idempotency key, so
  //       retry identity survives whatever the body does.
  //     · ack = beacon accepted (the browser guarantees the delivery attempt and
  //       hands us no response to wait on) or fetch 2xx. An entry leaves the
  //       queue ONLY on ack; anything else retries next drain/page with capped
  //       backoff. Ambiguity always re-sends — duplicates are free, losses aren't.
  //     · dedup: ONE signature scheme, and the durable marker is committed only
  //       AFTER the ack (D6/D14). This replaces the three divergent answers the
  //       file used to give: 'bc_identify_last_sig' (after — right),
  //       'bc_first_touch_ts' (BEFORE the send — one lost request killed the
  //       mint for the whole session) and 'bcrm_beacon_fired' (after — right).
  //       Server-side hash dedup at the door remains the authority.
  //
  // Fail-soft is absolute: sessionStorage can throw (private mode / partitioned
  // context), sendBeacon can be absent, JSON.stringify can choke on a cyclic
  // sensor payload. Every branch degrades to "send anyway" or "drop this one
  // observation" — never to an exception that reaches a sensor.
  // ═══════════════════════════════════════════════════════════════════════
  var OUTBOX_QUEUE_KEY = 'bc_outbox_q';
  var OUTBOX_ACK_KEY = 'bc_outbox_ack';
  var OUTBOX_TELEMETRY_KEY = 'bc_outbox_tel';
  var OUTBOX_MAX_ENTRIES = 50;       // beyond this we drop OLDEST, counted in telemetry
  var OUTBOX_MAX_ACKS = 100;         // durable dedup markers retained per session
  var OUTBOX_DRAIN_MS = 5000;
  var OUTBOX_MAX_PER_DRAIN = 10;
  var OUTBOX_MAX_TRIES = 10;
  var OUTBOX_KEEPALIVE_MAX = 32768;  // keepalive's ceiling is 64KB/spec; stay clear of it
  var TELEMETRY_MAX_PER_MODULE = 3;

  // Dedup signatures — one namespace for the whole file.
  var DEDUP_FIRST_TOUCH = 'first_touch';
  var DEDUP_SNAPSHOT = 'identifier_snapshot';
  var DEDUP_IDENTIFY = 'bc_identify_emit|';
  var DEDUP_MINT_FAILED = 'handoff_mint_failed|';

  // ─── Payload contract version (2026-09-05, perf item 21) ────────────────
  // Every browser-built body carries `capture_v` so the door can tell which
  // contract produced it instead of inferring one from absence. v2 stops
  // sending six things no reader in bc-tracking/src ever read (verified by
  // grep over the whole `src/` tree on 2026-09-05, and by the readers named at
  // each site):
  //   · `raw_capture`                  — 1.3–1.5 MB/day, zero readers
  //   · the six FLAT device duplicates — extractVisitorDevice() reads
  //     `payload.device` FIRST (visitor-identifiers.ts: ordered =
  //     [device, fingerprint, payload]), so the flat copies only lost a tie
  //   · `fingerprint_components.math`  — 4 distinct values in 2,170 samples
  //   · empty-string cookie keys       — `_fbc: ""` is not an identifier
  //   · `payload.ua`                   — the door reads the request HEADER
  //   · `payload.event`                — `event_name` is the field that is read
  // A v1 body is still accepted by the door unchanged; nothing about this is a
  // migration, it is a stop-sending.
  var CAPTURE_V = 2;

  // The session vocabulary, shared with bc-page-vitals / the relay assets AND
  // (since the MEV fix r1, contract 1) the Shopify custom pixel: `bc_sess`,
  // refreshed against a 30-minute idle gap. The ONE visit id for every sender.
  // Source of truth = the first-party SESSION cookie `bc_sess` (decided first
  // by the inline head core, snippets/datalayer-init.liquid, before the pixel
  // sandbox exists), so every tab and the pixel share it; sessionStorage
  // `bc_sess` is the fallback when cookies fail. Idle time: localStorage
  // `bc_sess_ts` (shared by tabs) with a sessionStorage copy — the newest wins
  // — mirrored to the session cookie `bc_sess_ts` so the custom pixel applies
  // the same idle rule on pages this script never runs on (review r2 R2-B2).
  var SESSION_KEY = 'bc_sess';
  var SESSION_TS_KEY = 'bc_sess_ts';
  var SESSION_GAP_MS = 30 * 60 * 1000;
  var SESSION_ID_RE = /^[A-Za-z0-9-]{8,64}$/;
  var sessionIdFallback = null;
  function currentSessionId() {
    var now = Date.now();
    var id = '', tabId = null, lastTs = 0, tabOk = false;
    try { id = readCookie(SESSION_KEY); } catch (e) { id = ''; }
    if (!SESSION_ID_RE.test(id || '')) id = '';
    try {
      tabId = sessionStorage.getItem(SESSION_KEY);
      lastTs = parseInt(sessionStorage.getItem(SESSION_TS_KEY) || '0', 10) || 0;
      tabOk = true;
    } catch (e) {}
    try { lastTs = Math.max(lastTs, parseInt(localStorage.getItem(SESSION_TS_KEY) || '0', 10) || 0); } catch (e) {}
    try { lastTs = Math.max(lastTs, parseInt(readCookie(SESSION_TS_KEY) || '0', 10) || 0); } catch (e) {}
    if (!id && tabId && SESSION_ID_RE.test(tabId)) id = tabId;
    if (!id || (lastTs && (now - lastTs) > SESSION_GAP_MS)) {
      id = (window.crypto && crypto.randomUUID) ? crypto.randomUUID()
        : ('sess-' + now.toString(36) + '-' + Math.random().toString(36).slice(2, 10));
    }
    try { if (readCookie(SESSION_KEY) !== id) writeCookie(SESSION_KEY, id, 'session'); } catch (e) {}
    try { writeCookie(SESSION_TS_KEY, String(now), 'session'); } catch (e) {}
    try { sessionStorage.setItem(SESSION_KEY, id); sessionStorage.setItem(SESSION_TS_KEY, String(now)); } catch (e) {}
    try { localStorage.setItem(SESSION_TS_KEY, String(now)); } catch (e) {}
    if (!tabOk && readCookie(SESSION_KEY) !== id) {
      // Neither the cookie nor sessionStorage holds it: keep one id for this page.
      if (!sessionIdFallback) sessionIdFallback = 'nosess-' + now.toString(36) + '-' + Math.random().toString(36).slice(2, 10);
      return sessionIdFallback;
    }
    return id;
  }

  // ONE session rule, published (round 2). `bc-relay-shared.js:81` and
  // `bc-page-vitals.js:64` each carry their own hand-written copy of the block
  // above — same keys, same 30-minute gap, three places to keep in step. Both
  // are `defer` scripts rendered at the END of <body> (theme.liquid:1400/1403)
  // while this one is `defer` in <head`, so this assignment has always run by
  // the time they look. They keep their local copy as a fallback for the ways
  // this line can fail to run AT ALL: the asset 404s (a snippet deployed
  // without its asset), the CSP refuses the script, or something above it
  // throws. NOT because of the bot gate — corrected round 3, 2026-09-06. The
  // only bot gate in this file is `if (isBot()) return` inside the handoff
  // IIFE (~:3908), which returns from THAT IIFE only and says so at its own
  // call site; there is no top-level return anywhere above this assignment. A
  // missing session id must never be an exception, whatever the cause.
  //
  // SIDE EFFECT, stated because it is real: every call REFRESHES `bc_sess_ts`,
  // so the 30-minute idle window restarts on each read. The only non-user
  // caller is the outbox drop path (`releaseSessionDeviceClaim` /
  // `releaseSnapshotDeviceClaim` → `parseDeviceClaim` → here) and the snapshot
  // send path. An entry that will never be accepted lives at most
  // 2+4+8+16+32+60+60+60+60 s of capped backoff ≈ 5 minutes before
  // OUTBOX_MAX_TRIES drops it, so the worst case is a session kept alive ~5
  // minutes past the visitor's last real action, once, while a door outage
  // drains. Not worth a non-refreshing read path.
  try { window.__bcSessionId = currentSessionId; } catch (e) {}

  function storageGet(key) {
    try { return sessionStorage.getItem(key); } catch (e) { return null; }
  }
  function storageSet(key, value) {
    try { sessionStorage.setItem(key, value); return true; } catch (e) { return false; }
  }
  function storageRemove(key) {
    try { sessionStorage.removeItem(key); return true; } catch (e) { return false; }
  }
  // ─── session-scoped, device-signed device claims (perf round 1, A2 + A3) ──
  //
  // Both device lanes hold a "this session already sent its device bag" claim
  // in sessionStorage — `bc_device_sent` (session lane, keyed on the claiming
  // event id) and `bc_device_snap` (snapshot lane, keyed on the dedup
  // signature). Both were keyed on the CLAIM ALONE.
  //
  // A2. `currentSessionId()` above rotates `bc_sess` after a 30-minute idle
  // gap, and touched neither key. So a tab left open over lunch came back with
  // a fresh session id and a claim belonging to the session before it: the
  // snapshot lane sent `device_ref = <new session id>` for a session that has
  // no device row, the session lane refused to attach a bag, and NEITHER lane
  // sent one again for the rest of the tab's life. A `device_ref` pointing at
  // nothing is worse than no reference: it states a fact that is not true.
  //
  // A3 — AND WHAT IT ACTUALLY BUYS, corrected in round 2 (H5).
  //
  // `fold()` in the projections is COALESCE(incoming, existing) (0066:294-295,
  // src/ingress/projections.ts:700-701), so `visitors.browser_env_v1` is
  // FIRST-EVER for the visitor — not first-of-session, and never latest. A
  // re-sent bag therefore does NOT replace it, in this session or any later
  // one. Round 1's note said otherwise and was wrong.
  //
  // What a re-send DOES achieve: `writeVisitorObservation`
  // (src/ingress/visitor-observations.ts:114) writes a `visitor_observations`
  // row carrying the new `device` bag and the `browser_env_v1` computed from
  // it for that signal. That row is the durable record that the device moved.
  // The `visitors` column stays at its first value, and the two readers that
  // consume it — `measure-cross-device-keys.ts`, `key-buckets.ts` — read it
  // from `visitors`, so they see no change. Re-sending buys an observation
  // ROW, nothing more; it is worth the bytes because without it a device
  // change leaves no trace at all, but it is not a repair of the visitor row.
  //
  // The claim carries a cheap signature of the STABLE facts (below) so a real
  // device change voids it and the bag is re-sent once.
  //
  // Stored form: "<session id>|<device signature>|<claim>" — the claim itself may
  // contain the separator (the snapshot lane's is "identifier_snapshot|<hash>"),
  // so only the FIRST TWO fields are split off and the rest is rejoined. A value
  // with no separators is a pre-round-1 claim and is treated as stale, which
  // costs one extra device bag per open tab at deploy and nothing after.
  var CLAIM_SEP = '|';

  // THREE STABLE FACTS — deliberately NOT the whole of `browser_env_v1`.
  //
  // `browserEnvTupleV1` (src/shared/browser-env.ts:141-152) needs EIGHT
  // components: screen width, screen height, DPR to three decimals,
  // `hardwareConcurrency`, the resolved timezone, `os_family`,
  // `browser_family` (both derived at the INGRESS from the signal's `ua`, not
  // sent by the browser) and up to `ENV_TUPLE_LANGUAGE_COUNT` = 3 languages.
  // Round 1 said "six" and sliced six languages; both were wrong.
  //
  // Round 2 (H5): DPR and the screen dimensions are OUT of this signature.
  // They are in the tuple, but they are not facts about WHICH DEVICE this is:
  // desktop browser zoom moves `devicePixelRatio`, and an Android rotation
  // moves `screen.width`/`screen.height`. Either would void the claim and buy
  // a full bag rebuild — WebGL probe, five `matchMedia` calls, navigation
  // timing, media-device enumeration — for a device that never changed, which
  // is the exact cost item 22 exists to avoid. What stays is the set that means
  // a genuinely different machine or a genuinely relocated one:
  //
  //   · `hardwareConcurrency` — core count; a different device
  //   · the resolved IANA timezone — a flight, or a machine in another region
  //   · the language list — a UI language change
  //
  // All three are plain property reads: no WebGL, no matchMedia, no navigation
  // timing, no media enumeration, so computing this on every tick is free. It
  // is also deliberately NOT part of SNAPSHOT_HASH_FIELDS: it re-sends the BAG,
  // it does not re-key the snapshot, so it cannot flood re-ingest.
  function deviceEnvSignature() {
    return [
      envValue(function () { return navigator.hardwareConcurrency; }, ''),
      envValue(function () { return Intl.DateTimeFormat().resolvedOptions().timeZone; }, ''),
      // Lowercased, deduplicated, SORTED and then sliced to three — the same
      // order of operations as browserEnvTupleV1, which sorts before it slices.
      // Slicing first (round 1 took the first six as they came) can keep a
      // different three for the same browser.
      envValue(function () {
        var seen = {};
        var out = [];
        var list = navigator.languages || [];
        for (var i = 0; i < list.length; i++) {
          var l = String(list[i] || '').toLowerCase();
          if (!l || seen[l]) continue;
          seen[l] = 1;
          out.push(l);
        }
        return out.sort().slice(0, 3).join(',');
      }, '')
    ].join('~');
  }

  /** Write a claim stamped with the session and the device it belongs to. */
  function writeDeviceClaim(key, claim) {
    return storageSet(key, currentSessionId() + CLAIM_SEP + deviceEnvSignature() + CLAIM_SEP + claim);
  }
  /** Build the same value, for the in-memory fallback (Safari private mode). */
  function deviceClaimValue(claim) {
    return currentSessionId() + CLAIM_SEP + deviceEnvSignature() + CLAIM_SEP + claim;
  }
  /**
   * The claim inside ONE stored value, if it belongs to this session and this
   * device — otherwise null. Exported separately from readDeviceClaim() because
   * the two release paths must interrogate the two stores INDEPENDENTLY: a live
   * sessionStorage value would otherwise hide an in-memory claim that also
   * needs clearing.
   */
  function parseDeviceClaim(raw) {
    if (!raw) return null;
    var parts = String(raw).split(CLAIM_SEP);
    if (parts.length < 3) return null;                  // pre-round-1: stale
    if (parts[0] !== currentSessionId()) return null;   // another session's
    if (parts[1] !== deviceEnvSignature()) return null; // another device's
    return parts.slice(2).join(CLAIM_SEP);
  }
  /**
   * The claim held for THIS session on THIS device, or null. `memo` is the
   * in-memory fallback used when sessionStorage throws (Safari private mode);
   * it is stamped the same way, so a dead store does not mean a claim that
   * outlives its session either.
   */
  function readDeviceClaim(key, memo) {
    return parseDeviceClaim(storageGet(key) || memo || null);
  }

  function parseStoredArray(raw) {
    if (!raw) return null;
    try {
      var v = JSON.parse(raw);
      return Object.prototype.toString.call(v) === '[object Array]' ? v : null;
    } catch (e) { return null; }
  }

  // ---- client telemetry (D19) -------------------------------------------
  // #1152's capture loss was invisible for a full day because the file is one
  // IIFE full of empty catches: a module that threw took every module declared
  // after it with it, silently. Errors are now counted per module and ride the
  // NEXT envelope, so the ledger sees what the browser saw.
  var telemetry = (function () {
    var stored = null;
    try { stored = JSON.parse(storageGet(OUTBOX_TELEMETRY_KEY) || 'null'); } catch (e) {}
    if (stored && typeof stored === 'object' && stored.errors && typeof stored.errors === 'object') {
      if (typeof stored.dropped !== 'number') stored.dropped = 0;
      if (!stored.flags || typeof stored.flags !== 'object') stored.flags = {};
      return stored;
    }
    return { errors: {}, last: null, dropped: 0, flags: {} };
  })();
  window.__bcTelemetry = telemetry;

  function saveTelemetry() { storageSet(OUTBOX_TELEMETRY_KEY, JSON.stringify(telemetry)); }

  // The message leaves the browser, and a thrown value carries whatever the
  // thrower put in it — so anything person-shaped is removed before it can.
  function scrubTelemetryMessage(err) {
    var msg = '';
    try { msg = String((err && err.message) || err || ''); } catch (e) { msg = ''; }
    return msg
      .replace(/[^\s@]+@[^\s@]+/g, '[email]')
      .replace(/\d{6,}/g, '[num]')
      .slice(0, 120);
  }

  function noteModuleError(moduleName, err) {
    try {
      var seen = telemetry.errors[moduleName] || 0;
      if (seen >= TELEMETRY_MAX_PER_MODULE) return;   // 3 per module per session
      telemetry.errors[moduleName] = seen + 1;
      telemetry.last = { module: moduleName, msg: scrubTelemetryMessage(err) };
      saveTelemetry();
    } catch (e) {}
  }

  // ---- contract canaries (D20) -------------------------------------------
  // `errors` counts OUR code failing. `flags` records a third-party contract
  // that looks broken WITHOUT anything having thrown — the silent kind, where
  // Klaviyo ships a release, our selector or private-internals assumption stops
  // matching, and capture quietly goes to zero with a clean console. Set once
  // per session; the server reads them as rates across sessions, never as a
  // per-visitor verdict.
  function setTelemetryFlag(name) {
    try {
      if (telemetry.flags[name]) return;
      telemetry.flags[name] = 1;
      saveTelemetry();
    } catch (e) {}
  }

  // Wrap every top-level module so one module's throw can no longer end
  // initialization for the modules declared after it.
  function guard(moduleName, fn) {
    return function () {
      try { return fn.apply(this, arguments); }
      catch (e) { noteModuleError(moduleName, e); return null; }
    };
  }

  // ---- consent (D9) ------------------------------------------------------
  // ONE switch for consent-gating what the OUTBOX sends. Default false =
  // capture everywhere, per owner: privacy/compliance is explicitly not the
  // current focus, and the asymmetry this replaces (Watcher E gated on
  // Shopify.customerPrivacy, watchers A–D and the abandonment identifier not
  // gated at all) was accidental rather than designed.
  //
  // BE PRECISE ABOUT ITS REACH — it is narrower than "gates identity capture",
  // and a reviewer probe confirmed each of these. Flipping it to true stops:
  //   · every outbox send (identify, first_touch, checkout_intent, snapshot),
  //     because outboxEnqueue is the single door they all pass through.
  // It does NOT stop:
  //   · the dataLayer copy of bc_identify_emit — the old GTM→sGTM→BCRM pipeline
  //     keeps receiving identity, because emit() pushes before it enqueues;
  //   · the three Klaviyo wires (chokepoint stamp, _learnq identify, unload
  //     profile-import beacon) — those talk to Klaviyo, not to us;
  //   · the Crisp mirrors (user:email / nickname / phone, session:data).
  // Widening it to those sinks is a deliberate owner decision, not an
  // implementation detail — it changes what third parties receive, so it is
  // deliberately NOT bundled into this constant today.
  //
  // The consent STATE rides every envelope either way (B12), so the ledger can
  // filter retroactively without the storefront being re-instrumented.
  var CONSENT_GATE = false;

  // 2026-09-29 (lane A): the ONE helper, window.bcConsent
  // (snippets/consent-mode-default.liquid), answers first. userCanBeTracked()
  // below is Shopify's REGION DEFAULT (false for an unanswered visitor in an
  // opt-in region) and is kept only as the no-helper fallback. 'denied' = an
  // explicit analytics or marketing "no"; null = not answered (unknown).
  function readConsentState() {
    try {
      if (window.bcConsent) {
        if (!window.bcConsent.captureAllowed()) return 'denied';
        return window.bcConsent.stamp().state;
      }
    } catch (e) {}
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (!cp) return null;
      if (typeof cp.userCanBeTracked === 'function') {
        return cp.userCanBeTracked() ? 'granted' : 'denied';
      }
      // Older/partial API surface: derive from the marketing purpose, which is
      // the one this capture layer actually depends on. Values are 'yes'|'no'|''.
      if (typeof cp.currentVisitorConsent === 'function') {
        var c = cp.currentVisitorConsent();
        if (c && c.marketing === 'yes') return 'granted';
        if (c && c.marketing === 'no') return 'denied';
      }
      return null;
    } catch (e) { return null; }
  }

  // The door `consent` stamp: the full explicit-answer object (same keys the
  // checkout pixel stamps — state/marketing/analytics/sale_of_data) so the
  // server suppresses ad destinations on a "no" or a sale/share opt-out. An
  // explicit caller `consent_state` still wins, as before.
  function consentStamp(payload) {
    if (payload && payload.consent_state) return { state: payload.consent_state };
    try { if (window.bcConsent) return window.bcConsent.stamp(); } catch (e) {}
    return { state: readConsentState() };
  }

  // Door sends WAIT (bounded) for the privacy API, so the stamp above is
  // never read before Shopify can answer — first_touch used to leave with
  // `{state:null}` for an all-denied visitor (lane A, C2). Unknown after the
  // bounded wait still sends (owner decision 2026-09-29).
  function consentPending() {
    try { return !!(window.bcConsent && !window.bcConsent.settled()); } catch (e) { return false; }
  }
  var consentWaitArmed = false;
  function drainWhenConsentSettles() {
    if (consentWaitArmed) return;
    consentWaitArmed = true;
    try { window.bcConsent.whenSettled(function () { outboxDrain(); }); } catch (e) {}
  }

  function consentAllowsCapture() {
    if (!CONSENT_GATE) return true;
    return readConsentState() !== 'denied';
  }

  // ---- burst-then-trickle polling (D18) ----------------------------------
  // Every watcher used to carry its own shot-clock — 2min for the __kla_id
  // cookie, 60s for the Klaviyo chokepoint install, 30s for Crisp, 5s for the
  // klaviyo-id probe — and then give up silently. #1152's shopper browsed for
  // ten minutes before signing up, which is past every one of them, so nothing
  // was watching at the only moment that mattered. These polls now run for the
  // life of the page: 2s while the page is young, 30s forever after.
  //
  // `fn` returns true when it is satisfied and wants the poll torn down.
  //
  // The interaction retry is what pays for the slower initial cadence: the
  // chokepoint installer used to tick every 250ms so it would be wrapped before
  // a popup submit. A capture-phase click/keydown listener beats that outright —
  // typing in the popup or clicking Submit re-attempts the install BEFORE the
  // form's own React handler runs, so the dispatcher is wrapped by the time
  // Klaviyo pushes through it.
  // 2026-09-05 (perf item 25). "For the life of the page" was measured as four
  // pollers ticking every 30 s forever, in every tab, for every anonymous
  // visitor who will never sign up — a background tab left open overnight was
  // 2,880 wake-ups per poller doing a property read that has answered `false`
  // 2,879 times. Three things change, none of which shortens the window that
  // #1152 needed:
  //   1. BACKOFF, not a flat trickle: 2 s while the page is young, 30 s to
  //      five minutes, 5 min to fifteen. #1152's shopper signed up ten minutes
  //      in and is still covered, at 1/10th the wake-ups.
  //   2. A STOP at fifteen minutes — but an idle stop, not a teardown: the
  //      interaction listener stays bound and re-arms the poll (rearm()) on the
  //      first click or keystroke, and identify/login/form paths call
  //      rearmPolls() directly. A visitor who comes back to the tab and starts
  //      typing is watched again from the 2 s burst.
  //   3. NOTHING TICKS IN A HIDDEN TAB. A poll due while the tab is hidden is
  //      deferred to the visibilitychange→visible edge and attempted there —
  //      which is strictly better than the old behaviour, because a cookie
  //      that appeared while the tab was backgrounded is now seen at the
  //      moment the shopper returns rather than up to 30 s later.
  var POLL_BURST_MS = 2000;
  var POLL_BURST_WINDOW_MS = 60000;
  var POLL_TRICKLE_MS = 30000;
  var POLL_TRICKLE_WINDOW_MS = 300000;   // 5 min: 30 s ticks until here …
  var POLL_SLOW_MS = 300000;             // … then one tick every 5 min …
  var POLL_MAX_MS = 900000;              // … and idle at 15 min, re-armable.

  // Every live poll, so an identify / login / form event can re-arm all of
  // them at once. Visibility is handled per-poll (see park()), not from here:
  // a poll that is mid-interval must keep its own timer rather than be
  // re-attempted on every tab switch.
  var activePolls = [];
  // 2026-09-08. rearm() is re-entrant-safe (see attempt()), but this loop
  // walks a COPY: a poll satisfied by its rearm calls stop(), which splices
  // it out of activePolls and would skip the poll behind it.
  var rearmingPolls = false;
  function rearmPolls() {
    if (rearmingPolls) return;
    rearmingPolls = true;
    try {
      var polls = activePolls.slice();
      for (var i = 0; i < polls.length; i++) {
        try { polls[i].rearm(); } catch (e) {}
      }
    } finally { rearmingPolls = false; }
  }

  // opts.interaction === false opts out of the click/keydown retry. Only the
  // hook INSTALLERS want it; a poll that never completes would otherwise stay
  // bound for the life of the page and re-run on every keystroke, which is a
  // real cost for the one poll whose check is not a cheap property read.
  function pollUntilDone(moduleName, fn, opts) {
    var wantsInteraction = !(opts && opts.interaction === false);
    var started = Date.now();
    var timer = null;
    var stopped = false;      // DONE — torn down for good
    var bound = false;

    function visible() {
      try { return document.visibilityState !== 'hidden'; } catch (e) { return true; }
    }

    function unbind() {
      if (!bound) return;
      bound = false;
      try {
        document.removeEventListener('click', onInteraction, true);
        document.removeEventListener('keydown', onInteraction, true);
      } catch (e) {}
    }
    function bind() {
      if (bound || !wantsInteraction) return;
      bound = true;
      try {
        document.addEventListener('click', onInteraction, true);
        document.addEventListener('keydown', onInteraction, true);
      } catch (e) {}
    }
    function stop() {
      stopped = true;
      if (timer) { try { clearTimeout(timer); } catch (e) {} timer = null; }
      unbind();
      unpark();
      for (var i = 0; i < activePolls.length; i++) {
        if (activePolls[i] === handle) { activePolls.splice(i, 1); break; }
      }
    }
    // Go quiet WITHOUT tearing down: no timer, but a one-shot resume listener
    // stays bound so the poll comes back the moment the visitor does. This is
    // the difference between "we stopped watching" and "we stopped burning a
    // timer in a tab nobody is looking at".
    //
    // The park listener is bound even when `opts.interaction === false`. That
    // option exists to keep an EXPENSIVE per-tick check off every keystroke
    // while the poll is running; a PARKED poll runs nothing at all, so the
    // same argument says the opposite here — without it, the snapshot watcher
    // would go permanently blind on a page left open past fifteen minutes.
    var parkBound = false;
    function onResume() {
      unpark();
      rearm();
    }
    function unpark() {
      if (!parkBound) return;
      parkBound = false;
      try {
        document.removeEventListener('click', onResume, true);
        document.removeEventListener('keydown', onResume, true);
        document.removeEventListener('visibilitychange', onVisible, false);
      } catch (e) {}
    }
    function onVisible() {
      if (document.visibilityState === 'visible') onResume();
    }
    // Round 1 (A5): the resume listeners are registered BEFORE bind(), and
    // onInteraction defers to onResume while parked. Both halves are needed and
    // neither is redundant.
    //
    // The defect: on a poller with `interaction: true` (the default — the
    // Klaviyo id probe, the Crisp probe and both identify watchers) `bind()`
    // has already run inside the first attempt(), long before anything parks.
    // So on the document's capture-phase click list onInteraction sits FIRST.
    // A click then ran onInteraction -> attempt() -> unpark(), which removed
    // onResume DURING the dispatch — and a listener removed by an earlier
    // listener on the same event target is never invoked (DOM spec; verified
    // in Chromium). onResume was therefore skipped, `started` was never reset,
    // and schedule() saw age >= POLL_MAX_MS and re-parked at once: one tick per
    // click for the life of the page, never a resumed 2 s burst. The journey it
    // broke: a tab left open past fifteen minutes, clicked, and only THEN
    // visited by the Klaviyo SDK — the one tick ran before the SDK existed and
    // nothing ticked again, so the visitor was never joined to their profile.
    function park() {
      if (timer) { try { clearTimeout(timer); } catch (e) {} timer = null; }
      if (!parkBound) {
        parkBound = true;
        try {
          // The tab edge is ours alone; bind() never registers it.
          document.addEventListener('visibilitychange', onVisible, false);
          // click / keydown ONLY when this poll has no interaction listener of
          // its own. When it does, onInteraction is already on the list and
          // delegates to onResume while parked — which is both order-proof and
          // free of the double tick two listeners on one click would cause.
          if (!wantsInteraction) {
            document.addEventListener('click', onResume, true);
            document.addEventListener('keydown', onResume, true);
          }
        } catch (e) { parkBound = false; }
      }
      bind();
    }
    function delayFor(age) {
      if (age < POLL_BURST_WINDOW_MS) return POLL_BURST_MS;
      if (age < POLL_TRICKLE_WINDOW_MS) return POLL_TRICKLE_MS;
      return POLL_SLOW_MS;
    }
    function schedule() {
      if (stopped) return;
      var age = Date.now() - started;
      if (age >= POLL_MAX_MS) { park(); return; }
      // A hidden tab schedules nothing at all. The tick is remembered and run
      // on the visible edge (see wake), so no observation is lost — it simply
      // happens when somebody is there to see it.
      if (!visible()) { park(); return; }
      try { timer = setTimeout(attempt, delayFor(age)); } catch (e) {}
    }
    // 2026-09-08 — re-entrancy. Watcher A's fn() calls emit(), and emit()
    // calls rearmPolls() — which came straight back into THIS poll's attempt()
    // while its fn() was still on the stack: fn → emit → rearm → attempt → fn …
    // until the browser threw RangeError (13 mobile shoppers on 2026-09-07,
    // the day this cadence shipped; every one carried an existing __kla_id).
    // A rearm that lands mid-attempt now only resets the clock — schedule()
    // after fn() returns already reads `started`, so the burst cadence still
    // applies — and never re-enters fn().
    var attempting = false;
    function attempt() {
      if (stopped || attempting) return;
      unpark();
      timer = null;
      var done = false;
      attempting = true;
      try { done = !!fn(); }
      catch (e) { noteModuleError(moduleName, e); }
      finally { attempting = false; }
      if (done) { stop(); return; }
      bind();
      schedule();
    }
    function onInteraction() {
      if (stopped) return;
      // PARKED means the visitor has come back to a poll that gave up, so the
      // answer is "restart the clock", not "take one tick and park again".
      // This is the half that does not depend on listener order at all: on an
      // already-bound poller onInteraction runs first whatever park() does.
      if (parkBound) { onResume(); return; }
      if (timer) { try { clearTimeout(timer); } catch (e) {} timer = null; }
      attempt();
    }
    // Resume: restart the clock so the burst cadence applies again. Called by
    // the park listener (click / keydown / tab-visible) and by rearmPolls()
    // from the identify / login / form paths.
    function rearm() {
      if (stopped) return;
      started = Date.now();
      if (attempting) return;   // mid-fn(): clock reset is the whole re-arm
      if (timer) { try { clearTimeout(timer); } catch (e) {} timer = null; }
      if (!visible()) { park(); return; }
      attempt();
    }

    var handle = { stop: stop, rearm: rearm };
    activePolls.push(handle);
    attempt();   // first look is immediate; the SDK may already be here
    return handle;
  }

  // ---- per-context Klaviyo stamps (D16) ----------------------------------
  // A single `bc_aid` slot on the Klaviyo profile is last-write-wins, so a
  // shopper who browses in the Meta in-app browser and buys in Safari loses the
  // first context outright — that is precisely how #1152's Threads touch
  // vanished. We keep writing the legacy slot (the CRM's klaviyo-new-profiles
  // cron reads it) and ADD one key per browser context.
  //
  // Klaviyo's identify MERGES properties onto the profile, so this only ever
  // adds keys: nothing in this file reads, rewrites or removes another
  // context's `bc_aid_*` key.
  function klaviyoAidProps(target) {
    var props = target || {};
    try {
      var aid = currentBcAid();
      if (!aid) return props;
      props.bc_aid = aid;                                        // legacy slot
      props['bc_aid_' + aid.slice(0, 8)] = Math.floor(Date.now() / 1000);
    } catch (e) {}
    return props;
  }

  // ---- the ONE Klaviyo identify sender (B8) ------------------------------
  // Three call sites used to build their own identify payload: the chokepoint
  // install stamp, the abandonment `_learnq` push, and the unload
  // profile-import beacon. Same action, three payload builders, three places
  // for a property to go missing — D16's per-context key had to be threaded
  // through all three for exactly that reason, and the next property would too.
  //
  // The WIRES genuinely differ and have to, so `via` selects one while the
  // payload is built once:
  //   'chokepoint' → the dispatcher we wrapped; a bc_aid stamp with no email
  //   'learnq'     → window._learnq, the in-session queue Klaviyo drains
  //   'beacon'     → Klaviyo's client profile-import over sendBeacon. THEIR
  //                  endpoint, not ours, so the outbox never owns it, and the
  //                  only one that survives unload.
  // BeyondCarat's public Klaviyo company id (also the public_api_key). Hard-
  // coded because reading it off window.klaviyo at unload time is racy.
  var KLAVIYO_COMPANY_ID = 'UQxE4W';

  function sendKlaviyoIdentify(via, opts) {
    opts = opts || {};
    try {
      var props = klaviyoAidProps(opts.email ? { $email: opts.email } : {});

      if (via === 'chokepoint') {
        if (!props.bc_aid || typeof opts.push !== 'function') return false;
        opts.push(['identify', props]);
        return true;
      }

      if (via === 'learnq') {
        window._learnq = window._learnq || [];
        window._learnq.push(['identify', props]);
        return true;
      }

      if (via === 'beacon') {
        if (!opts.email) return false;
        if (!navigator || typeof navigator.sendBeacon !== 'function') return false;
        // The profile-import shape is Klaviyo's, not ours: identity fields sit
        // on `attributes`, everything custom under `attributes.properties`.
        var attrs = { email: opts.email };
        var extra = {};
        var hasExtra = false;
        for (var k in props) {
          if (!Object.prototype.hasOwnProperty.call(props, k) || k === '$email') continue;
          extra[k] = props[k];
          hasExtra = true;
        }
        if (hasExtra) attrs.properties = extra;
        var body = JSON.stringify({ data: { type: 'profile', attributes: attrs } });
        navigator.sendBeacon(
          'https://a.klaviyo.com/client/profiles/?company_id=' + encodeURIComponent(KLAVIYO_COMPANY_ID),
          new Blob([body], { type: 'application/json' })
        );
        return true;
      }

      return false;
    } catch (e) {
      noteModuleError('klaviyo_identify', e);
      return false;
    }
  }

  // Change-detection hash (djb2). NOT a security primitive and never treated as
  // one — it exists so the snapshot can tell "the bag I already sent" from "the
  // bag as it is now" in one cheap comparison.
  function bcHash(str) {
    var h = 5381;
    var s = String(str || '');
    for (var i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
    return (h >>> 0).toString(16);
  }

  // ---- queue + durable dedup markers ------------------------------------
  var outboxQueue = parseStoredArray(storageGet(OUTBOX_QUEUE_KEY)) || [];
  var outboxAcks = parseStoredArray(storageGet(OUTBOX_ACK_KEY)) || [];
  var outboxPending = {};   // signatures enqueued during THIS page load
  var outboxInFlight = {};  // entry ids currently on the wire
  var outboxSeq = 0;
  var outboxTimer = null;
  var outboxDraining = false;

  function saveQueue() { storageSet(OUTBOX_QUEUE_KEY, JSON.stringify(outboxQueue)); }
  function saveAcks() { storageSet(OUTBOX_ACK_KEY, JSON.stringify(outboxAcks)); }

  function outboxAcked(sig) {
    for (var i = 0; i < outboxAcks.length; i++) if (outboxAcks[i] === sig) return true;
    return false;
  }

  // "Have we already delivered — or queued this page — this exact observation?"
  // The single dedup question the whole file asks. A queued-but-unacked entry
  // from an EARLIER page deliberately does NOT count as seen: the sensor should
  // still push its dataLayer copy (the old pipeline gets its own retry) while
  // the outbox retries the ledger copy.
  function outboxSeen(sig) {
    if (!sig) return false;
    return !!outboxPending[sig] || outboxAcked(sig);
  }

  function outboxForget(sig) {
    if (!sig) return;
    try {
      delete outboxPending[sig];
      var next = [];
      for (var i = 0; i < outboxAcks.length; i++) if (outboxAcks[i] !== sig) next.push(outboxAcks[i]);
      outboxAcks = next;
      saveAcks();
    } catch (e) { noteModuleError('outbox', e); }
  }

  // The ONLY place a durable dedup marker is written.
  function outboxCommit(sig) {
    if (!sig || outboxAcked(sig)) return;
    outboxAcks.push(sig);
    if (outboxAcks.length > OUTBOX_MAX_ACKS) outboxAcks = outboxAcks.slice(-OUTBOX_MAX_ACKS);
    saveAcks();
  }

  // kind → endpoint. `identifier_snapshot` posts its cookie bag to the snapshot
  // door as-is; every other kind is a /collect event. The envelope is built at
  // DRAIN time, not enqueue time, so a queued observation goes out with the
  // bc_aid and click ids current when it actually leaves — a first-touch mint
  // that resolved in between is picked up for free.
  function outboxEndpoint(kind) {
    return kind === DEDUP_SNAPSHOT ? ENDPOINTS.identifierSnapshot : ENDPOINTS.collect;
  }

  function outboxBody(entry) {
    var body;
    if (entry.k === DEDUP_SNAPSHOT) {
      body = {};
      var f = entry.f || {};
      for (var k in f) if (Object.prototype.hasOwnProperty.call(f, k)) body[k] = f[k];
      // D26, at the LAST exit: the entry froze its bag at enqueue, and a retry
      // is a new request made now — under the consent in force now. Only the
      // wire body is withheld; the stored entry keeps its hash, so consent
      // returning mid-queue puts the hash back on the next attempt.
      if (body.fingerprint_hash != null && !fingerprintAllowed()) body.fingerprint_hash = null;
      // Same rule, same reason, for the components: a retry is a new request
      // made under the consent in force NOW.
      if (body.fingerprint_components != null && !fingerprintAllowed()) body.fingerprint_components = null;
    } else {
      body = buildCollectEnvelope(entry.k, entry.f || {});
      // F-2: pin this observation's identity across retries. The envelope is
      // rebuilt on every attempt — telemetry moves, cookies and href are re-read,
      // so the door's whole-payload hash would see each retry as a NEW event and
      // the resend would not collapse. The entry id is minted once at enqueue and
      // persisted with the entry, and the door prefers a supplied event_id over
      // the payload hash, so retry identity no longer depends on body stability.
      // Not applied to the snapshot: its door keys on hash-of-bag, and a bag that
      // CHANGED between attempts is a genuinely new observation we want recorded
      // (that is D17's whole point), not a duplicate to collapse.
      body.event_id = entry.i;
      // Theme view beacon (2026-10-02): a page_seen / view_item_seen describes
      // the page it was ENQUEUED on. The envelope is built at drain time from
      // location.href, so an entry drained on the NEXT page would otherwise
      // carry that page's URL. The entry froze both at enqueue.
      if (entry.u) {
        body.url = entry.u;
        body.referrer = entry.rf || '';
      }
      // MEV fix r1 (contract 2): a view beacon carries `t_view` (frozen at page
      // load, in its fields) and `t_send` (now, per attempt) so the ledger can
      // place the view on the door's clock: received_at − (t_send − t_view).
      // The customizer's configured price (bc_vc) is resolved HERE, at send, so
      // the observation itself could be queued synchronously at load (BR-2).
      if (VIEW_KINDS[entry.k]) {
        body.payload = viewPayloadAtSend(entry);
      }
      // §2.1 session lane: the FIRST storefront /collect of the session carries
      // the device bag. Attached HERE, at drain time, because that is where the
      // envelope is built and where the entry id — the claim token — is known.
      // A throw must never take the drain down with it, hence the guard.
      try { attachSessionDevice(body, entry.i); }
      catch (e) { noteModuleError('device_bag', e); }
    }
    body.telemetry = {
      errors: telemetry.errors,
      last: telemetry.last,
      dropped: telemetry.dropped,
      flags: telemetry.flags
    };
    return JSON.stringify(body);
  }

  function outboxRemove(entry) {
    for (var i = 0; i < outboxQueue.length; i++) {
      if (outboxQueue[i].i === entry.i) { outboxQueue.splice(i, 1); break; }
    }
    saveQueue();
    if (!outboxQueue.length) stopOutboxTimer();
  }

  function outboxAck(entry) {
    delete outboxInFlight[entry.i];
    outboxRemove(entry);
    outboxCommit(entry.d);
  }

  function outboxDrop(entry) {
    delete outboxInFlight[entry.i];
    outboxRemove(entry);
    // A dropped entry never reaches a door, so any device-bag claim it holds is
    // a lease on a row that will never be written. Free it here — the next
    // /collect of the session re-claims and carries the bag. A throw must never
    // take the drop path down with it.
    try { releaseSessionDeviceClaim(entry.i); }
    catch (e) { noteModuleError('device_bag', e); }
    // Same rule for the snapshot lane's own lease (item 22): a dropped
    // snapshot that was carrying this session's device bag frees the slot, so
    // the next snapshot carries the bag instead of a `device_ref` naming a row
    // that will never be written.
    try { if (entry.k === DEDUP_SNAPSHOT) releaseSnapshotDeviceClaim(entry.d); }
    catch (e) { noteModuleError('device_bag', e); }
    telemetry.dropped = (telemetry.dropped || 0) + 1;
    saveTelemetry();
  }

  function outboxRetry(entry) {
    delete outboxInFlight[entry.i];
    entry.n = (entry.n || 0) + 1;
    if (entry.n >= OUTBOX_MAX_TRIES) { outboxDrop(entry); return; }
    // Capped backoff so a door that is down (or a body it will never accept)
    // can't turn into a per-page retry storm.
    entry.r = Date.now() + Math.min(60000, 1000 * Math.pow(2, entry.n));
    saveQueue();
  }

  function outboxFetch(entry, url, body) {
    try {
      var init = {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: body,
        credentials: 'include'
      };
      // `keepalive` shares WebKit's per-page 64 KB beacon budget, which this
      // site overflows at load; a full budget rejects the fetch synchronously.
      // A response-dependent entry (entry.q) fires at load, not unload, so it
      // gains nothing from keepalive and must not be exposed to that quota.
      if (!entry.q) init.keepalive = body.length <= OUTBOX_KEEPALIVE_MAX;
      fetch(url, init).then(function (r) {
        if (!r || !r.ok) { outboxRetry(entry); return null; }
        outboxAck(entry);
        // The door answered the first contact: first_touch is no longer owed.
        if (entry.k === DEDUP_FIRST_TOUCH) noteFirstTouchAck();
        try { return (typeof r.json === 'function') ? r.json().catch(function () { return null; }) : null; }
        catch (e) { return null; }
      }).then(function (j) {
        if (j) adoptCanonicalAid(j);
      }).catch(function () { outboxRetry(entry); });
    } catch (e) {
      outboxRetry(entry);
    }
  }

  // D26, the queued-entry seam, final form: a snapshot that froze a
  // fingerprint under earlier consent is DEFERRED while consent is denied —
  // not sent stripped. Sending it stripped and acking the entry's original
  // WITH-hash signature poisons the dedup: consent returning rebuilds the
  // same hash-bearing bag, the poisoned ack suppresses it, and an accepted
  // observation never reaches the ledger that session. Deferred, the entry
  // waits intact (no retry-count burn, no ack) and ships the moment consent
  // returns; if it never does, the entry dies with the session. Fresh bags
  // built during denial carry no hash (their own predicate) and deliver the
  // rest of the identifiers under their own signatures.
  function outboxDeferred(entry) {
    if (consentPending()) { drainWhenConsentSettles(); return true; }
    return entry.k === DEDUP_SNAPSHOT && entry.f && entry.f.fingerprint_hash != null && !fingerprintAllowed();
  }

  function outboxSend(entry) {
    if (outboxInFlight[entry.i]) return;
    if (viewExpired(entry, Date.now())) { outboxDrop(entry); return; }
    if (outboxDeferred(entry)) return;
    // View beacons are queued at load, before consent settles (BR-2); an
    // explicit analytics/marketing "no" is applied here, when it is known.
    if (VIEW_KINDS[entry.k] && !viewCaptureAllowed()) { outboxRemove(entry); return; }
    var url = outboxEndpoint(entry.k);
    var body;
    try { body = outboxBody(entry); }
    catch (e) { noteModuleError('outbox', e); outboxDrop(entry); return; }
    outboxInFlight[entry.i] = true;

    // text/plain, NOT application/json: only text/plain, multipart/form-data and
    // application/x-www-form-urlencoded are CORS-safelisted. A JSON-typed Blob
    // makes this cross-origin beacon a preflighted request, which sendBeacon
    // cannot perform — yet it still returns true (queued), so the fallback would
    // never run and the observation would vanish. Both doors parse the body as
    // JSON regardless of the declared type.
    var beaconOk = false;
    try {
      // sendBeacon can never hand back a response, so a response-dependent
      // entry goes straight to a plain fetch (outboxFetch, no keepalive).
      if (navigator.sendBeacon && !entry.q) {
        beaconOk = !!navigator.sendBeacon(url, new Blob([body], { type: 'text/plain' }));
      }
    } catch (e) { beaconOk = false; }

    if (currentBcAid()) {
      if (beaconOk) { outboxAck(entry); return; }   // acked-on-accept
      outboxFetch(entry, url, body);
      return;
    }
    // No durable id yet — see the EXCEPTION note in the header.
    outboxFetch(entry, url, body);
  }

  function outboxDrain() {
    if (outboxDraining) return;
    outboxDraining = true;
    try {
      var now = Date.now();
      // Iterate a COPY of the WHOLE queue: a beacon-acked entry splices itself
      // out mid-loop. Ineligible entries — in flight, backing off, or deferred
      // under denied consent — are skipped WITHOUT consuming a send slot.
      // Slicing the first N positions instead is how ten deferred fingerprint
      // snapshots at the head permanently starved an accepted email identify
      // sitting at position eleven (Sol round-4 blocker): the cap must bound
      // ATTEMPTS, never eligibility.
      var batch = outboxQueue.slice();
      var attempts = 0;
      for (var i = 0; i < batch.length && attempts < OUTBOX_MAX_PER_DRAIN; i++) {
        var entry = batch[i];
        if (outboxInFlight[entry.i]) continue;
        // A view beacon older than the ledger's view-age bound is dropped
        // (counted), never sent a day late as a second view (review r2 R2-S4).
        if (viewExpired(entry, now)) { outboxDrop(entry); continue; }
        if (entry.r && entry.r > now) continue;      // still backing off
        if (outboxDeferred(entry)) continue;         // waiting for consent
        attempts++;
        outboxSend(entry);
      }
    } catch (e) {
      noteModuleError('outbox', e);
    }
    outboxDraining = false;
    if (outboxQueue.length) startOutboxTimer(); else stopOutboxTimer();
  }

  function startOutboxTimer() {
    if (outboxTimer) return;
    try { outboxTimer = setInterval(outboxDrain, OUTBOX_DRAIN_MS); } catch (e) { outboxTimer = null; }
  }
  function stopOutboxTimer() {
    if (!outboxTimer) return;
    try { clearInterval(outboxTimer); } catch (e) {}
    outboxTimer = null;
  }

  function outboxEnqueue(obs) {
    try {
      if (!obs || !obs.kind) return false;
      // The one place consent can stop capture (D9). Default-off, so this is a
      // pass-through today; when it is armed nothing reaches any door.
      if (!consentAllowsCapture()) return false;
      var sig = (obs.ctx && obs.ctx.dedup) || null;
      if (sig) {
        if (outboxAcked(sig)) return false;          // already delivered this session
        outboxPending[sig] = true;
        for (var i = 0; i < outboxQueue.length; i++) {
          // Same observation is already waiting — the retry loop owns it.
          if (outboxQueue[i].d === sig) { outboxDrain(); return true; }
        }
      }
      outboxQueue.push({
        // Entry id — the ack/retry handle. The random tail keeps two entries
        // minted in the same millisecond on different pages distinct, since the
        // per-page sequence restarts at 1.
        i: String(Date.now()) + '-' + (++outboxSeq) + '-' + Math.random().toString(36).slice(2, 7),
        k: obs.kind,
        // The visit id is FROZEN at enqueue (contract 1): an entry drained on a
        // later page, or after the 30-minute idle rotation, still names the
        // visit it was observed in. Senders that set their own keep it.
        f: fieldsWithVisit(obs),
        d: sig,
        t: obs.ts || Date.now(),
        n: 0,
        // Not-before (view beacons): queued at load, sent a beat later so the
        // session's device bag is complete — or at once on pagehide.
        r: obs.notBefore || 0,
        // Response-dependent (load-time) send: the caller reads the door's
        // reply, so the transport must not use a channel that cannot return
        // one. Persisted with the entry so a retry on a later page honours it.
        q: (obs.ctx && obs.ctx.needsResponse) ? 1 : 0,
        // Frozen page URL/referrer (view beacons only — see outboxBody).
        u: obs.url || null,
        rf: obs.url ? (obs.referrer || '') : null,
        // Product handle for the drain-time bc_vc lookup (view_item_seen).
        h: obs.vcHandle || null
      });
      if (outboxQueue.length > OUTBOX_MAX_ENTRIES) {
        var over = outboxQueue.length - OUTBOX_MAX_ENTRIES;
        outboxQueue.splice(0, over);                 // drop oldest
        telemetry.dropped = (telemetry.dropped || 0) + over;
        saveTelemetry();
      }
      saveQueue();
      outboxDrain();
      if (obs.notBefore) {
        try { setTimeout(outboxDrain, Math.max(0, obs.notBefore - Date.now()) + 5); } catch (e) {}
      }
      return true;
    } catch (e) {
      noteModuleError('outbox', e);
      return false;
    }
  }

  // ---- view beacon helpers (MEV fix r1: BR-2 / BR-4 / contract 2) --------
  var VIEW_KINDS = { page_seen: 1, view_item_seen: 1 };
  // = bc-tracking VIEW_AGE_MAX_MS (src/shared/view-beacon.ts). The ledger
  // places a view at received − (t_send − t_view) clamped to this bound, and
  // skips a beacon sent later than it (`view_too_old`): an entry this old can
  // no longer meet its pixel twin, so it is dropped here instead of being
  // drained on a later visit (review r2 R2-S4). The outbox otherwise has no
  // age bound (entries live until acked, OUTBOX_MAX_TRIES or the 50-entry cap).
  var VIEW_MAX_AGE_MS = 3600000;

  function viewExpired(entry, now) {
    if (!VIEW_KINDS[entry.k]) return false;
    var tView = Number((entry.f && entry.f.t_view) || entry.t);
    return isFinite(tView) && tView > 0 && (now - tView) > VIEW_MAX_AGE_MS;
  }

  function fieldsWithVisit(obs) {
    var f = obs.fields || {};
    if (obs.kind === DEDUP_SNAPSHOT || f.session_id != null) return f;
    var out = {};
    for (var k in f) if (Object.prototype.hasOwnProperty.call(f, k)) out[k] = f[k];
    try { out.session_id = currentSessionId(); } catch (e) {}
    return out;
  }

  function viewCaptureAllowed() {
    try { if (window.bcConsent && window.bcConsent.settled()) return window.bcConsent.captureAllowed() !== false; } catch (e) {}
    return true;
  }

  // Same `bc_vc` rule as the pixel (v3.7): <handle>~<usd value>~<CUR>, set by
  // the customizer for the configured ring. A cookie for another product, or
  // a malformed one, is ignored and the variant's own price stands.
  function viewConfiguredValue(handle) {
    var raw = readCookie('bc_vc');
    if (!raw || !handle) return null;
    var parts = String(raw).split('~');
    if (parts.length !== 3 || parts[0] !== handle) return null;
    var value = Number(parts[1]);
    if (!isFinite(value) || value <= 0 || value >= 10000000) return null;
    if (!/^[A-Z]{3}$/.test(parts[2])) return null;
    return { value: value, currency: parts[2] };
  }

  // A per-attempt COPY of the frozen fields (the stored entry never changes):
  // t_send = now; view_item_seen takes this product's bc_vc price if present.
  function viewPayloadAtSend(entry) {
    var p = JSON.parse(JSON.stringify(entry.f || {}));
    p.t_send = Date.now();
    if (entry.k === 'view_item_seen' && entry.h && p.ecommerce && p.ecommerce.items && p.ecommerce.items[0]) {
      var cfgd = viewConfiguredValue(entry.h);
      if (cfgd) {
        p.ecommerce.items[0].price = cfgd.value;
        p.ecommerce.items[0].currency = cfgd.currency;
        p.ecommerce.value = cfgd.value;
        p.ecommerce.currency = cfgd.currency;
      }
    }
    return p;
  }

  // Leaving the page (or hiding it): the last moment this document can hand a
  // request to the browser. Not-before and backoff are waived for everything
  // that does not need a live response (BR-2: a view queued at load must not
  // die with a quick exit). Response-dependent entries (q) keep their retry on
  // the next page. Consent still applies (outboxSend defers while pending).
  function outboxFlushOnHide() {
    try {
      var changed = false;
      for (var i = 0; i < outboxQueue.length; i++) {
        var e = outboxQueue[i];
        if (e.q || !e.r) continue;
        e.r = 0;
        changed = true;
      }
      if (changed) saveQueue();
      outboxDrain();
    } catch (e) { noteModuleError('outbox', e); }
  }

  var BcOutbox = {
    enqueue: outboxEnqueue,
    seen: outboxSeen,
    forget: outboxForget,
    drain: outboxDrain
  };
  // Exposed for the smoke harness and live debugging; no theme code reads it.
  window.__bcOutbox = BcOutbox;

  // A bfcache restore is a fresh chance to deliver whatever is still queued.
  try { window.addEventListener('pageshow', function () { outboxDrain(); }); } catch (e) {}
  try { window.addEventListener('pagehide', outboxFlushOnHide); } catch (e) {}
  try {
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') outboxFlushOnHide();
    });
  } catch (e) {}
  // First drain runs once this file has finished initializing, so anything the
  // previous page left unacked goes out even if no sensor fires here.
  try { setTimeout(outboxDrain, 0); } catch (e) {}

  function readCookie(name) {
    var m = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[.$?*|{}()[\]\\\/+^]/g, '\\$&') + '=([^;]*)'));
    if (!m) return '';
    // Cookies set by external libs (Meta `_fbc`, Google `_gcl_aw`) sometimes
    // contain raw `%` that decodeURIComponent rejects with URIError. Return the
    // raw value on decode failure — downstream code already handles non-canonical formats.
    try { return decodeURIComponent(m[1]); } catch (e) { return m[1]; }
  }

  function writeCookie(name, value, maxAge) {
    try {
      // maxAge 'session' = a SESSION cookie (no Max-Age): bc_sess.
      var ageAttr = maxAge === 'session' ? '' : '; max-age=' + ((typeof maxAge === 'number') ? maxAge : COOKIE_MAX_AGE);
      // domain=.beyondcarat.com so subdomain landings share the bc_aid jar;
      // only attach on live apex/subdomains (preview hosts drop a mismatched write).
      var domainAttr = '';
      try {
        var host = (location.hostname || '').toLowerCase();
        if (host === 'beyondcarat.com' || host.endsWith('.beyondcarat.com')) {
          domainAttr = '; domain=.beyondcarat.com';
        }
      } catch (e) {}
      document.cookie = name + '=' + encodeURIComponent(value) + '; path=/' + ageAttr + domainAttr + '; SameSite=Lax; Secure';
    } catch (e) {}
  }

  // RFC4122 v4 UUID (mirror of the inline core's) — used only to create the
  // one guarded bootstrap candidate. The bc_aid cookie remains server-owned.
  function uuid() {
    try {
      if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
    } catch (e) {}
    return ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g, function (c) {
      var n = Number(c);
      var r = (window.crypto && window.crypto.getRandomValues)
        ? window.crypto.getRandomValues(new Uint8Array(1))[0]
        : Math.floor(Math.random() * 256);
      return (n ^ (r & (15 >> (n / 4)))).toString(16);
    });
  }

  function validUuid(v) {
    return typeof v === 'string' && UUID_RE.test(v);
  }

  function currentBcAid() {
    var aid = window.__bcAid || bcAid || readCookie(ANON_ID_COOKIE);
    if (!validUuid(aid)) aid = null;
    bcAid = aid;
    window.__bcAid = aid || null;
    return aid;
  }

  function getBootstrapCandidate() {
    if (currentBcAid()) return null;
    try {
      var existing = localStorage.getItem(BOOTSTRAP_KEY);
      if (validUuid(existing)) return existing;
      if (existing) return null;
      var candidate = uuid();
      localStorage.setItem(BOOTSTRAP_KEY, candidate);
      window.__bcAidCandidate = candidate;
      return candidate;
    } catch (e) {
      return validUuid(window.__bcAidCandidate) ? window.__bcAidCandidate : null;
    }
  }

  function adoptCanonicalAid(body) {
    try {
      var aid = body && (body.bc_aid || body.bcAid || body.canonical_bc_aid);
      if (!validUuid(aid)) return;
      bcAid = aid;
      window.__bcAid = aid;
      refreshBcAidCarriers();
      // adopt: the old forced write, now posted only when the cart differs
      // (owner-approved 2026-10-23) — see window.__bcCartAttrs in theme.liquid.
      syncIdentifiersToCart(aid, { force: true, adopt: true });
    } catch (e) {}
  }

  function rawCapture() {
    return window.__bcRawCapture || {
      landing_url: location.href,
      referrer: document.referrer || '',
      utms: {},
      click_ids: {},
      _kx: ''
    };
  }

  function addRawCaptureFields(target) {
    var raw = rawCapture();
    var utms = raw.utms || {};
    var clickIds = raw.click_ids || {};
    target.landing_url = raw.landing_url || location.href;
    target.raw_referrer = raw.referrer || document.referrer || '';
    target.raw_utm_params = JSON.stringify(utms);
    target.raw_click_ids = JSON.stringify(clickIds);
    target._kx = raw._kx || '';
    for (var u in utms) if (Object.prototype.hasOwnProperty.call(utms, u)) target[u] = utms[u];
    for (var c in clickIds) if (Object.prototype.hasOwnProperty.call(clickIds, c)) target[c] = clickIds[c];
    return target;
  }

  function buildCollectEnvelope(eventName, payload) {
    var aid = currentBcAid();
    var raw = rawCapture();
    // Fold the Meta cookies (_fbp/_fbc) into click_ids. The main frame CAN read
    // them (unlike the checkout pixel sandbox), and the server's visitors
    // projection reads fbp/fbc EXCLUSIVELY from the envelope's click_ids field —
    // so storefront events should carry them from first touch. Copy the raw
    // URL-captured ids first, then only set fbp/fbc when absent and the cookie is
    // non-empty; never overwrite a raw click_ids key of the same name.
    var clickIds = {};
    var rawClickIds = raw.click_ids || {};
    for (var ck in rawClickIds) {
      if (Object.prototype.hasOwnProperty.call(rawClickIds, ck)) clickIds[ck] = rawClickIds[ck];
    }
    var fbpCookie = readCookie('_fbp');
    var fbcCookie = readCookie('_fbc');
    if (fbpCookie && !clickIds.fbp) clickIds.fbp = fbpCookie;
    if (fbcCookie && !clickIds.fbc) clickIds.fbc = fbcCookie;
    return {
      v: 1,
      source: 'storefront',
      // R10 binding token, body carrier: sendBeacon cannot set headers, so the
      // door accepts the token as a top-level body field and strips it before
      // anything is enqueued. Value comes from shop metafield
      // bc_tracking.write_key via window.__bcCfg (see datalayer-init.liquid).
      write_key: cfg.writeKey || null,
      // Payload contract v2 (see CAPTURE_V): the door can tell which fields a
      // body was built to carry without guessing from what is absent.
      capture_v: CAPTURE_V,
      // `event` is GONE in v2. envelope.ts `eventName()` reads
      // event_name / eventName / name / type — `event` is not in that list and
      // never was, so the duplicate only ever cost bytes in signals.payload.
      event_name: eventName,
      bc_aid: aid || null,
      bc_aid_candidate: aid ? null : (getBootstrapCandidate() || null),
      // Real consent state, read at send time (D9/B12). Envelopes used to carry
      // a hard null unless a caller passed one, so the ledger could never tell a
      // consenting visitor from a declining one. An explicit caller value still
      // wins; otherwise we ask Shopify. Never gates — only records.
      consent: consentStamp(payload),
      bot: { verified_bot: false, sbfm: null },
      // `ua` is GONE in v2. buildEnvelope() stamps the envelope's ua from
      // `request.headers.get("user-agent")` (worker/envelope.ts:1030) — the
      // body copy was never read by anything and cost 2.2 MB/day of
      // signals.payload, on every /collect event, forever.
      // T1: the pre-strip landing URL while still on the landing page (see
      // window.__bcLandingUrl in layout/theme.liquid), else location.href.
      url: (window.__bcLandingUrl ? window.__bcLandingUrl() : location.href),
      referrer: document.referrer || '',
      click_ids: clickIds,
      checkout_token: null,
      cart_token: null,
      order_id: null,
      payload: payload || {}
    };
  }

  // (postCollect removed 2026-08-10 — the outbox above is the one /collect
  // sender. Its credentialed-fetch behaviour lives on as outboxFetch, which the
  // aid-less path still takes for the Set-Cookie + canonical echo.)

  bcAid = currentBcAid();
  if (!bcAid && validUuid(window.__bcAidCandidate)) {
    try { localStorage.setItem(BOOTSTRAP_KEY, window.__bcAidCandidate); } catch (e) {}
  }

  // Defensive sanitizer for click IDs (mirror of the inline core's
  // sanitizeClickId in datalayer-init.liquid). Used by the handoff module
  // below when it writes click IDs sourced from the /handoff/resolve response
  // or from an `r.`-prefixed URL param back into first-party cookies — same
  // XSS / cookie-bomb guard the landing-URL capture path applies. Rejects
  // (returns '') rather than truncates so junk-appended values can't slip past.
  function sanitizeClickId(v) {
    if (typeof v !== 'string' || !v) return '';
    if (v.length > 512) return '';
    if (!/^[A-Za-z0-9._\-:~/+=]+$/.test(v)) return '';
    return v;
  }

  // ─── 2026-08-10 Device fingerprint (Phase G) ───────────────────────────
  // Absorbed from snippets/bc-fingerprint.liquid, now retired. That file was a
  // standalone beacon with its own transport, its own session definition, its
  // own sentinel and its own consent check, sitting outside every mechanism
  // this asset had grown — the ninth sensor with the eighth transport, which is
  // exactly what the outbox exists to stop.
  //
  // DELIVERY IS v2-ONLY. The snippet dual-wrote to the v1
  // `/identifier-snapshot` route (bcrm-edge, CRM identity stitching) and to v2.
  // The v1 half was already dead in every production browser: bcrm-edge's CORS
  // response carries no `access-control-allow-credentials`, so the snippet's
  // credentialed fetch never survived its own preflight. Nothing is lost by
  // dropping it and no fallback is kept.
  //
  // The thumbprint is now ONE MORE FIELD in the identifier-snapshot bag. Every
  // problem the old beacon hand-rolled is already solved by the machinery
  // around it: the bag-hash sentinel (D17) sees the field appear and re-fires,
  // the outbox owns transport / ack / retry / backoff, and the door dedups on a
  // digest that already includes `fingerprint_hash`.
  //
  // SENTINEL SEMANTICS CHANGED, deliberately. The snippet set its 7-day
  // sentinel only on a 2xx, so a failed DELIVERY forced a full RECOMPUTE on the
  // next navigation — it conflated "we know this browser's thumbprint" with "we
  // got it through". They separate here: the hash itself is cached durably, so
  // the sentinel gates only the expensive part (the compute) and is set once the
  // hash has been cached and handed to the bag. Delivery retries are the
  // outbox's problem, and a later page's bag carries the cached hash regardless.
  //
  // Everything else is preserved from the snippet verbatim: at most once per
  // 7 days per browser, in idle time after full load only, the library lazily
  // loaded (self-hosted — esm.sh was killed as a supply-chain risk 2026-05-13)
  // ONLY when actually computing, and once-per-session when localStorage is dead.
  //
  // CHANGED 2026-08-30 (visitor capture rehaul §1b, spec
  // bc-tracking/docs/DESIGN-2026-08-30-visitor-capture-rehaul.md):
  //   · `exclude` is now `['audio']` — FONTS ARE BACK ON. That is a
  //     fingerprint-hash migration, not a tweak: every cached hash recomputes
  //     over the 7-day window, and because `fingerprint_hash` is in
  //     SNAPSHOT_HASH_FIELDS it forces exactly ONE fresh snapshot per
  //     fingerprinted browser. Pre- and post-change hashes are incomparable.
  //   · `logging` is explicitly OFF. Thumbmark 0.20.6 defaults it to TRUE and
  //     POSTs the hash plus its components to logging.thumbmarkjs.com at a
  //     1e-5 sample rate — our visitors' device data, leaving to a third party.
  //   · the components are KEPT (feature-detected, `fp.components || fp.data`)
  //     and ride the snapshot bag as `fingerprint_components`. The 2026-07-02
  //     audit that said nothing read them is no longer true — §2.1 stores them.
  var FP_TTL_KEY = 'bc_fp_sent_at';      // localStorage: ms stamp of the last COMPUTE
  var FP_HASH_KEY = 'bc_fp_hash';        // localStorage: the thumbprint itself
  var FP_SESSION_KEY = 'bc_fp';          // sessionStorage: hash + the localStorage-dead fallback marker
  var FP_TTL_MS = 7 * 24 * 60 * 60 * 1000;
  var FP_DELAY_MS = 2000;
  var FP_IDLE_TIMEOUT_MS = 15000;
  // How long we wait for the lazily-imported library to call back before
  // declaring it dead, counting it, and removing the global callback.
  var FP_CALLBACK_TIMEOUT_MS = 30000;
  // The door drops a longer value (MAX_FINGERPRINT_HASH_CHARS, worker
  // identifierSnapshot.ts) — never put a value in the bag it will refuse.
  var FP_MAX_HASH_CHARS = 256;
  var FP_CALLBACK_NAME = '__bcFingerprint';

  function fpValid(hash) {
    return typeof hash === 'string' && hash.length > 0 && hash.length <= FP_MAX_HASH_CHARS;
  }

  // The ONE consent predicate for everything fingerprint-related (D26). It is
  // deliberately declared out here, above every consumer, because the first
  // version of this module asked it in exactly one place — before the compute —
  // and three other paths reached the wire without it. CONSENT_GATE is
  // owner-set to false (capture everywhere), but fingerprinting is the one place
  // the retired snippet gated on Shopify's own signal — it reads the DEVICE, not
  // a first-party cookie we already own — so it stays gated independently.
  //
  // readConsentState() calls the same Shopify.customerPrivacy.userCanBeTracked()
  // the snippet called; it differs only when that function is absent AND
  // currentVisitorConsent() reports marketing explicitly declined, which the
  // snippet let through.
  function fingerprintAllowed() { return readConsentState() !== 'denied'; }

  // The live thumbprint for this browser, or null.
  //
  // EVERY read goes through currentFingerprintHash(), which asks the predicate
  // FIRST. Three separate paths can surface this value — the durable cache at
  // load, a fresh compute, and the snapshot bag — and gating only the compute
  // let a cached hash from a consenting visit keep shipping after that visitor
  // revoked consent. Consent is therefore re-evaluated on every call rather
  // than latched at load: it can be granted or revoked mid-page, and both
  // directions have to be respected.
  //
  // The cache read is lazy for the same reason. Reading it eagerly at load
  // would mean a visitor who denies at load and accepts later never gets their
  // cached hash back for the rest of the session.
  var fingerprintHash = null;
  var fingerprintCacheRead = false;

  function readFingerprintCache() {
    var v = null;
    try { v = localStorage.getItem(FP_HASH_KEY); } catch (e) { v = null; }
    if (!fpValid(v)) v = storageGet(FP_SESSION_KEY);
    return fpValid(v) ? v : null;
  }

  function currentFingerprintHash() {
    if (!fingerprintAllowed()) return null;
    if (!fingerprintCacheRead) {
      fingerprintCacheRead = true;
      if (!fingerprintHash) fingerprintHash = readFingerprintCache();
    }
    return fingerprintHash;
  }

  // ─── §1b: fingerprint components, bounded allowlist ─────────────────────
  // Thumbmark hands back the full component dictionary it hashed. We keep a
  // NAMED SUBSET of it — never the whole thing — because the dictionary also
  // carries things we have decided not to store:
  //   · `permissions` — a per-visitor permission state dictionary; sensitive,
  //     and useless for the evidence this feature exists to gather.
  //   · `plugins` — frozen by Chrome 94, so it is zero entropy and pure bytes.
  //   · any raw User-Agent string — already on every envelope; storing a second
  //     copy inside a components blob just multiplies the places it lives.
  // Everything kept is device EVIDENCE (canvas, webgl, fonts, screen,
  // hardware, locales, and four flat system flags), never identity.
  //
  // `math` left the allowlist in payload contract v2 (2026-09-05). Measured on
  // the live population: 532 bytes per snapshot carrying FOUR distinct values
  // across 2,170 samples — IEEE-754 transcendental results are identical on
  // every mainstream engine, so the component is ~2 bits of entropy at
  // 0.3 MB/day. Thumbmark still hashes it; only what we STORE shrinks, so no
  // fingerprint_hash changes and nothing re-keys.
  var FP_COMPONENT_KEYS = ['canvas', 'webgl', 'fonts', 'screen', 'hardware', 'locales', 'system'];
  var FP_SYSTEM_KEYS = ['applePayVersion', 'product', 'productSub', 'cookieEnabled'];
  var FP_UA_STRING_RE = /Mozilla\/\d/;

  // Belt for the braces above: a string that looks like a raw UA is dropped
  // wherever it sits inside a kept subtree, so a library version that moves the
  // UA into `system` (or anywhere else) cannot smuggle it through.
  function scrubUaStrings(v) {
    if (typeof v === 'string') return FP_UA_STRING_RE.test(v) ? null : v;
    if (Object.prototype.toString.call(v) === '[object Array]') {
      var arr = [];
      for (var i = 0; i < v.length; i++) {
        var e = scrubUaStrings(v[i]);
        if (e !== null) arr.push(e);
      }
      return arr;
    }
    if (v && typeof v === 'object') {
      var o = {};
      for (var k in v) {
        if (!Object.prototype.hasOwnProperty.call(v, k)) continue;
        var s = scrubUaStrings(v[k]);
        if (s !== null) o[k] = s;
      }
      return o;
    }
    return v;
  }

  // webgl: the vendor/renderer strings and the library's rendered-image or
  // shader HASH. Matched on the key NAME rather than a guessed spelling,
  // because 0.20.6 actually ships a single `commonImageHash` here and puts the
  // vendor/renderer under `hardware.videocard` — a literal ['vendor','renderer']
  // list silently produced an EMPTY object against the real library (caught in
  // a live browser run, 2026-08-30). Anything else in this component (canvas
  // pixel dumps, extension lists) is left behind.
  function pickWebglComponent(v) {
    if (!v || typeof v !== 'object') return null;
    var o = {};
    for (var k in v) {
      if (!Object.prototype.hasOwnProperty.call(v, k)) continue;
      if (k === 'vendor' || k === 'renderer' || /shader|hash/i.test(k)) o[k] = v[k];
    }
    return o;
  }

  function pickSubset(v, keys) {
    if (!v || typeof v !== 'object') return null;
    var o = {};
    for (var i = 0; i < keys.length; i++) {
      if (Object.prototype.hasOwnProperty.call(v, keys[i])) o[keys[i]] = v[keys[i]];
    }
    return o;
  }

  function pickFingerprintComponents(comps) {
    if (!comps || typeof comps !== 'object') return null;
    var out = {};
    var kept = 0;
    for (var i = 0; i < FP_COMPONENT_KEYS.length; i++) {
      var key = FP_COMPONENT_KEYS[i];
      if (!Object.prototype.hasOwnProperty.call(comps, key)) continue;
      var v = comps[key];
      if (v === null || v === undefined) continue;
      if (key === 'webgl') v = pickWebglComponent(v);
      else if (key === 'system') v = pickSubset(v, FP_SYSTEM_KEYS);
      v = scrubUaStrings(v);
      if (v === null || v === undefined) continue;
      out[key] = v;
      kept++;
    }
    if (!kept) return null;
    return clipBagObject(out, FP_COMPONENTS_MAX_BYTES);
  }

  // In MEMORY only, deliberately: the components are up to 8 KB and they ride
  // the snapshot that the compute itself triggers (§2.1). Caching them the way
  // the hash is cached would put 8 KB in localStorage on every browser to be
  // re-sent on pages where nothing changed.
  //
  // Read through this accessor and never off the variable — consent is
  // re-evaluated on every call, exactly as it is for the hash (D26), so a
  // revocation drops the components too.
  var fingerprintComponents = null;
  function currentFingerprintComponents() {
    if (!fingerprintAllowed()) return null;
    return fingerprintComponents;
  }

  // D25, the closing half: called by enqueueSnapshot on every ACCEPTED
  // hand-off. When the bag that just went out carried this browser's hash and
  // no sentinel exists yet — the cap-refused-then-next-page path, where
  // acceptFingerprint was not on the stack to stamp it — the sentinel is
  // stamped HERE, at the moment "delivered or on its way" became true.
  // Without this, cached-hash + no-sentinel suppressed recompute with nothing
  // to expire, and weekly re-validation was dead forever.
  //
  // Only a DURABLY cached hash earns the durable sentinel (read back, not
  // trusted): a session-only copy dies with the tab, and a sentinel that
  // outlives its hash would silence recompute with nothing left to send.
  // An existing sentinel — fresh or expired — is left alone: refreshing an
  // expired one is the recompute's job (acceptFingerprint), not delivery's.
  function noteFingerprintDelivered(body) {
    var h = body && body.fingerprint_hash;
    if (!fpValid(h)) return;
    try {
      if (localStorage.getItem(FP_TTL_KEY)) return;
      if (localStorage.getItem(FP_HASH_KEY) !== h) return;
      localStorage.setItem(FP_TTL_KEY, String(Date.now()));
    } catch (e) { /* localStorage dead — the session gate owns this browser */ }
  }

  guard('fingerprint', function BcFingerprint() {
    function lsSet(key, value) {
      try { localStorage.setItem(key, value); return true; } catch (e) { return false; }
    }

    // Primary gate: if this browser computed within the last week, do nothing at
    // all — no load listener, no idle scheduling, no library fetch, no compute.
    function computeAllowed() {
      var sentAt = 0;
      try { sentAt = parseInt(localStorage.getItem(FP_TTL_KEY) || '0', 10) || 0; } catch (e) { sentAt = 0; }
      if (sentAt && (Date.now() - sentAt) < FP_TTL_MS) return false;

      // Tightening the sentinel to "accepted hand-off only" (D25) split one
      // marker into two facts that were always different: "this browser's
      // thumbprint is KNOWN" and "it has been DELIVERED". A hand-off refused at
      // the page cap leaves the first true and the second false, and recomputing
      // would answer a question we already know the answer to — the hash is
      // stable per browser, and getting it to the ledger is the bag's job, not
      // the compute's. So: a known-but-undelivered hash suppresses recompute.
      //
      // Bounded deliberately to the no-sentinel case. Once a sentinel exists and
      // has EXPIRED, the weekly re-validation must still happen, so a stale
      // sentinel plus a refused hand-off does recompute once per session until
      // one hand-off is accepted. Rare, self-healing, and it errs toward
      // freshness rather than toward a fingerprint that silently never updates.
      if (!sentAt && currentFingerprintHash()) return false;

      // No usable durable sentinel — localStorage is blocked, or its write
      // failed last time. Fall back to the snippet's once-per-session gate.
      if (!sentAt && storageGet(FP_SESSION_KEY)) return false;
      return true;
    }

    // The hand-off. From here the snapshot mechanism owns everything: the bag
    // gains a field, its hash changes, and the outbox delivers and retries it.
    function acceptFingerprint(hash, comps) {
      if (typeof hash === 'string') hash = hash.trim();
      if (!fpValid(hash)) return false;
      // The library resolves long after the compute was authorized, so ask the
      // predicate again — consent can have been revoked in between, and a value
      // we may not use is one we must not store either.
      if (!fingerprintAllowed()) return false;
      fingerprintHash = hash;
      fingerprintCacheRead = true;
      // §1b/§2.1: reduce to the allowlist and stash BEFORE the bag is built —
      // buildSnapshotBag() reads them through currentFingerprintComponents(),
      // so they must be in place for the snapshot this hand-off triggers.
      fingerprintComponents = pickFingerprintComponents(comps);
      // Cache BEFORE dispatching: a hand-off that never lands must not cost a
      // recompute, and a later page's bag picks the hash up from here. Both
      // writes are CACHES. The session copy doubles as the once-per-session
      // gate when localStorage is dead, which is why it is written even on a
      // refused hand-off — a later page reads the hash back and re-attempts,
      // rather than recomputing.
      var cached = lsSet(FP_HASH_KEY, hash);
      storageSet(FP_SESSION_KEY, hash);

      // D25: the 7-day sentinel is stamped ONLY on an accepted hand-off. It
      // used to be stamped unconditionally, so a bag refused at the page cap
      // (five identifier changes can consume the budget while the idle callback
      // is still pending) suppressed recompute for a week while the hash had
      // reached neither the outbox nor the ledger.
      if (!enqueueSnapshot(buildSnapshotBag())) return false;
      // "Do not recompute", not "delivered" — delivery retries belong to the
      // outbox. Only a durable cache earns the durable sentinel; without one
      // the session marker above is the gate.
      if (cached) lsSet(FP_TTL_KEY, String(Date.now()));
      return true;
    }

    function loadLibrary() {
      // Liquid cannot run in this file, so the self-hosted library URL is handed
      // across on __bcCfg by datalayer-init.liquid, like write_key.
      var src = cfg.fingerprintSrc;
      if (typeof src !== 'string' || !src) {
        // Nothing throws when the theme stops handing this over — capture just
        // goes quietly to zero. That is what a canary is for (D20).
        setTelemetryFlag('canary_fingerprint_src');
        return;
      }
      // Every way this can end — success, the library throwing or 404ing, the
      // <script> failing to load, and a browser that never runs module scripts
      // at all — funnels through here. Previously the failure paths were empty
      // catches: no telemetry (so a week of zero fingerprints looked identical
      // to a week of healthy ones) and no cleanup (so the global callback sat on
      // window forever on any browser that never called it).
      var settled = false;
      var watchdog = null;
      function finish(err) {
        if (settled) return;
        settled = true;
        if (watchdog !== null) { try { clearTimeout(watchdog); } catch (e) {} watchdog = null; }
        if (err) noteModuleError('fingerprint', err);
        try { delete window[FP_CALLBACK_NAME]; } catch (e) { window[FP_CALLBACK_NAME] = undefined; }
      }

      window[FP_CALLBACK_NAME] = guard('fingerprint', function (fp) {
        var hash = fp && (fp.thumbmark || fp.hash);
        // Feature-detected, because the library has moved this between major
        // versions and a hard `fp.data` would return null components forever
        // after an upgrade with nothing to say so.
        var comps = (fp && (fp.components || fp.data)) || null;
        // A falsy payload is the module telling us the import or the library
        // itself failed — it is not "no fingerprint today", it is a broken
        // contract with a third party, which is exactly what telemetry is for.
        if (!fpValid(typeof hash === 'string' ? hash.trim() : hash)) {
          finish(new Error('fingerprint library returned no usable hash'));
          return;
        }
        acceptFingerprint(hash, comps);
        // A refused hand-off is NOT an error — the cap is working as designed
        // and a later page retries from cache — so it settles without counting.
        finish(null);
      });

      // Synchronous insertion failures (CSP refusing the element, a locked-down
      // webview throwing on appendChild) land AFTER the callback above was
      // installed and BEFORE the watchdog below is armed — the one window where
      // an escape used to bypass finish() and leak the global for the life of
      // the page. Route them through finish() like every other outcome.
      try {
        var loader = document.createElement('script');
        loader.type = 'module';
        loader.textContent =
          'import { getFingerprint, setOption } from ' + JSON.stringify(src) + ';\n' +
          'let fp = null;\n' +
          'try {\n' +
          // fonts ON (see the migration note in this module's header); audio
          // stays excluded because it is the one genuinely slow probe.
          "  setOption('exclude', ['audio']);\n" +
          // NOT optional: the library's default is logging:true, which ships
          // hash + components to logging.thumbmarkjs.com.
          "  setOption('logging', false);\n" +
          '  fp = await getFingerprint(true);\n' +               // (true) → {hash, data}
          '} catch (e) { fp = null; }\n' +
          // Always call back, even on failure, so the counter and the cleanup run.
          'if (typeof window.' + FP_CALLBACK_NAME + " === 'function') window." + FP_CALLBACK_NAME + '(fp);';
        loader.onerror = function () { finish(new Error('fingerprint module script failed to load')); };
        (document.head || document.documentElement).appendChild(loader);
      } catch (e) {
        finish(e);
        return;
      }

      // Last resort: a browser that ignores type="module" fires neither the
      // callback nor onerror. Without this the global leaks for the life of the
      // page and the failure is invisible. Retained so finish() can clear it on
      // early settlement instead of holding a dead closure for 30s.
      try { watchdog = setTimeout(function () { finish(new Error('fingerprint library timed out')); }, FP_CALLBACK_TIMEOUT_MS); } catch (e) { watchdog = null; }
    }

    var run = guard('fingerprint', function () {
      if (!fingerprintAllowed()) return;
      // §2.1: the UA client-hint read resolves in THIS idle slot, beside the
      // Thumbmark compute, and is never awaited by anything. Consent-gated with
      // the compute because it reads the DEVICE, like the fingerprint does.
      //
      // Started BEFORE the 7-day gate below on purpose: a browser that already
      // computed its thumbprint this week loads no library at all, and gating
      // the hint read on the same sentinel would starve the session lane of
      // ua_* for every returning visitor.
      probeUaClientHints();
      if (!computeAllowed()) return;
      loadLibrary();
    });

    // Idle time after full load only — never compete with rendering.
    function whenIdle() {
      if (window.requestIdleCallback) window.requestIdleCallback(run, { timeout: FP_IDLE_TIMEOUT_MS });
      else setTimeout(run, FP_DELAY_MS);
    }

    // The 7-day compute gate moved INTO run() (§2.1): the idle slot itself is
    // now also where the UA client hints resolve, and those must happen on a
    // page that will not compute. computeAllowed() is asked there instead —
    // later than here, so it reads a sentinel that is at worst fresher.
    //
    // NOT gated on a bc_aid cookie, which the snippet required. Its beacon body
    // needed a visitor to attach to; the bag this feeds is already sent aid-less
    // with a bootstrap candidate the door canonicalizes, so the old gate would
    // now skip precisely the visitors whose identity is least established.
    if (document.readyState === 'complete') {
      setTimeout(whenIdle, FP_DELAY_MS);
    } else {
      window.addEventListener('load', function () { setTimeout(whenIdle, FP_DELAY_MS); }, { once: true });
    }
  })();

  // ─── 2026-05-26 Identifier Snapshot Beacon ─────────────────────────
  // Once per session, fire a fetch() to events.beyondcarat.com/identifier-snapshot
  // with the full cookie + storage bag. The CF Worker decodes and writes the
  // canonical identifier_observations row + denormalizes onto the visitor
  // row. This is the path that lets the CRM stitch cross-device customers
  // even when our bc_aid cookie dies (Safari ITP, Shop Pay express, etc.).
  //
  // Auth is origin-based (browser can't safely carry a shared secret).
  // The CF Worker allowlists this storefront's origin.
  //
  // The bag is handed to the outbox, which owns the transport, the retry and
  // the durable marker (committed on ack, exactly as the old
  // 'bcrm_beacon_fired' sentinel was). Server has hash-of-bag dedup so retries
  // are safe.
  // _rdt_em is NOT sent — privacy fix: we don't ingest Reddit's hashed
  // email; if cross-vendor matching is needed we'd compute the hash
  // server-side from emails we've resolved through normal channels.
  //
  // D17 — the sentinel is keyed on the CONTENT of the bag, not on the session.
  // A single shot at +1.5s photographs the room before the guest arrives:
  // #1152's __kla_id was minted 47 seconds after the shot, at signup, and the
  // session sentinel then blocked every re-capture, which is why that visitor's
  // kla_anon_id is empty in the ledger. Now an unchanged bag is free (the hash
  // is already acked, so nothing is sent) and a bag that gains a cookie, a
  // login or a restored click id re-photographs within one trickle tick.
  //
  // Declared (not a self-invoking IIFE) so the handoff-restore path below can
  // re-fire it after it has written a restored _fbc cookie — the restored id
  // reaches the CRM in the same snapshot bag as everything else.
  var SNAPSHOT_MAX_PER_PAGE = 5;
  var snapshotsSentThisPage = 0;
  var snapshotCapCounted = false;
  var lastSnapshotHash = null;
  var snapshotWatchStarted = false;

  // Truncate any cookie value > 1KB to keep the body bounded. Verbose vendor
  // cookies (Datadome especially) can blow up keepalive's 64KB ceiling on
  // pages with many third-party SDKs.
  function clipBagValue(v) {
    if (!v) return '';
    return v.length > 1024 ? v.slice(0, 1024) : v;
  }

  // Device-env readers. A missing API must yield an explicit fallback, never
  // `undefined`: JSON.stringify DROPS undefined-valued keys, so the bag's field
  // set would silently vary by browser — navigator.hardwareConcurrency is absent
  // on older Safari, devicePixelRatio on some embedded webviews — and the door
  // would receive a different shape per visitor with nothing to explain it.
  function envValue(read, fallback) {
    var v;
    try { v = read(); } catch (e) { return fallback; }
    return (v === undefined) ? fallback : v;
  }

  // ─── 2026-08-30 Device bag — visitor capture rehaul §2.1 ────────────────
  // Spec: bc-tracking/docs/DESIGN-2026-08-30-visitor-capture-rehaul.md §2.1.
  //
  // ONE builder, TWO lanes, and the second lane is not redundancy:
  //   · SNAPSHOT lane — buildSnapshotBag() carries `device` on every genuine
  //     snapshot fire. That lane is deduped twice (the door's digest of
  //     bc_aid + identifiers + fingerprint_hash, and the outbox's own acked
  //     bag hash), so a returning visitor whose identifier bag has not changed
  //     sends nothing at all, ever. Device fields must therefore NEVER be
  //     added to SNAPSHOT_HASH_FIELDS — that would re-key every snapshot in
  //     the population and flood re-ingest — which is exactly why the snapshot
  //     lane alone cannot be the carrier.
  //   · SESSION lane — attachSessionDevice() puts the same bag on the FIRST
  //     storefront /collect event of each session. One row per visit per
  //     visitor: that is what makes device coverage ≈ beacon coverage.
  //
  // Every read goes through envValue(), so a missing or throwing API yields an
  // explicit null and the field set is identical on every browser (see the
  // note above envValue). Nothing here prompts, nothing here reads a device
  // LABEL (media devices are counted by kind, never named), and nothing here
  // builds a `net` key — that family is server-built at the door, §2.2.
  var DEVICE_BAG_MAX_BYTES = 4096;      // §2.1 budget; the door re-caps at 8 KB
  var FP_COMPONENTS_MAX_BYTES = 8192;   // the fonts list alone can approach 4 KB
  var DEVICE_SESSION_KEY = 'bc_device_sent';  // sessionStorage: the event id that claimed this session's device row

  // UTF-8 byte length. The budgets are in BYTES and String.length counts UTF-16
  // code units — a bag full of non-ASCII renderer strings would sail straight
  // past a char-counted 4 KB cap.
  function byteLen(s) {
    var str = String(s == null ? '' : s);
    try {
      if (typeof TextEncoder === 'function') return new TextEncoder().encode(str).length;
    } catch (e) { /* fall through to the manual count */ }
    var n = 0;
    for (var i = 0; i < str.length; i++) {
      var c = str.charCodeAt(i);
      if (c < 0x80) n += 1;
      else if (c < 0x800) n += 2;
      else if (c >= 0xd800 && c <= 0xdbff) { n += 4; i++; }   // surrogate pair
      else n += 3;
    }
    return n;
  }

  // clipBagValue() truncates a STRING. An object needs a different answer:
  // truncating serialized JSON produces something the door cannot parse at all.
  // So drop whole fields instead, biggest first — a fonts list or a renderer
  // string is what blows a budget, never a scalar — and record how many went as
  // `_clipped`, so a shrunken bag is visible rather than silently short.
  function clipBagObject(obj, maxBytes) {
    if (!obj || typeof obj !== 'object') return null;
    var json;
    try { json = JSON.stringify(obj); } catch (e) { return null; }
    if (typeof json !== 'string') return null;
    if (byteLen(json) <= maxBytes) return obj;

    var out = {};
    var sizes = [];
    for (var k in obj) {
      if (!Object.prototype.hasOwnProperty.call(obj, k)) continue;
      out[k] = obj[k];
      var size;
      try { size = byteLen(JSON.stringify(obj[k])); } catch (e) { size = Infinity; }
      sizes.push([k, size]);
    }
    sizes.sort(function (a, b) { return b[1] - a[1]; });
    for (var i = 0; i < sizes.length; i++) {
      delete out[sizes[i][0]];
      out._clipped = i + 1;
      var next;
      try { next = JSON.stringify(out); } catch (e) { return null; }
      if (byteLen(next) <= maxBytes) return out;
    }
    return out;
  }

  // Media-device COUNTS by kind — never labels. Labels are the permission-gated
  // half of this API; counts prompt nowhere. enumerateDevices() returns a
  // promise, so it is kicked off once at load and buildDeviceBag reads the
  // resolved counts synchronously (null until they land, like every other
  // absent reading).
  //
  // Cadence, measured in a real browser 2026-08-30: the aid-less first_touch
  // mint is enqueued and SENT during script evaluation, before any promise can
  // settle, so on a brand-new visitor the session lane's bag carries
  // `media_device_counts: null` and null `ua_*`. The +1.5s snapshot on the same
  // page carries both. That is the §2.1 S3 cadence — an async probe rides the
  // next genuine fire — not a defect to chase with an artificial delay on the
  // one request that mints this visitor's id.
  var mediaDeviceCounts = null;
  function probeMediaDevices() {
    try {
      var md = navigator.mediaDevices;
      if (!md || typeof md.enumerateDevices !== 'function') return;
      var p = md.enumerateDevices();
      if (!p || typeof p.then !== 'function') return;
      p.then(function (list) {
        var counts = {};
        for (var i = 0; i < (list || []).length; i++) {
          var kind = list[i] && list[i].kind;
          if (typeof kind !== 'string' || !kind) continue;
          counts[kind] = (counts[kind] || 0) + 1;
        }
        mediaDeviceCounts = counts;
      })['catch'](function () { /* permissions policy / webview — stays null */ });
    } catch (e) { /* absent or throwing — stays null */ }
  }

  // WebGL vendor/renderer, probed ONCE per page. Context CREATION is the part
  // that throws (locked-down webviews, and any browser that has run out of
  // contexts), so the whole probe sits in a try/catch and the context is
  // released immediately afterwards. Masked on Firefox and generic on Safari by
  // design — a null or a generic string is a real answer here, not a failure.
  var webglInfo = null;
  function readWebglInfo() {
    if (webglInfo) return webglInfo;
    var info = { vendor: null, renderer: null };
    try {
      var canvas = document.createElement('canvas');
      var gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      if (gl) {
        var dbg = gl.getExtension('WEBGL_debug_renderer_info');
        if (dbg) {
          info.vendor = clipBagValue(gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL)) || null;
          info.renderer = clipBagValue(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL)) || null;
        }
        var lose = gl.getExtension('WEBGL_lose_context');
        if (lose && typeof lose.loseContext === 'function') lose.loseContext();
      }
    } catch (e) { /* context refused — both stay null */ }
    webglInfo = info;
    return info;
  }

  // Storage probes. A read alone answers nothing — a partitioned store can hand
  // back null without throwing — so this writes and removes one scratch key.
  // Both stores are already used by this file, so the probe exercises no
  // capability the page did not already use.
  function storageWorks(store) {
    try {
      var k = '__bc_probe';
      store.setItem(k, '1');
      var ok = store.getItem(k) === '1';
      store.removeItem(k);
      return ok;
    } catch (e) { return false; }
  }

  function mediaMatches(query) {
    return !!(window.matchMedia && window.matchMedia(query).matches);
  }
  // First matching value of a discrete media feature, or null when the browser
  // does not know the feature at all (Safari answers false to every value of an
  // unknown feature, which is indistinguishable from "no preference" — null is
  // the honest reading).
  function firstMediaMatch(feature, values) {
    for (var i = 0; i < values.length; i++) {
      if (mediaMatches('(' + feature + ': ' + values[i] + ')')) return values[i];
    }
    return null;
  }

  function msOrNull(v) {
    return (typeof v === 'number' && isFinite(v) && v >= 0) ? Math.round(v) : null;
  }

  // Social-webview marker, mirroring the door's CLOSED vocabulary and its
  // precedence order (bc-tracking src/shared/in-app-browser.ts): Instagram is
  // tested before Facebook because Instagram's Android webview carries Meta's
  // shared FB_IAB token, and a Facebook-first order mislabels every Instagram
  // session. The door still classifies the UA it sees; this is the client's own
  // answer, in the same words, so the two can be compared.
  function detectInAppBrowser() {
    var ua = navigator.userAgent || '';
    if (/\bInstagram\b/i.test(ua)) return 'instagram';
    if (/\bFB(?:AN|AV|_IAB|4A|IOS)\b/i.test(ua)) return 'facebook';
    if (/musical_ly|BytedanceWebview/i.test(ua)) return 'tiktok';
    if (/\bSnapchat\//i.test(ua)) return 'snapchat';
    if (/\bLine\/\d/i.test(ua)) return 'line';
    return null;
  }

  // UA client hints, high entropy. ASYNC, therefore never on the critical path:
  // the promise is started in the SAME idle slot as the Thumbmark compute and
  // whatever has resolved by the time a bag is built rides along — the first
  // beacon never waits for it (§2.1 cadence note S3: a probe that lands after a
  // fire rides the NEXT genuine fire). Chromium only, null everywhere else, and
  // it prompts nowhere.
  //
  // `fullVersionList` (bounded, structured) — NOT the deprecated
  // `uaFullVersion`, which Chromium is removing.
  var uaHighEntropy = null;
  var UA_CH_HINTS = ['architecture', 'model', 'platform', 'platformVersion', 'fullVersionList'];
  function probeUaClientHints() {
    try {
      var uad = navigator.userAgentData;
      if (!uad || typeof uad.getHighEntropyValues !== 'function') return;
      var p = uad.getHighEntropyValues(UA_CH_HINTS);
      if (!p || typeof p.then !== 'function') return;
      p.then(function (v) { if (v) uaHighEntropy = v; })['catch'](function () { /* refused — stays null */ });
    } catch (e) { /* absent — stays null */ }
  }

  // [{brand, version}] → ["Brand;v=139"]. Flat strings, because the ledger's
  // array columns are text[]; the join is reversible and the shape is stable.
  function brandList(list) {
    if (Object.prototype.toString.call(list) !== '[object Array]') return null;
    var out = [];
    for (var i = 0; i < list.length && i < 12; i++) {
      var b = list[i];
      if (!b || typeof b.brand !== 'string') continue;
      out.push(clipBagValue(b.brand + ';v=' + (b.version == null ? '' : String(b.version))));
    }
    return out.length ? out : null;
  }

  // THE device bag. Column spellings are the ledger's own (§0.5 of the spec:
  // no third alias set), and the whole thing is clipped to 4 KB before it goes
  // anywhere.
  function buildDeviceBag() {
    var conn = envValue(function () {
      return navigator.connection || navigator.mozConnection || navigator.webkitConnection || null;
    }, null) || {};
    var nav = envValue(function () { return performance.getEntriesByType('navigation')[0] || null; }, null) || {};
    var gl = envValue(function () { return readWebglInfo(); }, null) || { vendor: null, renderer: null };
    var ch = uaHighEntropy || {};

    var bag = {
      // screen / viewport
      screen_width: envValue(function () { return screen.width; }, null),
      screen_height: envValue(function () { return screen.height; }, null),
      avail_width: envValue(function () { return screen.availWidth; }, null),
      avail_height: envValue(function () { return screen.availHeight; }, null),
      color_depth: envValue(function () { return screen.colorDepth; }, null),
      device_pixel_ratio: envValue(function () { return window.devicePixelRatio; }, null),
      viewport_width: envValue(function () { return window.innerWidth; }, null),
      viewport_height: envValue(function () { return window.innerHeight; }, null),
      orientation: envValue(function () { return screen.orientation.type; }, null),

      // hardware
      hardware_concurrency: envValue(function () { return navigator.hardwareConcurrency; }, null),
      device_memory_gb: envValue(function () { return navigator.deviceMemory; }, null),
      touch_points: envValue(function () { return navigator.maxTouchPoints; }, null),

      // locale / time
      browser_timezone: envValue(function () { return Intl.DateTimeFormat().resolvedOptions().timeZone; }, null),
      browser_timezone_offset_min: envValue(function () { return new Date().getTimezoneOffset(); }, null),
      // ALL entries, not the snapshot bag's legacy first three — the tail is
      // the signal (a `ko` behind `en-US` says more than the head does).
      // Bounded at 20 so a hostile list cannot eat the whole 4 KB budget and
      // make the clipper drop the field outright.
      languages: envValue(function () { return (navigator.languages || []).slice(0, 20); }, null),

      // rendering preferences
      prefers_dark_mode: envValue(function () { return mediaMatches('(prefers-color-scheme: dark)'); }, null),
      prefers_reduced_motion: envValue(function () { return mediaMatches('(prefers-reduced-motion: reduce)'); }, null),
      prefers_contrast: envValue(function () { return firstMediaMatch('prefers-contrast', ['more', 'less', 'custom', 'no-preference']); }, null),
      color_gamut: envValue(function () { return firstMediaMatch('color-gamut', ['rec2020', 'p3', 'srgb']); }, null),
      hdr: envValue(function () { return mediaMatches('(dynamic-range: high)'); }, null),

      // network (Chromium/Android only). `type` is the physical bearer
      // ('wifi', 'cellular'); `effectiveType` is the measured class ('4g').
      // They answer different questions and both are kept.
      connection_type: envValue(function () { return conn.type || null; }, null),
      effective_connection_type: envValue(function () { return conn.effectiveType || null; }, null),
      connection_downlink_mbps: envValue(function () { return conn.downlink; }, null),
      connection_rtt_ms: envValue(function () { return conn.rtt; }, null),
      save_data: envValue(function () { return conn.saveData; }, null),

      // gpu
      webgl_vendor: gl.vendor,
      webgl_renderer: gl.renderer,

      // media devices — counts by kind, never labels
      media_device_counts: mediaDeviceCounts,

      // capability probes
      cookie_enabled: envValue(function () { return navigator.cookieEnabled; }, null),
      local_storage_ok: envValue(function () { return storageWorks(localStorage); }, null),
      session_storage_ok: envValue(function () { return storageWorks(sessionStorage); }, null),
      webdriver: envValue(function () { return navigator.webdriver; }, null),

      // navigation timing, whole ms. A 0 DCL means "not fired yet", not
      // "instant" — the bag can be built before DOMContentLoaded.
      perf_dns_ms: envValue(function () { return msOrNull(nav.domainLookupEnd - nav.domainLookupStart); }, null),
      perf_connect_ms: envValue(function () { return msOrNull(nav.connectEnd - nav.connectStart); }, null),
      perf_ttfb_ms: envValue(function () { return msOrNull(nav.responseStart - nav.requestStart); }, null),
      perf_dcl_ms: envValue(function () { return msOrNull(nav.domContentLoadedEventEnd - nav.startTime); }, null),

      in_app: envValue(function () { return detectInAppBrowser(); }, null),

      // async, best-effort (see probeUaClientHints)
      ua_brands: envValue(function () { return brandList(ch.brands); }, null),
      ua_platform: envValue(function () { return clipBagValue(ch.platform) || null; }, null),
      ua_platform_version: envValue(function () { return clipBagValue(ch.platformVersion) || null; }, null),
      ua_arch: envValue(function () { return clipBagValue(ch.architecture) || null; }, null),
      ua_model: envValue(function () { return clipBagValue(ch.model) || null; }, null),
      ua_mobile: envValue(function () { return (typeof ch.mobile === 'boolean') ? ch.mobile : null; }, null),
      ua_full_version_list: envValue(function () { return brandList(ch.fullVersionList); }, null),
    };
    return clipBagObject(bag, DEVICE_BAG_MAX_BYTES);
  }

  // §2.1 session lane. The FIRST storefront /collect event of each session
  // carries the device bag — one per session, not one per page and not one per
  // event. The theme's session IS sessionStorage (the outbox's own ack list
  // lives there), so that is the gate.
  //
  // The claim is stored as the claiming EVENT ID rather than a boolean, and
  // that is load-bearing: the outbox rebuilds the envelope at every drain
  // attempt, so a boolean would strip `device` off the RETRY of the very event
  // that claimed the slot — losing the bag for the whole session whenever the
  // first attempt failed. Matching on the id lets that one event keep its bag
  // across retries while every other event gets nothing.
  //
  // sessionStorage throws on both read and write in Safari private mode, hence
  // the in-memory fallback: a dead store must still yield one bag per page
  // rather than one per event.
  //
  // The fallback holds the claiming EVENT ID, not a boolean, for exactly the
  // reason the stored claim does (Sol class E(a)): with a boolean, the first
  // attempt of the claiming event set the flag and carried the bag, and its
  // own RETRY — which re-enters here having found no stored claim and no
  // writable store — saw the flag and returned without the bag. Every retry
  // path then shipped a bagless envelope and the session lost its device row.
  // Keyed on the id, the claiming event keeps its bag across all ten attempts
  // while every other event is still denied.
  //
  // Round 1 (A2/A3): the claim is written and read through writeDeviceClaim /
  // readDeviceClaim, so it is void the moment `bc_sess` rotates or the device
  // facts move. Everything else about the lease — keyed on the event id, held
  // across all ten attempts of the claiming event, released when that event is
  // dropped — is unchanged.
  var deviceClaimEventId = null;
  function attachSessionDevice(body, eventId) {
    if (!body || !eventId) return false;
    var claimed = readDeviceClaim(DEVICE_SESSION_KEY, deviceClaimEventId);
    if (claimed) {
      if (claimed !== eventId) return false;
    } else if (!writeDeviceClaim(DEVICE_SESSION_KEY, eventId)) {
      // Dead store (Safari private mode): fall back to memory, stamped the
      // same way so a memo cannot outlive its session either. `claimed` is
      // null here, so whatever the memo held was absent or already void —
      // overwriting it is the correct answer, not a refusal.
      deviceClaimEventId = deviceClaimValue(eventId);
    }
    // Copy, never mutate: buildCollectEnvelope hands back the ENTRY'S OWN
    // fields object as `payload`, and writing into it would grow the entry that
    // gets persisted to sessionStorage and replayed on later pages.
    var payload = {};
    var src = body.payload || {};
    for (var k in src) {
      if (Object.prototype.hasOwnProperty.call(src, k)) payload[k] = src[k];
    }
    payload.device = buildDeviceBag();
    body.payload = payload;
    return true;
  }

  // The other half of the same claim (Sol class E(b)): a claim is a lease on
  // the session's ONE device row, and the outbox can destroy the leaseholder.
  // When an entry exhausts its ten attempts it is removed from the queue and
  // will never be sent — but its id stayed in `bc_device_sent`, so every later
  // event of that session (all with different ids) was refused the bag and the
  // visitor's device evidence was lost for good. Dropping the leaseholder
  // therefore frees the lease, and the next event re-claims it.
  //
  // Keyed on the id in BOTH stores so it is correct whichever lane holds the
  // claim; a claim held by some other event is left strictly alone.
  function releaseSessionDeviceClaim(eventId) {
    if (!eventId) return false;
    var released = false;
    // Compare the CLAIM, not the stored string: the stored form now carries the
    // session and device stamp in front of it. The two stores are checked
    // INDEPENDENTLY (parseDeviceClaim on each), exactly as before — a
    // readDeviceClaim() here would let a live storage value hide a memo that
    // also needs clearing.
    if (parseDeviceClaim(storageGet(DEVICE_SESSION_KEY)) === eventId) {
      storageRemove(DEVICE_SESSION_KEY);
      released = true;
    }
    if (parseDeviceClaim(deviceClaimEventId) === eventId) {
      deviceClaimEventId = null;
      released = true;
    }
    return released;
  }

  guard('device_bag', probeMediaDevices)();

  // Every cookie the snapshot lane carries, in the order the door's
  // normalizeIdentifierBag() expects to find them. A list, not twelve
  // hand-written lines, because v2 has to ask the same question of each one
  // ("is it non-empty?") and a per-field `if` is twelve chances to differ.
  var SNAPSHOT_COOKIES = [
    '_shopify_y', 'shopify_client_id', '_ga', '_gcl_au', '__kla_id',
    '_fbp', '_fbc', '_rdt_uuid',
    // Intentionally NOT capturing _rdt_em — that's Reddit's hashed-email
    // cookie set without our consent flow. We compute rdt_em_hash server-side
    // from emails we own.
    '_uetvid', '_clck'
  ];

  // ─── The IDENTIFIER projection ──────────────────────────────────────────
  // Exactly what snapshotSignature() hashes, and nothing else. Split out of
  // buildSnapshotBag() (perf item 25 / Astra #6) because the watcher asks the
  // signature question every tick and the answer almost never changes: the
  // device bag it used to build first — a WebGL probe, five matchMedia calls,
  // a navigation-timing read, a media-device count — was thrown away on every
  // one of those ticks. This function is cookie reads and nothing more.
  //
  // v2: a cookie that is absent is OMITTED rather than sent as "". The
  // signature is unchanged by that — stableIdentifierPart() maps both `''` and
  // `undefined` to the empty string — so no bag re-keys and no dedupe resets.
  function buildSnapshotIdentifiers() {
    var clip = clipBagValue;
    var aid = currentBcAid();
    var candidate = aid ? null : getBootstrapCandidate();
    var body = {
      capture_v: CAPTURE_V,
      bc_aid: aid || null,
      bc_aid_candidate: candidate || null
    };
    for (var i = 0; i < SNAPSHOT_COOKIES.length; i++) {
      var name = SNAPSHOT_COOKIES[i];
      var value = clip(readCookie(name));
      if (value) body[name] = value;
    }
    var multiFbc = envValue(function () { return clip(localStorage.getItem('multiFbc')); }, '');
    if (multiFbc) body.multi_fbc = multiFbc;
    // Phase G: the device thumbprint, when this browser has one. ALWAYS
    // present as a key (null until computed) — JSON.stringify drops
    // undefined, and a bag whose field set varies by browser is the shape
    // problem envValue() exists to prevent. The door lifts it into the signal
    // payload, where src/ingress/visitor-identifiers.ts reads it onto
    // visitors.fingerprint_hash. Read through the accessor, never off the
    // variable — the accessor is where consent is enforced (D26).
    body.fingerprint_hash = currentFingerprintHash();
    // Logged-in Shopify customer identity (from window.__bcCfg — Liquid
    // rendered by the inline core). Matches the previous Liquid customer
    // gate: keys only present when a customer is logged in.
    // 2026-09-07: the customer id is NO LONGER SENT — the door deletes any
    // browser-asserted customer id from this body (counter
    // customer_id_asserted_dropped) and trusts ONLY the signed binding token
    // `bt` (v2) the theme already attaches for logged-in visitors.
    if (customer) {
      body.email = customer.email;
      body.phone = customer.phone || '';
    }
    return body;
  }

  // ─── §2.1 snapshot lane: the device bag, ONCE per session ───────────────
  // (perf item 22.) A visitor sends ~5.8 snapshots a day carrying ~3 distinct
  // device bags: the bag is a per-BROWSER fact re-photographed on every
  // identifier change. The first snapshot of a session carries it; every later
  // one carries `device_ref` — the session id — instead.
  //
  // Verified BEFORE shipping, against the readers that actually consume the
  // snapshot lane (2026-09-05):
  //   · writeVisitorObservation() (ingress/visitor-observations.ts) writes the
  //     row when ANY of device / net / components / fingerprint_hash /
  //     browser_env_v1 / the three net keys is non-null. A bagless snapshot
  //     still carries fingerprint_hash and the door-built `net` family, so the
  //     row still lands, still on lane "snapshot".
  //   · measure-cross-device-keys.ts reads browser_env_v1 / languages /
  //     browser_timezone / ip_region from `visitors` (projected, COALESCE
  //     fold — `fold()` in projections.ts is COALESCE(incoming, existing), so
  //     a null incoming never erases) and home_net_v1 from the OBSERVATION,
  //     which is derived from the IP at the door and owes nothing to the bag.
  //   · key-buckets.ts sources fingerprint_hash and browser_env_v1 from
  //     `visitors`, and home_net_v1 / net_day_v1 from observations. Same
  //     answer.
  // So the recall measurement still counts a snapshot with a fingerprint and
  // no bag. The bag itself reaches `visitors` ONCE PER VISITOR, not once per
  // session: the fold is COALESCE(incoming, existing), so the first non-null
  // value wins forever and every later bag lands only as a
  // `visitor_observations` row (round 2, H5 — see the A3 note above).
  var SNAPSHOT_DEVICE_KEY = 'bc_device_snap';   // sessionStorage: the dedup sig that claimed this session's snapshot bag
  var snapshotDeviceClaim = null;               // in-memory fallback (Safari private mode)

  // Round 1 (A2/A3), narrowed in round 2 (H5): read through readDeviceClaim, so
  // a claim made by the session before the 30-minute idle gap — or on a device
  // whose core count, timezone or language list has since moved — does NOT
  // satisfy this session. Zoom and rotation deliberately do not count: see
  // deviceEnvSignature(). Within one unchanged session the answer is
  // exactly what it was: the first snapshot carries the bag, every later one
  // carries `device_ref` and nothing else.
  function snapshotDeviceClaimed() {
    return !!readDeviceClaim(SNAPSHOT_DEVICE_KEY, snapshotDeviceClaim);
  }

  // Claimed only on an ACCEPTED hand-off, exactly like the session lane's
  // lease: a bag the outbox refused must not spend the session's one device
  // slot. Keyed on the dedup signature so the release below can tell its own
  // claim from somebody else's.
  function claimSnapshotDevice(sig) {
    if (!sig || snapshotDeviceClaimed()) return;
    if (!writeDeviceClaim(SNAPSHOT_DEVICE_KEY, sig)) snapshotDeviceClaim = deviceClaimValue(sig);
  }

  // The other half of the lease (same class as releaseSessionDeviceClaim): an
  // entry that exhausts its ten attempts is never sent, so the session would
  // otherwise spend the rest of its life sending `device_ref` pointing at a
  // row that does not exist.
  function releaseSnapshotDeviceClaim(sig) {
    if (!sig) return;
    // Compare the CLAIM, not the stored string, and check the two stores
    // INDEPENDENTLY — see releaseSessionDeviceClaim.
    if (parseDeviceClaim(storageGet(SNAPSHOT_DEVICE_KEY)) === sig) storageRemove(SNAPSHOT_DEVICE_KEY);
    if (parseDeviceClaim(snapshotDeviceClaim) === sig) snapshotDeviceClaim = null;
  }

  function buildSnapshotBag() {
    var clip = clipBagValue;
    var body = buildSnapshotIdentifiers();
    // The components that hash was computed over, reduced to the §1b
    // allowlist. In memory only, so this is non-null on the snapshot the
    // compute itself triggered and null on every later page — which is the
    // intent: one components record per compute, not one per navigation.
    body.fingerprint_components = currentFingerprintComponents();
    // NOT in SNAPSHOT_HASH_FIELDS — see the note there and buildDeviceBag()'s
    // header. Built ONLY when this session still owes a bag, which is the
    // whole of items 22 and 25: on a repeat snapshot nothing below this line
    // touches the GPU, the media queries or the navigation timeline.
    if (snapshotDeviceClaimed()) {
      body.device_ref = currentSessionId();
    } else {
      // 2026-09-08: a bag that fails to build is OMITTED, never sent as null.
      // The ingress schema has `device` optional-but-not-nullable, so an
      // explicit null refused the whole envelope (quarantine row 26, a
      // RangeError from the poller recursion thrown mid-buildDeviceBag) and
      // clamped the bc-signals fence to that row for 8 h. Absent = the
      // ordinary repeat-snapshot shape the consumer already accepts.
      var deviceBag = envValue(function () { return buildDeviceBag(); }, null);
      if (deviceBag) body.device = deviceBag;
    }
    body.page_url = clip((window.__bcLandingUrl ? window.__bcLandingUrl() : location.href));
    body.referrer = clip(document.referrer || '');
    body.ts = Math.floor(Date.now() / 1000);
    // v2 drops `raw_capture` here. It carried landing_url / referrer / utms /
    // click_ids / _kx — 1.3–1.5 MB/day — and bc-tracking/src reads none of it
    // (the door lifts click ids out of `click_ids`, the landing URL out of the
    // envelope `url`). The six flat device duplicates went with it: the
    // ingress prefers `payload.device` over the flat spelling, so they were
    // dead weight whenever `device` was present and are covered by
    // `device_ref` when it is not.
    return body;
  }

  // Only the IDENTIFIER fields feed the hash. page_url, referrer and ts change
  // on every navigation and every tick — folding them in would re-shoot the
  // whole bag on each page, which is the opposite of the point.
  //
  // `device` and `fingerprint_components` are DELIBERATELY ABSENT and must stay
  // absent (spec §2.1). Device readings drift — viewport on rotate, rtt on
  // every network blip, perf timings on every navigation — so folding them in
  // would re-key every snapshot in the population at once and flood re-ingest,
  // and would then re-shoot the bag on every resize forever after. The session
  // lane exists precisely so device data does not need to be in this list.
  var SNAPSHOT_HASH_FIELDS = [
    'bc_aid', 'bc_aid_candidate', '_shopify_y', 'shopify_client_id', '_ga',
    '_gcl_au', '__kla_id', '_fbp', '_fbc', '_rdt_uuid', '_uetvid', '_clck',
    'multi_fbc', 'fingerprint_hash', 'email', 'phone'
  ];

  // 2026-09-05: hash the STABLE part of each identifier, not the raw cookie.
  // Four of the hashed cookies carry a per-page timestamp — `_gcl_au` (Google
  // appends the last-seen epoch), `_uetvid`/`_clck` (Bing/Clarity `|epoch|…`
  // suffixes) and `__kla_id` (base64 JSON whose `bc_aid_<id>` stamp is
  // rewritten every page) — so two bags with IDENTICAL identifiers hashed
  // differently on every tick and the dedupe never fired: 4,685 snapshots
  // from 896 browsers in one measured day (one browser sent 91), 41% of which
  // carried no new identifier at all. Each one is a full ingest (≈34% of all
  // door volume) and the ingest backlog is what drives the p95 landing lag.
  // Normalising here restores the dedupe the field list already intended; the
  // 5-per-page cap and the outbox `seen` marker are unchanged.
  function stableIdentifierPart(field, value) {
    if (!value) return '';
    var s = String(value);
    try {
      if (field === '_gcl_au') return s.split('.').slice(0, 3).join('.');
      if (field === '_uetvid' || field === '_clck') return s.split('|')[0];
      if (field === '__kla_id') {
        // Klaviyo's cookie is base64(JSON). The stable identity is `cid`; the
        // rest is rewritten on every page. Unpadded/URL-safe base64 tolerated.
        var b64 = s.replace(/-/g, '+').replace(/_/g, '/');
        while (b64.length % 4) b64 += '=';
        var parsed = JSON.parse(atob(b64));
        if (parsed && typeof parsed.cid === 'string' && parsed.cid) return parsed.cid;
        return s.slice(0, 48);
      }
    } catch (e) {
      return s.slice(0, 48);
    }
    return s;
  }

  function snapshotSignature(body) {
    var parts = [];
    for (var i = 0; i < SNAPSHOT_HASH_FIELDS.length; i++) {
      parts.push(stableIdentifierPart(SNAPSHOT_HASH_FIELDS[i], body[SNAPSHOT_HASH_FIELDS[i]]));
    }
    return DEDUP_SNAPSHOT + '|' + bcHash(parts.join('~'));
  }

  // Returns TRUE when this bag is delivered or on its way — i.e. the caller's
  // hand-off succeeded and there is nothing left to retry. Returns FALSE only
  // when the bag was REFUSED and a later attempt is required.
  //
  // That distinction is load-bearing and used to not exist: this function threw
  // away BcOutbox.enqueue()'s result and unconditionally returned true, while
  // advancing both the page counter and the change marker BEFORE the enqueue.
  // The fingerprint's 7-day sentinel keys on this answer (D25), so a refusal
  // reported as success silently cost a whole week of that browser's thumbprint.
  //
  // The already-seen branch answers TRUE deliberately: the bag IS out, so a
  // caller waiting to stamp a "stop recomputing" sentinel should stamp it.
  // Answering false there would make a browser whose hash already landed
  // recompute forever, every session.
  function enqueueSnapshot(body) {
    var sig = snapshotSignature(body);
    if (BcOutbox.seen(sig)) { lastSnapshotHash = sig; noteFingerprintDelivered(body); return true; }
    if (snapshotsSentThisPage >= SNAPSHOT_MAX_PER_PAGE) {
      // A bag churning faster than five times a page means something is
      // rewriting a cookie in a loop; count it rather than feed the loop.
      // Counted once per PAGE, not once per refusal — the watcher re-attempts a
      // refused bag on every tick by design (it must not advance its marker
      // past a refusal), and a per-tick counter would turn that into a
      // slow-motion telemetry flood.
      if (!snapshotCapCounted) {
        snapshotCapCounted = true;
        telemetry.errors.snapshot_cap = (telemetry.errors.snapshot_cap || 0) + 1;
        saveTelemetry();
      }
      return false;
    }
    var accepted = BcOutbox.enqueue({
      kind: DEDUP_SNAPSHOT,
      fields: body,
      ts: Date.now(),
      ctx: { dedup: sig }
    });
    // State advances only on an accepted hand-off. Advancing first is how a
    // refused bag used to consume a page slot AND move the change marker past
    // itself, so nothing ever re-attempted it.
    if (!accepted) return false;
    snapshotsSentThisPage++;
    lastSnapshotHash = sig;
    // Item 22's lease, taken at the same moment and for the same reason as the
    // page counter: this bag is out, so the session's one snapshot-lane device
    // row is spoken for. A bag the outbox refused above never gets here.
    if (body && body.device) claimSnapshotDevice(sig);
    noteFingerprintDelivered(body);
    return true;
  }

  // Re-photograph on change, for the life of the page (2s burst, then 30s).
  function startSnapshotWatch() {
    if (snapshotWatchStarted) return;
    snapshotWatchStarted = true;
    pollUntilDone('identifier_snapshot_watch', function () {
      // SIGNATURE FIRST (perf item 25). This tick used to build the whole
      // device bag — WebGL context creation, five matchMedia calls, the
      // navigation-timing entry, the media-device counts — and then compare a
      // hash that is derived from cookies alone. On the overwhelming majority
      // of ticks the answer is "nothing changed" and every byte of that work
      // was discarded. The projection below is cookie reads; the bag is built
      // only on the tick where a send is actually due.
      var sig = snapshotSignature(buildSnapshotIdentifiers());
      if (sig !== lastSnapshotHash) {
        var body = buildSnapshotBag();
        // enqueueSnapshot advances the marker itself, and ONLY on an accepted
        // hand-off. This used to advance it first, unconditionally, so a bag
        // refused at the page cap was never re-attempted even once a slot
        // freed up. A refused bag now stays un-advanced and the next tick
        // tries again; the cap counter is per-page so that is not noisy.
        enqueueSnapshot(body);
      }
      return false;   // never done — the bag can change again at any time
    }, { interaction: false });
  }

  function fireIdentifierBeacon() {
    try {
      // Cheap pre-check: if the bag as it stands RIGHT NOW is one this session
      // already delivered, skip the 1.5s shot and its app-proxy token fetch —
      // otherwise every page load would pay for a request whose bag we know is
      // a duplicate. The watcher below still runs, so anything arriving later
      // is still caught.
      // Signature-first here too: the pre-check is a dedupe question, and the
      // dedupe key is the identifier projection. Building the device bag to
      // ask it made every page load pay for a WebGL probe whose result was
      // discarded on the common (already-delivered) path.
      var already = BcOutbox.seen(snapshotSignature(buildSnapshotIdentifiers()));
      if (already) { startSnapshotWatch(); return; }
      // Wait a beat for async SDKs (Klaviyo, Meta, Reddit) to set their
      // cookies before we read them. 1.5s is enough on a typical page.
      setTimeout(function () {
        try {
          var body = buildSnapshotBag();
          // Hand the bag to the outbox. It owns the transport choice (beacon
          // first, keepalive fetch under the body-size ceiling), the ack, the
          // canonical-aid adoption and the durable per-bag marker.
          // Returns the hand-off result rather than dropping it. Nothing
          // branches on it here (the watcher below re-attempts a refused bag),
          // but this is the fourth site of the ignored-enqueue-result class and
          // a silent `false` is what made the other three wrong.
          var sendBeacon = function () {
            var handed = enqueueSnapshot(body);
            startSnapshotWatch();
            return handed;
          };

          // ─── App Proxy signed identity binding (logged-in only) ────────
          // Ask the Shopify App Proxy (/apps/bc → CRM) for a short-lived
          // signed token proving this bc_aid came through the authenticated
          // proxy, and attach it as `bt`. The DOOR is what then records the
          // write as verified: it verifies `bt` against IDENTIFY_BRIDGE_TOKEN
          // (v2 token shape `${exp}.${customerId}.${hmac}`) — the CRM Worker
          // only mints it. This is a silent enhancement: if the proxy isn't
          // configured the fetch fails and the beacon still fires unverified.
          // A 1.2s cap ensures a slow/absent proxy never delays (or blocks)
          // the beacon.
          //
          // 2026-09-07: only fetch when a customer is logged in. The route is
          // logged-in-only — Shopify passes an empty `logged_in_customer_id`
          // for anonymous visitors and the CRM answers 401 `not_logged_in` —
          // so for every logged-out visitor (the large majority of page views)
          // this was a guaranteed-failing round trip on every page load. The
          // flag is emitted by snippets/datalayer-init.liquid, which is Liquid
          // and can see `customer`; this asset is plain JS and cannot. It is a
          // boolean only, never the customer object.
          var beaconSent = false;
          var proceed = function () {
            if (beaconSent) return;
            beaconSent = true;
            sendBeacon();
          };
          setTimeout(proceed, 1200);
          if (window.__bcCustomerLoggedIn !== true) {
            // Skip: the identify-token route is logged-in only, so for an
            // anonymous visitor there is no token to get. Same silent
            // fall-through as a failed fetch — the beacon fires unverified.
            proceed();
          } else {
            try {
              fetch(ENDPOINTS.identifyToken + '?bc_aid=' + encodeURIComponent(currentBcAid() || ''), {
                method: 'GET',
                credentials: 'include',
              }).then(function (r) {
                return r && r.ok ? r.json() : null;
              }).then(function (j) {
                if (j && j.token) body.bt = j.token;
              }).catch(function () {
                // Proxy not configured / network error → fall back silently.
              }).then(proceed);
            } catch (e) {
              proceed();
            }
          }
        } catch (e) { noteModuleError('identifier_snapshot', e); }
      }, 1500);
    } catch (e) { noteModuleError('identifier_snapshot', e); }
  }
  guard('identifier_snapshot', fireIdentifierBeacon)();

  // first_touch owed/ack markers (MEV fix r1, C1 — see fireFirstTouchMint).
  var FT_ACK_KEY = 'bc_ft_ack';
  var FT_PENDING_KEY = 'bc_ft_pending';
  function firstTouchAid() {
    return currentBcAid() || getBootstrapCandidate() || null;
  }
  function firstTouchOwed() {
    var aid = firstTouchAid();
    if (!aid) return false;
    var acked = null, pending = null;
    try { acked = localStorage.getItem(FT_ACK_KEY); pending = localStorage.getItem(FT_PENDING_KEY); } catch (e) {}
    if (acked === aid) return false;
    // No cookie id at all (cookies blocked, or the head core never ran): the
    // pre-2026-10-03 rule — a candidate-only visitor is first contact.
    if (!currentBcAid()) return true;
    return window.__bcAidDecided === aid || pending === aid;
  }
  function noteFirstTouchAck() {
    try {
      var aid = firstTouchAid();
      if (aid) localStorage.setItem(FT_ACK_KEY, aid);
      localStorage.removeItem(FT_PENDING_KEY);
    } catch (e) {}
  }

  // ─── 2026-07-14 First-touch bc_aid mint ────────────────────────────────
  // The bc_aid cookie is SERVER-owned: the door (events.beyondcarat.com/collect)
  // only Set-Cookies it in response to a /collect POST that carries our
  // bootstrap candidate — it canonicalizes the candidate, accepts it, and echoes
  // {bc_aid, canonical} which the outbox adopts (→ refreshBcAidCarriers +
  // the adopt cart write, posted only when the cart differs). Before this,
  // /collect was reached ONLY by two rare events (bc_identify_emit,
  // checkout_intent), so the vast majority of visitors never minted a bc_aid — and syncIdentifiersToCart could not write
  // bc_fbp/bc_fbc/click-id cart attributes for them either, blowing the checkout
  // pixel's fbc attribution. Fire ONE /collect on first load when we have no aid
  // yet so the door mints a durable bc_aid. Guards: only when no aid; once per
  // session; fire-and-forget (never awaited). A duplicate first_touch is harmless
  // (a retry carries the same outbox event_id, so the door collapses it on its
  // idempotency key even though the rebuilt body differs, and competing bootstrap
  // candidates converge through the server's alias table), a missed mint
  // is not — which is exactly why the once-per-session marker now commits ON ACK
  // (B7/D14). It used to be written BEFORE the send, so a single lost request
  // meant this visitor got no mint for the rest of the session.
  //
  // MEV fix r1 (C1, 2026-10-03): "no aid yet" is NO LONGER the test for
  // "first contact". The inline head core now writes the bc_aid cookie on the
  // very first page, so `currentBcAid()` is truthy for every new visitor and
  // the old `if (currentBcAid()) return;` silenced first_touch for all of them
  // (no door Set-Cookie refresh, no first_touch-keyed device/bot evidence, no
  // CRM landing row). The rule now: first_touch is OWED for an id the head
  // DECIDED (window.__bcAidDecided this page, or localStorage bc_ft_pending
  // from an earlier page) until the door ACKS it (2xx → localStorage
  // bc_ft_ack=<id>, see outboxFetch). An id the door set itself (cookie
  // present before the head ran, no pending marker) was met by the door
  // already and is never re-announced. Always `needsResponse`, so it is a
  // credentialed fetch: the response re-Set-Cookies bc_aid (Safari's cap on
  // JS-written cookies) and echoes {bc_aid, canonical}.
  guard('first_touch', function fireFirstTouchMint() {
    if (!firstTouchOwed()) return;
    if (BcOutbox.seen(DEDUP_FIRST_TOUCH)) return;
    BcOutbox.enqueue({
      kind: 'first_touch',
      // session_id: the ONE visit id (contract 1) — first contact belongs to a visit.
      fields: { consent_state: null, session_id: currentSessionId() },
      ts: Date.now(),
      // needsResponse: the mint is load-time and reads the door's {bc_aid}
      // echo (adoptCanonicalAid). Before 2026-08-22 it went through the
      // generic transport: a blind sendBeacon (reply unreadable) AND a
      // keepalive fetch — the same WebKit quota that was losing the handoff
      // mint on collection pages.
      ctx: { dedup: DEDUP_FIRST_TOUCH, needsResponse: true }
    });
  })();

  // ─── 2026-10-02 Theme view beacon (bc-tracking plan item #2) ───────────
  // 25–35% of desktop visitors never get the Shopify custom pixel to the door
  // (blockers; the pixel's sendBeacon reports success, so it has no fallback),
  // and the CRM then has no page_view / view_item for them. This transport DOES
  // reach the door for those visitors (their first_touch lands), so every page
  // load also sends `page_seen`, and a product page `view_item_seen`, through
  // the outbox. bc-tracking forwards exactly ONE CRM page_view / view_item per
  // real view — the pixel's copy when it arrived, this one when it did not —
  // so this never double-counts. The door accepts these two names ONLY from
  // this transport and rebuilds their payload from a fixed field list.
  //
  // Consent: same helper, same rule as bc-page-vitals and the soft-nav bridge
  // — nothing LEAVES until window.bcConsent settles (bounded, ≤1.5 s; the
  // outbox defers), then an explicit analytics/marketing "no"
  // (captureAllowed() === false) sends nothing; unknown sends. The outbox
  // stamps the real answer on the envelope.
  //
  // No dedup signature: every document load is its own view (a reload is a
  // second view, exactly as the pixel counts it). URL + referrer are frozen at
  // enqueue so a retry drained on a later page still names this page.
  guard('view_seen', function fireViewSeen() {
    function viewData() {
      try {
        var el = document.querySelector('script[data-bc-view-seen]');
        return el ? (JSON.parse(el.textContent || '{}') || {}) : {};
      } catch (e) { return {}; }
    }
    function productItem(d) {
      var p = d.product || {}, v = d.variant || {};
      if (!p.id || !v.id) return null;
      var cents = Number(v.price_cents);
      return {
        item_id: String(v.sku || v.id),
        item_variant_id: String(v.id),
        item_product_id: String(p.id),
        item_name: p.title || '',
        item_brand: p.vendor || 'Beyond Carat',
        item_category: String(p.type || '').trim(),
        item_variant: v.title || '',
        item_url: p.url || '',
        price: isFinite(cents) ? Math.round(cents) / 100 : 0,
        currency: d.currency || '',
        quantity: 1,
        index: 0
      };
    }
    // An explicit "no" that is ALREADY known stops the observation here; one
    // that arrives later is applied at send time (outboxSend).
    if (!viewCaptureAllowed()) return;
    // MEV fix r1 (BR-2 / BR-4): the observation is BUILT AND QUEUED NOW, at
    // page load — url, referrer, title, visit id and `t_view` are frozen with
    // it, so a soft navigation (pushState) or a quick exit a few milliseconds
    // later can neither rename it nor lose it. It leaves after a short
    // not-before (the session's FIRST /collect carries the device bag, and
    // enumerateDevices() resolves a beat after load), or at once on pagehide.
    var tView = Date.now();
    // T1: the landing view names the URL the shopper actually arrived on.
    var url = (window.__bcLandingUrl ? window.__bcLandingUrl() : location.href);
    var ref = document.referrer || '';
    var d = viewData();
    var base = {
      page_title: String(document.title || '').slice(0, 300),
      session_id: currentSessionId(),
      page_template: d.template || null,
      t_view: tView
    };
    BcOutbox.enqueue({ kind: 'page_seen', fields: base, ts: tView, url: url, referrer: ref, notBefore: tView + 300 });
    if (d.template !== 'product') return;
    var item = productItem(d);
    if (!item) return;
    BcOutbox.enqueue({
      kind: 'view_item_seen',
      fields: {
        page_title: base.page_title,
        session_id: base.session_id,
        page_template: 'product',
        t_view: tView,
        ecommerce: { currency: item.currency, value: item.price, items: [item] }
      },
      ts: tView, url: url, referrer: ref,
      // The customizer prices on DOMContentLoaded; like the pixel, its bc_vc
      // cookie gets 400 ms, and is read when the entry is sent.
      notBefore: tView + 400,
      vcHandle: (d.product && d.product.handle) || ''
    });
  })();

  // Cross-device Klaviyo profile-id probe (2026-05-26 multi-way audit).
  // When the Klaviyo onsite SDK has already identified the visitor (any
  // previous Klaviyo email click, form submit, or programmatic identify),
  // their klaviyo_id sits on window.klaviyo. The SDK loads async, so we
  // try a short retry loop and push a follow-up dataLayer event if it
  // becomes available after bc_init has already fired. Idempotent — Tag
  // #207's dedup guard collapses duplicate values.
  guard('klaviyo_id_probe', function probeKlaviyoId() {
    // Was a 5s ceiling (20 × 250ms), which only ever caught an SDK that was
    // already warm. Now it watches for the life of the page (D18).
    pollUntilDone('klaviyo_id_probe', function () {
      var k = window.klaviyo;
      // Klaviyo's window.klaviyo is a catch-all Proxy: EVERY property name
      // reads as a function, and calling an unknown one queues it on
      // _klOnsite, where the SDK logs "Unable to process event: id,…" (5–24
      // console errors per page) and hands back a Promise — so this probe
      // never produced a string. Only call id() on an object that is NOT a
      // catch-all (a made-up property reads undefined); reading a property
      // on the Proxy queues nothing. Same result on every object this ever
      // returned a string for (2026-10-23 speed pass).
      var catchAll = false;
      try { catchAll = !!k && typeof k.__bcNoSuchMethod__ === 'function'; } catch (e) { catchAll = true; }
      var kid = k && !catchAll && typeof k.id === 'function' ? k.id() : (k && typeof k.id !== 'function' && k.id) || null;
      if (!kid || typeof kid !== 'string' || !kid.length) return false;
      (window.dataLayer = window.dataLayer || []).push({
        event: 'bc_klaviyo_id_seen',
        klaviyo_id: kid
      });
      return true;
    });
  })();

  // Sync bc_aid + Meta cookies (_fbp, _fbc) into Shopify cart.attributes so
  // the Customer Events pixel can read them from event.data.checkout.attributes
  // in checkout-stage events. Only POSTs to /cart/update if any attribute is
  // missing or stale (avoids unnecessary cart-write traffic on every page load).
  //
  // Why this matters for fbp/fbc: Shopify's Customer Events pixel sandbox
  // runs at a different origin than the storefront, so browser.cookie.get('_fbp')
  // returns null for first-party cookies on .beyondcarat.com. Cart attributes
  // ARE exposed to the pixel via event.data.checkout.attributes — confirmed
  // working for bc_aid (100% Purchase coverage). Without this sync, Stape
  // Storage Writer captures empty fbp/fbc in 96% of cart_token rows, breaking
  // webhook Purchase fbc attribution (Meta diagnostic: "100%+ median lift").
  //
  // Declared (not a self-invoking IIFE) so the handoff-restore path below can
  // re-invoke it after writing a restored _fbc / bc_* click-ID cookie, pushing
  // the recovered ids straight into cart.attributes. The immediate invocation
  // just below preserves the original once-per-load behaviour.
  // GA4 identity bridge (2026-08-13). The checkout pixel sandbox cannot read
  // first-party cookies (the same isolation that forced _fbp/_fbc through the
  // cart), and GA4's identifiers were never added to this bridge — so every
  // pixel-sent event and both purchase legs reached GA4 under bc_aid, an
  // identity GA4 has never seen, and 100% of revenue (17/17 transactions,
  // Aug 6–13) landed in Unassigned while every real channel read $0.
  //
  // _ga = GA1.1.<random>.<first-visit-ts>; the client id is the LAST TWO
  // dot-segments (never assume the version prefix is one segment).
  function gaClientId() {
    var v = readCookie('_ga');
    if (!v) return '';
    var parts = String(v).split('.');
    if (parts.length < 4) return '';
    var cid = parts.slice(-2).join('.');
    return /^\d+\.\d+$/.test(cid) ? cid : '';
  }
  // _ga_GXGY8VW3R0 = GS1.1.<session-id>.<count>… or GS2.1.s<session-id>$o<n>…
  // The session id is the third dot-segment; GS2 prefixes it with "s" and
  // switches to "$" separators after it. PINNED to OUR stream's cookie
  // (G-GXGY8VW3R0, the measurement id sGTM forwards to) — a wildcard
  // _ga_[A-Z0-9]+ grabs whichever _ga_* cookie appears first, and Shopify's
  // Google & YouTube channel can set a SECOND property's cookie; shipping that
  // property's session id would stitch the purchase into a session that does
  // not exist on ours (2026-08-13 review finding).
  function gaSessionId() {
    var m = document.cookie.match(/(?:^|;\s*)_ga_GXGY8VW3R0=([^;]+)/);
    if (!m) return '';
    var raw = m[1];
    try { raw = decodeURIComponent(raw); } catch (e) {}
    var seg = (String(raw).split('.')[2] || '').split('$')[0];
    if (seg.charAt(0) === 's') seg = seg.slice(1);
    return /^\d{8,}$/.test(seg) ? seg : '';
  }

  function cartAttributePayload(aid) {
    var update = {};
    if (aid) update.bc_aid = aid;
    var fbp = readCookie('_fbp');
    var fbc = readCookie('_fbc');
    if (fbp) update.bc_fbp = fbp;
    if (fbc) update.bc_fbc = fbc;
    // GA4 client + session ids → checkout pixel (event.data.checkout.attributes)
    // and order note attributes. Consumers ship separately: the updated
    // customer-events pixel (paste) + sGTM tag 154 (workspace staged
    // 2026-08-13, publish pending) — until both land these attributes are
    // written but unread, which is safe.
    var gaCid = gaClientId();
    if (gaCid) update.bc_ga_cid = gaCid;
    var gaSid = gaSessionId();
    if (gaSid) update.bc_ga_sid = gaSid;
    // Traffic-source label attributes (Trap 2 of HANDOFF-2026-08-06): this
    // payload used to carry identity + click ids only, so the post-repair
    // reSync() posted no label and theme.liquid's own POST waits for the NEXT
    // page load. Names + defaults mirror theme.liquid:959-978 exactly. Cart
    // attributes are order-level note attributes — NOT customer-visible (only
    // line-item properties are; these never ride that path).
    var srcLabel = readCookie('_bc_src');
    if (srcLabel) {
      update['Traffic Source'] = srcLabel;
      update['Original Referrer'] = readCookie('_bc_ref') || 'none';
      update['Landing Page'] = readCookie('_bc_land') || '/';
    }
    // Reddit click ID — persists Reddit ad attribution into the order so
    // downstream Purchase delivery can read it from note_attributes.bc_rdt_cid.
    var rdtCid = readCookie('bc_rdt_cid');
    if (rdtCid) update.bc_rdt_cid = rdtCid;

    // Click IDs → cart attributes with display names that match the
    // meta-dashboard parser. Cookie names keep the bc_ prefix for sGTM Click ID
    // Restorer compatibility; cart-attribute keys are Title Case for orders.
    var clickIdAttrs = {
      'Gclid':          readCookie('bc_gclid'),
      'Gbraid':         readCookie('bc_gbraid'),
      'Wbraid':         readCookie('bc_wbraid'),
      'Gad Source':     readCookie('bc_gad_source'),
      'Gad Campaignid': readCookie('bc_gad_campaignid'),
      'Msclkid':        readCookie('bc_msclkid'),
      'Fbclid':         readCookie('bc_fbclid'),
      'Rdt Cid':        readCookie('bc_rdt_cid'),
      'Ttclid':         readCookie('bc_ttclid'),
      'Epik':           readCookie('bc_epik'),
      'Twclid':         readCookie('bc_twclid'),
      'Li Fat Id':      readCookie('bc_li_fat_id')
    };
    for (var k in clickIdAttrs) {
      var v = (clickIdAttrs[k] || '').trim();
      if (v) update[k] = v;
    }
    return update;
  }

  // The page-wide cart-attribute gate defined inline in layout/theme.liquid
  // (Traffic Source block). Absent on a layout without that block: every
  // caller then takes its original path.
  function cartAttrGate() {
    try {
      var g = window.__bcCartAttrs;
      return g && typeof g.post === 'function' && typeof g.write === 'function' &&
        typeof g.sync === 'function' ? g : null;
    } catch (e) { return null; }
  }

  // Mark the gate's open /cart.js read as predating a cart change. No-op
  // without the gate (or without an open read).
  function cartAttrInvalidate() {
    try {
      var g = window.__bcCartAttrs;
      if (g && typeof g.invalidate === 'function') g.invalidate();
    } catch (e) {}
  }

  function postCartAttributes(update, keepalive) {
    if (!update || !Object.keys(update).length) return;
    // Same request through the gate, which records it so an older call-time
    // value queued there never lands over this write.
    var gate = cartAttrGate();
    if (gate) { gate.post(update, !!keepalive); return; }
    try {
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: update }),
        credentials: 'same-origin',
        keepalive: !!keepalive
      }).catch(function () { /* silent */ });
    } catch (e) { /* silent */ }
  }

  function syncIdentifiersToCart(aid, opts) {
    opts = opts || {};
    aid = aid || currentBcAid();
    // No early return when aid is absent: cartAttributePayload(null) still emits
    // bc_fbp/bc_fbc/click-id attributes (only bc_aid itself is aid-gated) and
    // postCartAttributes no-ops on an empty update — so Meta/click identity can
    // reach the cart (→ checkout pixel) before a server-owned bc_aid is minted.
    if (opts.force) {
      // adopt (door reply with a canonical bc_aid): same payload, captured now,
      // but written only if a value differs from a fresh /cart.js read (falls
      // back to this immediate keepalive POST whenever that read can't be
      // trusted). checkout-intent and reSync stay unconditional.
      var gateF = opts.adopt ? cartAttrGate() : null;
      if (gateF) { gateF.write(cartAttributePayload(aid), true); return; }
      postCartAttributes(cartAttributePayload(aid), true);
      return;
    }
    // Debounce: skip if we just synced this session. Fast navigation, section
    // re-renders, and bfcache restores otherwise fire the sync 3–5× / sec
    // → Shopify's 4 req/sec cart endpoint rate-limit kicks in → silent 429s
    // → attributes never land. 5s window is plenty for normal UX.
    try {
      var DEBOUNCE_MS = 5000;
      var last = +sessionStorage.getItem('bc_cart_sync_ts') || 0;
      if (Date.now() - last < DEBOUNCE_MS) return;
      sessionStorage.setItem('bc_cart_sync_ts', String(Date.now()));
    } catch (e) { /* sessionStorage disabled — let the sync run */ }
    // The original path: own /cart.js GET, then POST only what differs.
    function ownReadSync() {
      try {
        fetch('/cart.js', { credentials: 'same-origin' })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (c) {
            if (!c) return;
            var attrs = c.attributes || {};
            var wanted = cartAttributePayload(aid);
            var update = {};
            for (var k in wanted) {
              if (Object.prototype.hasOwnProperty.call(wanted, k) && attrs[k] !== wanted[k]) {
                update[k] = wanted[k];
              }
            }
            postCartAttributes(update, false);
          })
          .catch(function () { /* silent */ });
      } catch (e) { /* silent */ }
    }
    // Share the page's one in-flight /cart.js read; build runs when it answers,
    // exactly when this function's own GET used to answer. If that shared read
    // fails or stalls, the gate hands back to ownReadSync (the original path).
    var gateS = cartAttrGate();
    try {
      if (gateS && gateS.sync(function () { return cartAttributePayload(aid); }, ownReadSync) === true) return;
    } catch (e) {}
    ownReadSync();
  }
  // ─── cart_sync cadence (2026-09-05, perf item 25) ───────────────────────
  // This was `setInterval(…, 60000)`, unconditional, for the life of every
  // page — so a tab left open in the background issued a `/cart.js` GET to
  // Shopify every minute, forever, for a cart nobody could be changing. At
  // 100k visitors/day that is the single largest source of storefront requests
  // this file makes, and every one of them in a hidden tab is answering a
  // question whose answer cannot have changed.
  //
  // The replacement is the same sync driven by the three things that can
  // actually invalidate it:
  //   · a cart MUTATION (Dawn/theme events + our own /cart/update posts),
  //   · the tab becoming VISIBLE again (the shopper may have changed the cart
  //     in another tab — this is the case the interval was really covering),
  //   · and a 60 s heartbeat that runs ONLY while the tab is visible.
  // The 5 s sessionStorage debounce inside syncIdentifiersToCart still applies,
  // so a mutation burst collapses to one GET.
  var CART_SYNC_INTERVAL_MS = 60000;
  var cartSyncTimer = null;
  function cartSyncTick() {
    // A mutation/visibility/heartbeat tick: the old code always took a fresh
    // read here, so a /cart.js read the gate opened earlier must not be
    // reused for it (writes queued on that read go out now, as they used to).
    cartAttrInvalidate();
    syncIdentifiersToCart(currentBcAid());
  }
  function stopCartSyncTimer() {
    if (!cartSyncTimer) return;
    try { clearInterval(cartSyncTimer); } catch (e) {}
    cartSyncTimer = null;
  }
  function startCartSyncTimer() {
    if (cartSyncTimer) return;
    try {
      cartSyncTimer = setInterval(guard('cart_sync', cartSyncTick), CART_SYNC_INTERVAL_MS);
    } catch (e) { cartSyncTimer = null; }
  }
  // Theme/Dawn cart mutation events, on the DOM.
  //
  // Round 1 (A4): these six names have ZERO dispatchers in the live theme —
  // grepped across assets/, snippets/, sections/ and layout/ on 2026-09-06, no
  // `dispatchEvent` or `new CustomEvent` for any of them. Dawn's bus is
  // `assets/pubsub.js`, an IN-MEMORY subscriber registry that never touches the
  // DOM: `publish(PUB_SUB_EVENTS.cartUpdate, …)` is called by
  // `assets/product-form.js:73`, `assets/cart.js:225` and
  // `assets/quick-add-bulk.js:131`, and a `document.addEventListener` can no
  // more hear it than it can hear a function call. So the event-driven half of
  // the cart sync was dead: only the 60 s visible heartbeat ever ran, and a
  // shopper who added to cart and went straight to checkout could reach it with
  // up to a minute of stale cart attributes.
  //
  // The DOM list STAYS — it costs six listeners, it is what a future section or
  // a third-party app would dispatch, and removing it would be a second change
  // in the same breath as the fix. The bus subscription below is the one that
  // actually fires today.
  var CART_MUTATION_EVENTS = [
    'cart:refresh', 'cart:updated', 'cart-update', 'cart:change',
    'cart-drawer:updated', 'bc:cart-updated'
  ];

  // The theme's own bus — which does NOT exist yet when this file evaluates.
  //
  // Round 2 (H1). THE LIVE DOCUMENT ORDER, read off `beyond-carat-shopify` @
  // `live` 31330bc:
  //
  //   layout/theme.liquid:8      {%- render 'datalayer-init' -%}   ← in <head>
  //   snippets/datalayer-init.liquid:480
  //                              <script src=…bc-tracking-async.js defer>
  //   layout/theme.liquid:307    <script src=…constants.js defer>
  //   layout/theme.liquid:308    <script src=…pubsub.js   defer>
  //
  // This asset is rendered by `datalayer-init` INSIDE `<head>` — it is
  // `bc-relay-shared.js` and `bc-page-vitals.js` that sit at the end of
  // `<body>`, not this one — and deferred classic scripts execute in DOCUMENT
  // ORDER. So this file runs FIRST, before `constants.js` and before
  // `pubsub.js`: at module-eval time neither `subscribe` nor `PUB_SUB_EVENTS`
  // exists, and a one-shot attempt here can never attach. Round 1's fix was
  // therefore dead on arrival, in exactly the way the dead DOM listeners it
  // replaced were.
  //
  // The attach is re-attempted on `DOMContentLoaded` — which the spec fires
  // AFTER every deferred script has run, so it is the first moment the bus is
  // guaranteed present — on `load`, and on a short poll that covers a theme
  // that ever loads `pubsub.js` asynchronously instead. The poll is bounded
  // (5 s) and stops on the first success; it makes no requests, so the
  // hidden-tab contract is untouched.
  //
  // Why the reads still look odd: `subscribe` is a function DECLARATION, so it
  // lands on `window`; `PUB_SUB_EVENTS` is a top-level `const`, which does NOT
  // — it lives in the global LEXICAL environment, reachable as a bare
  // identifier but absent from `window` (verified: `assets/constants.js`
  // assigns nothing to `window`). Hence the `typeof` guard on both: reading a
  // not-yet-declared `const` as a bare identifier throws a ReferenceError, and
  // a throw here would take the whole cart lane down.
  //
  // The 5 s debounce inside syncIdentifiersToCart still collapses a burst, so a
  // theme that publishes cartUpdate three times for one add still makes one GET.
  var THEME_BUS_POLL_MS = 250;
  var THEME_BUS_POLL_MAX = 20;          // 20 x 250 ms = 5 s, then give up
  var themeBusSubscribed = false;

  /** One attempt. True once the subscription is actually held. */
  function attachThemeCartBus(onCartChange) {
    if (themeBusSubscribed) return true;
    var sub = null;
    try { if (typeof subscribe === 'function') sub = subscribe; } catch (e) {}
    if (!sub) {
      try { if (window && typeof window.subscribe === 'function') sub = window.subscribe; } catch (e) {}
    }
    if (!sub) return false;
    var events = null;
    try { if (typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS) events = PUB_SUB_EVENTS; } catch (e) {}
    if (!events) {
      try { events = (window && window.PUB_SUB_EVENTS) || null; } catch (e) {}
    }
    // The literal is the fallback, not the primary: the map is the theme's own
    // spelling and a rename there must not silently unsubscribe us.
    var name = (events && events.cartUpdate) || 'cart-update';
    try { sub(name, onCartChange); } catch (e) { return false; }
    themeBusSubscribed = true;
    return true;
  }

  function subscribeToThemeCartBus(onCartChange) {
    if (attachThemeCartBus(onCartChange)) return true;
    var tries = 0;
    var timer = null;
    function stop() {
      if (!timer) return;
      try { clearInterval(timer); } catch (e) {}
      timer = null;
    }
    function retry() {
      if (attachThemeCartBus(onCartChange)) { stop(); return; }
      if (++tries >= THEME_BUS_POLL_MAX) stop();
    }
    // NOT gated on `document.readyState === 'loading'`: a deferred script runs
    // while readyState is already 'interactive', and DOMContentLoaded has still
    // not fired. Gating on 'loading' is how this listener would be skipped on
    // the one load path that matters.
    try { document.addEventListener('DOMContentLoaded', retry); } catch (e) {}
    try { window.addEventListener('load', retry); } catch (e) {}
    try { timer = setInterval(retry, THEME_BUS_POLL_MS); } catch (e) { timer = null; }
    return false;
  }

  guard('cart_sync', function () {
    syncIdentifiersToCart(bcAid);
    for (var i = 0; i < CART_MUTATION_EVENTS.length; i++) {
      try {
        document.addEventListener(CART_MUTATION_EVENTS[i], guard('cart_sync', cartSyncTick));
      } catch (e) {}
    }
    subscribeToThemeCartBus(guard('cart_sync', cartSyncTick));
    // A submitted /cart/add form is a mutation the events above can miss
    // (a non-AJAX add navigates, an AJAX add may publish nothing we hear).
    try {
      document.addEventListener('submit', function (e) {
        var form = e && e.target;
        if (!form || !form.action || form.action.indexOf('/cart/add') === -1) return;
        cartAttrInvalidate();
        setTimeout(guard('cart_sync', cartSyncTick), 400);
      }, true);
    } catch (e) {}
    try {
      document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'visible') {
          startCartSyncTimer();
          // The tab was away; the cart may have moved in another one.
          guard('cart_sync', cartSyncTick)();
        } else {
          stopCartSyncTimer();
        }
      });
    } catch (e) {}
    if (document.visibilityState !== 'hidden') startCartSyncTimer();
  })();

  /** Every `/cart/add` form inside `scope` — or the scope itself when it IS one. */
  function productFormsIn(root) {
    if (root.matches && root.matches('form[action*="/cart/add"]')) return [root];
    return root.querySelectorAll('form[action*="/cart/add"]');
  }

  /**
   * Set one carrier's value on every product form, CREATING the hidden input
   * when a form has none.
   *
   * WHY CREATE, instead of only writing the rendered `[data-bc-*-carrier]` nodes
   * (2026-07-27): the customizer swaps a sibling product by REPLACING the
   * product section with fetched HTML. The replacement resets every carrier to
   * value="" and can leave a `/cart/add` form with no carrier inputs at all —
   * a form a querySelector-only refresh can never stamp. Verified live: after
   * one metal swap the page holds TWO `/cart/add` forms and only one carries
   * any `_bc_*` input. Order #1120 (White Gold, express checkout from a Meta
   * in-app browser) reached Shopify with `_bc_aid`/`_bc_fbp`/`_bc_fbc` but NO
   * `_bc_ids`, so its Traffic Source / Original Referrer / Landing Page never
   * reached the order and a paid Meta sale was booked as "referral".
   *
   * Creating the input makes the carrier independent of whatever the swapped
   * fragment happens to contain. Same create-if-missing contract as
   * `syncInput()` in diamond-customizer.js.
   *
   * An empty value is still written, which is what CLEARS a previously
   * stamped value on refresh. Do not assume Shopify drops it: a form-encoded
   * /cart/add with a blank property LANDS ON THE LINE as '' (probed live
   * 2026-08-14), so every reader of a carrier property must treat '' as
   * absent — the gift stand-down marker's readers all do.
   */
  function stampCarrier(scope, dataAttr, propName, value) {
    var root = scope && scope.querySelectorAll ? scope : document;
    // 1. Carriers the theme rendered — unchanged behaviour.
    var existing = root.querySelectorAll('[' + dataAttr + ']');
    for (var i = 0; i < existing.length; i++) existing[i].value = value;
    // 2. Any product form the swap left without this carrier gets one.
    var forms = productFormsIn(root);
    for (var j = 0; j < forms.length; j++) {
      var form = forms[j];
      if (form.querySelector('[' + dataAttr + ']')) continue;
      var input = form.querySelector('input[name="properties[' + propName + ']"]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'properties[' + propName + ']';
        form.appendChild(input);
      }
      input.setAttribute(dataAttr, '');
      input.value = value;
    }
  }

  function refreshBcAidCarriers(scope) {
    try {
      stampCarrier(scope, 'data-bc-aid-carrier', '_bc_aid', currentBcAid() || '');
    } catch (e) {}
  }

  // Race-free Meta identity path: unlike the asynchronous /cart/update.js
  // sync above, these values travel in the product form itself. Refresh from
  // cookies at every submission/checkout intent because Meta may set or restore
  // them after this deferred asset initializes.
  function refreshMetaCookieCarriers(scope) {
    try {
      stampCarrier(scope, 'data-bc-fbp-carrier', '_bc_fbp', readCookie('_fbp') || '');
      stampCarrier(scope, 'data-bc-fbc-carrier', '_bc_fbc', readCookie('_fbc') || '');
    } catch (e) {}
  }

  // `_bc_ids` = "aid|fbp|fbc|src|ref|land" — ONE hidden field inside the product
  // form, so it rides an EXPRESS checkout ("Buy it now") that never touches the
  // cart.
  //
  // WHY THE SOURCE FIELDS ARE HERE (2026-07-27): Traffic Source / Original
  // Referrer / Landing Page are written to the CART (layout/theme.liquid POSTs
  // /cart/update.js). Express checkout builds its order straight from the
  // product form and never uses that cart, so those attributes never reach the
  // order — order #1117 (2026-07-25, $365, Meta in-app browser) had ZERO note
  // attributes for exactly this reason, while its `_bc_aid` line-item property
  // survived and was the only thing that attributed the sale. Same carrier,
  // same survival.
  //
  // ONE FIELD, NOT SIX: the Cart Transform's input query is scored against a
  // complexity-30 budget and already reads 11 line attributes. Complexity is
  // charged per FIELD REQUESTED, not per byte returned, so widening this one
  // string costs exactly nothing; adding `_bc_src`/`_bc_ref`/`_bc_land` as
  // separate reads would cost three. That is why the combined carrier exists.
  //
  // Values are pipe-JOINED, so a literal pipe must never survive into a
  // segment — one stray `|` in a landing-page query string would shift every
  // later field by one. Stripped, not escaped, so the Function needs no decoder
  // (it runs in a restricted runtime; fewer primitives = fewer surprises).
  var BC_IDS_CAPS = { src: 64, ref: 256, land: 512 };
  function bcIdsSegment(value, max) {
    if (!value) return '';
    return String(value).split('|').join('').slice(0, max);
  }
  function refreshCombinedIdentityCarriers(scope) {
    try {
      var aid = currentBcAid() || '';
      var fbp = readCookie('_fbp') || '';
      var fbc = readCookie('_fbc') || '';
      // Same cookies the cart-attribute writer in layout/theme.liquid reads, so
      // both checkout paths put IDENTICAL values on the order.
      var src = bcIdsSegment(readCookie('_bc_src'), BC_IDS_CAPS.src);
      var ref = bcIdsSegment(readCookie('_bc_ref'), BC_IDS_CAPS.ref);
      var land = bcIdsSegment(readCookie('_bc_land'), BC_IDS_CAPS.land);
      var value = (aid || fbp || fbc || src || ref || land)
        ? [aid, fbp, fbc, src, ref, land].join('|')
        : '';
      stampCarrier(scope, 'data-bc-ids-carrier', '_bc_ids', value);
    } catch (e) {}
  }

  // ── A discount code and the free gift are EITHER/OR, on every checkout path ──
  //
  // The cart page already refuses to trade a customer's own discount code for
  // the gift: cart-gift.js sees the code, stands down instead of auto-adding,
  // and records the refusal where the server can read it — `_free_gift:
  // 'declined'` on the lines that are staying. The cart transform honours any
  // non-empty, non-'true' marker and grants nothing.
  //
  // Express checkout never touches the cart page, so none of that reaches the
  // shopper who arrives on a /discount/CODE link from an email, opens a product
  // and presses "Buy it now": the checkout is built from the product FORM, the
  // cart module never runs, the transform sees a qualifying single-line cart
  // with no marker, and the customer gets the code AND $299 of studs. The
  // decision therefore has to be stamped where that checkout is actually built.
  //
  // WHY THE COOKIE, AND ONLY THE COOKIE (verified live 2026-08-14 against
  // beyondcarat.com):
  //   • `/discount/<CODE>` responds `set-cookie: discount_code=<CODE>; path=/;
  //     SameSite=Lax` — no HttpOnly flag, so this file can read it — and the
  //     cookie survives page views and add-to-cart untouched. That same session
  //     cookie is what carries the code into an express checkout, so its
  //     presence is not a proxy for "a code will apply": it is the mechanism.
  //   • `/cart.js` was the obvious alternative and cannot serve this cohort at
  //     all. A session holding WELCOME250 with an EMPTY cart reports
  //     `discount_codes: []`, and the buy-now shopper's cart is empty by
  //     definition. A code sitting on a NON-empty cart is already covered by
  //     the cart page's own stand-down, which marks every remaining line.
  //
  // FAILS TOWARD NO STAMP. No cookie, an unreadable one, or a thrown read
  // leaves the carrier empty, which is exactly today's behaviour — an
  // undetected code merely keeps a bug we already have, while a stamp on a
  // code-less order withholds a gift the customer was promised. Note the empty
  // value is written ACTIVELY on every refresh, never just skipped: a cookie
  // that has gone away must clear the input the previous refresh stamped, or a
  // code seen once would blocklist the rest of the session.
  //
  // OUR OWN CODE IS NOT A COMPETING CODE. cart-gift.js applies the gift by
  // fetching `/discount/FREE-EARRINGS`, which sets this very cookie. Reading
  // that back as "a competing code is active" would stamp 'declined' on every
  // product form for the rest of the session — starting the moment the customer
  // claimed the gift — and the next Buy-it-now would silently lose it.
  //
  // MUST MATCH `assign gift_code` in snippets/cart-gift-unlock.liquid, which is
  // where the storefront's copy of this code lives. Renaming the code in Admin
  // means editing both.
  var GIFT_DISCOUNT_CODE = 'FREE-EARRINGS';
  var GIFT_DECLINED = 'declined';

  /**
   * The discount code Shopify is holding for this shopper, ours excluded.
   *
   * EVERY `discount_code=` TOKEN, NOT THE FIRST. Cookies of one name can
   * coexist at different paths and domains, `document.cookie` exposes them all
   * with no path or domain to tell them apart, and `readCookie` returns the
   * first match — correct for its other callers, wrong here. A stale
   * FREE-EARRINGS scoped to /products would mask a live WELCOME250 scoped to /,
   * clear the marker, and hand out the gift on top of the code. Which duplicate
   * Shopify's server picks is not knowable from this side, so the never-both
   * invariant takes the conservative reading: ANY token that is a competing
   * code stands the gift down.
   *
   * Deliberately does not go through readCookie — this is the only predicate
   * that needs all matches, and widening the shared helper would change what
   * every other carrier reads.
   */
  function competingDiscountCode() {
    var raw = '';
    try { raw = document.cookie || ''; } catch (e) { return ''; }
    var mine = GIFT_DISCOUNT_CODE.toUpperCase();
    var tokens = raw.split(';');
    for (var i = 0; i < tokens.length; i++) {
      var eq = tokens[i].indexOf('=');
      if (eq === -1) continue;
      if (tokens[i].slice(0, eq).replace(/^\s+|\s+$/g, '') !== 'discount_code') continue;
      // slice past the FIRST '=' only: a code containing '=' stays intact.
      var value = tokens[i].slice(eq + 1);
      // Same decode contract as readCookie: a value carrying a raw '%' throws,
      // and the raw text is a more honest answer than no answer — it fails
      // toward standing the gift down.
      try { value = decodeURIComponent(value); } catch (e) {}
      value = value.replace(/^\s+|\s+$/g, '');
      // Shopify redeems codes case-insensitively, so the comparison is too.
      if (value && value.toUpperCase() !== mine) return value;
    }
    return '';
  }

  function refreshGiftStandDownCarrier(scope) {
    try {
      stampCarrier(
        scope,
        'data-bc-gift-carrier',
        '_free_gift',
        competingDiscountCode() ? GIFT_DECLINED : ''
      );
    } catch (e) {}
  }

  function refreshProductFormCarriers(scope) {
    try { refreshBcAidCarriers(scope); } catch (e) {}
    try { refreshMetaCookieCarriers(scope); } catch (e) {}
    try { refreshCombinedIdentityCarriers(scope); } catch (e) {}
    try { refreshGiftStandDownCarrier(scope); } catch (e) {}
  }

  function currentVariantId(target) {
    try {
      var form = target && target.closest ? target.closest('form') : null;
      var input = (form && form.querySelector('[name="id"]')) || document.querySelector('form[action*="/cart/add"] [name="id"]');
      if (input && input.value) return String(input.value);
      var variant = new URLSearchParams(location.search).get('variant');
      return variant ? String(variant) : null;
    } catch (e) {
      return null;
    }
  }

  function currentProductHandle() {
    try {
      var m = location.pathname.match(/\/products\/([^/?#]+)/);
      return m ? decodeURIComponent(m[1]) : null;
    } catch (e) {
      return null;
    }
  }

  function sendCheckoutIntent(reason, target) {
    try {
      var aid = currentBcAid();
      var payload = addRawCaptureFields({
        event: 'checkout_intent',
        checkout_intent_reason: reason || 'checkout',
        variant_id: currentVariantId(target),
        product_handle: currentProductHandle(),
        bc_aid: aid || null,
        bc_aid_candidate: aid ? null : (getBootstrapCandidate() || null)
      });
      // The outbox drains synchronously on enqueue, so the beacon is still
      // handed to the browser in the same tick as the click — before the
      // navigation this flare exists to outrun. Deliberately NOT deduped: each
      // checkout intent carries its own reason/variant and the door wants them all.
      BcOutbox.enqueue({
        kind: 'checkout_intent',
        fields: payload,
        ts: Date.now(),
        ctx: {}
      });
    } catch (e) {}
  }

  function handleCheckoutIntent(reason, target) {
    var aid = currentBcAid();
    refreshProductFormCarriers(document);
    // Force-flush cart attributes on checkout intent even without an aid: the
    // aid-less first-visit cohort (e.g. FB in-app buyer, seconds from click to
    // purchase before the first-touch mint resolves) still needs bc_fbp/bc_fbc/
    // click-ids in the cart for the checkout pixel. syncIdentifiersToCart handles
    // a null aid and postCartAttributes no-ops on an empty payload, so this is a
    // no-op when there's nothing to write.
    syncIdentifiersToCart(aid, { force: true });
    sendCheckoutIntent(reason, target);
  }

  /**
   * Cause-agnostic backstop for a rebuilt product form.
   *
   * `diamond:product-changed` only covers the ONE rebuild the customizer chooses
   * to announce. Every carrier is rendered `value=""` by the theme and filled at
   * runtime, so ANY re-render from server HTML — a customizer swap, Dawn's own
   * variant re-render, a third-party app, or a future refactor that drops the
   * event — resets them to blank, silently, exactly as the original bug did.
   * Watching the container itself removes that dependency on a bespoke contract.
   *
   * Scoped to `.product__info-container` (the node whose innerHTML the swap
   * replaces) rather than the document, and debounced, so this stays cheap on a
   * page whose price/gallery mutate constantly. `input.value = x` sets a
   * PROPERTY, not an attribute, so a plain re-stamp emits no mutation record;
   * the guard exists only for the create-if-missing path, which does append.
   */
  function observeProductFormRebuilds() {
    try {
      if (typeof MutationObserver !== 'function') return;
      var containers = document.querySelectorAll('.product__info-container');
      if (!containers.length) return;
      var pending = null;
      var stamping = false;
      var observer = new MutationObserver(function () {
        if (stamping || pending) return;
        pending = setTimeout(function () {
          pending = null;
          stamping = true;
          try { refreshProductFormCarriers(document); } catch (e) {}
          stamping = false;
        }, 50);
      });
      for (var i = 0; i < containers.length; i++) {
        observer.observe(containers[i], { childList: true, subtree: true });
      }
    } catch (e) {}
  }

  function initCheckoutIntentCapture() {
    try {
      // Deferred assets normally execute after parsing, but refresh immediately
      // and once more at DOM ready so either loading mode gets initialized.
      refreshProductFormCarriers(document);
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function () { refreshProductFormCarriers(document); });
      }
      // Capture runs before Dawn's product-form submit handler serializes the
      // form, ensuring the freshest cookie values are included in FormData.
      document.addEventListener('submit', function (ev) {
        try {
          var form = ev.target;
          if (!form || !form.matches || !form.querySelectorAll) return;
          var isProductForm = form.matches('form[action*="/cart/add"], form[data-type="add-to-cart-form"]') ||
            !!(form.closest && form.closest('product-form'));
          if (!isProductForm) return;
          refreshProductFormCarriers(form);
        } catch (e) {}
      }, true);
      // A sibling swap (metal / shape) replaces the product section, which wipes
      // every carrier back to "" — verified live on the PDP. Re-stamp the moment
      // the customizer reports the swap is done, so the form is correct even if
      // the shopper taps express pay before any other event fires.
      document.addEventListener('diamond:product-changed', function () {
        refreshProductFormCarriers(document);
      });
      observeProductFormRebuilds();
      document.addEventListener('pointerdown', function (ev) {
        var target = ev.target && ev.target.closest ? ev.target.closest('.shopify-payment-button, shopify-accelerated-checkout') : null;
        if (!target) return;
        handleCheckoutIntent('accelerated_checkout_pointerdown', target);
      }, true);
      document.addEventListener('click', function (ev) {
        if (!ev.target || !ev.target.closest) return;
        // ACCELERATED CHECKOUT ACTIVATED WITHOUT A POINTER. Keyboard and
        // assistive activation of "Buy it now" emits `click` with no
        // `pointerdown`, and accelerated checkout need not emit a native form
        // `submit` either — so for those shoppers the pointerdown arm above and
        // the submit arm further up BOTH miss, and whatever was stamped at page
        // load is what gets serialized. A cookie changed in another tab makes
        // that stale in either direction: a stale empty marker hands out the
        // gift on top of a discount code, a stale 'declined' withholds a gift
        // that is owed. Re-stamp here, in the capture phase, before Shopify
        // reads the form.
        //
        // REFRESH ONLY, NO SECOND BEACON. A mouse activation fires pointerdown
        // AND click, and checkout intents are deliberately not deduped, so
        // beaconing here would double every accelerated intent. (Keyboard
        // activation still sends no intent beacon at all — a pre-existing gap
        // in the flare, not in the marker.)
        if (ev.target.closest('.shopify-payment-button, shopify-accelerated-checkout')) {
          refreshProductFormCarriers(document);
          return;
        }
        var target = ev.target.closest('[name="checkout"], .cart__checkout-button, a[href*="/checkout"], button[name="checkout"], input[name="checkout"]');
        if (!target) return;
        handleCheckoutIntent('checkout_click', target);
      }, true);
      // A page restored from the bfcache and a tab returning to the foreground
      // can both be carrying a cookie that changed somewhere else — the shopper
      // applied a code in another tab, or claimed the gift there and left
      // FREE-EARRINGS behind. Neither event refreshed any carrier before this
      // (the existing pageshow handler only drains the outbox), so this hardens
      // _bc_aid, the Meta cookies and _bc_ids against the same staleness for
      // free. Cheap enough to run unconditionally: every arm is a synchronous
      // cookie read and a value assignment, and each keeps its own try/catch,
      // so one failing arm cannot stop the others.
      window.addEventListener('pageshow', function () {
        refreshProductFormCarriers(document);
      });
      document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'visible') refreshProductFormCarriers(document);
      });
    } catch (e) {}
  }
  guard('checkout_intent', initCheckoutIntentCapture)();

  // ═══════════════════════════════════════════════════════════════════════
  // BcHandoff — click-ID handoff propagation + restore (2026-07-03)
  //
  // Problem: an ad click lands in the Meta / IG in-app browser with a
  // `?fbclid=` (or gclid/ttclid/msclkid). That identity dies at three
  // boundaries: (a) internal navigation drops the param, (b) "Open in Safari"
  // hands off the CURRENT (already-clean) URL, (c) iOS 17+ Link Tracking
  // Protection strips the literal `fbclid` from links shared via Messages /
  // Mail. Meta CAPI then can't match the eventual purchase back to the click.
  //
  // Fix: copy the click identity into a short first-party code (minted by
  // events.beyondcarat.com/handoff/mint) carried in the URL under our OWN
  // param name `bcid`, and keep re-appending it. Any handoff/share carries
  // `bcid`; a landing WITH `bcid` restores the click identity (writes the
  // Meta-format `_fbc` cookie so fbevents / CAPI / cart-sync pick it up).
  //
  // 2026-07-03 extension — mint on EVERY visit (not just click-id landings) so
  // any shared/handed-off link stitches the opener back to the origin visitor:
  //   - Click-id landing  → fresh per-SESSION mint (sessionStorage), raw r. fallback.
  //   - No click id (new) → reuse a durable per-BROWSER code from localStorage
  //     (bc_handoff_v1), re-minted every 60 days; no raw fallback (nothing to
  //     carry) — propagation just starts when the code arrives. A click-id
  //     landing always wins over the stored codeless code for the session, and
  //     never overwrites it (click codes stay session-scoped).
  // A BOT GUARD skips the whole module (no minting, no decoration) for
  // webdriver / crawler UAs — with bcid now on every human's URL we don't want
  // crawlers minting codes or being fed decorated internal links.
  //
  // Everything here is try/catch-safe and must never break the page. The
  // module also exposes a tiny window.BcHandoff API for assets/share.js.
  // ═══════════════════════════════════════════════════════════════════════
  guard('handoff', function BcHandoff() {
    var PARAM = 'bcid';
    var SESSION_KEY = 'bc_handoff';                 // sessionStorage: {code, ts, fbclid} — click-id / session-scoped
    var LOCAL_KEY = 'bc_handoff_v1';                // localStorage: {code, minted_at} — durable per-browser codeless code
    var CODELESS_TTL_MS = 60 * 24 * 60 * 60 * 1000; // re-mint the durable codeless code every 60 days
    var FBC_MAX_AGE = 60 * 60 * 24 * 90;            // ~90 days, Meta _fbc lifetime
    // Crawlers / automated agents: never mint or decorate for these.
    var BOT_RE = /bot|crawl|spider|slurp|bingpreview|facebookexternalhit|pinterest|headless/i;
    // ---- click-id family registry ----------------------------------------
    // Emitted inline by snippets/datalayer-init.liquid from the canonical
    // manifest (bc-tracking/src/shared/click-id-families.json). NOTHING here
    // redeclares a platform's param, cookie or carrier tag — that duplication
    // is what made this module fbclid-only and left gbraid/wbraid uncarried.
    function bcFams() {
      try {
        var reg = window.__bcClickFamilies;
        return (reg && Object.prototype.toString.call(reg.f) === '[object Array]') ? reg.f : [];
      } catch (e) { return []; }
    }
    function bcCarried() {
      var f = bcFams(), out = [];
      for (var i = 0; i < f.length; i++) if (f[i].y && f[i].t) out.push(f[i]);
      return out; // already priority-sorted by the generator
    }
    function bcLegacyAliases() {
      try { return (window.__bcClickFamilies || {}).legacy || {}; } catch (e) { return {}; }
    }
    function bcFamVersion() {
      try { return (window.__bcClickFamilies || {}).v || ''; } catch (e) { return ''; }
    }
    function famByParam(param) {
      var f = bcFams();
      for (var i = 0; i < f.length; i++) if (f[i].p === param) return f[i];
      return null;
    }
    // Resolve a carrier TAG (or a frozen legacy alias) to its family.
    function famByTag(tag) {
      var alias = bcLegacyAliases()[tag];
      if (alias) return famByParam(alias);
      var c = bcCarried();
      for (var i = 0; i < c.length; i++) if (c[i].t === tag) return c[i];
      return null;
    }
    // Parse a bcid value → {family, value} when it is a RAW carrier, else null
    // (a dotless value is a minted code). Splits on the FIRST dot only: click-id
    // values legitimately contain dots.
    function parseCarrier(v) {
      if (!v || typeof v !== 'string') return null;
      var dot = v.indexOf('.');
      if (dot <= 0) return null;
      var fam = famByTag(v.slice(0, dot));
      if (!fam) return null;
      var raw = sanitizeClickId(v.slice(dot + 1));
      if (!raw) return null;
      return { family: fam, value: raw };
    }

    // ---- pure URL helpers (kept string-based + testable; preserve hash and
    //      every other param verbatim rather than round-tripping through the
    //      URL API, which re-encodes existing values) ----------------------
    function splitHash(href) {
      var i = href.indexOf('#');
      return i >= 0 ? [href.slice(0, i), href.slice(i)] : [href, ''];
    }
    function hasParam(href, name) {
      var base = splitHash(href)[0];
      return new RegExp('[?&]' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=').test(base);
    }
    // Replace the param if present, else append it. Preserves order + hash.
    function setParam(href, name, value) {
      var parts = splitHash(href), base = parts[0], hash = parts[1];
      var qi = base.indexOf('?');
      var path = qi >= 0 ? base.slice(0, qi) : base;
      var pairs = qi >= 0 && base.slice(qi + 1) ? base.slice(qi + 1).split('&') : [];
      var out = [], found = false;
      for (var i = 0; i < pairs.length; i++) {
        if (!pairs[i]) continue;
        var eq = pairs[i].indexOf('=');
        var k = eq >= 0 ? pairs[i].slice(0, eq) : pairs[i];
        if (k === name) { out.push(name + '=' + encodeURIComponent(value)); found = true; }
        else out.push(pairs[i]);
      }
      if (!found) out.push(name + '=' + encodeURIComponent(value));
      return path + (out.length ? '?' + out.join('&') : '') + hash;
    }

    // A handoff value is either a minted alphanumeric code (no dot — mint codes
    // are [a-z0-9]{6}) or a typed RAW carrier `<tag>.<value>` whose tag is a
    // live carried family or a frozen legacy alias. Validate before we
    // resolve/propagate so a junk `?bcid=` can't drive a cookie write or
    // pollute outgoing links.
    function isValidCode(v) {
      if (!v) return false;
      if (v.indexOf('.') > 0) return !!parseCarrier(v);
      return /^[a-z0-9]{4,16}$/i.test(v);
    }

    function readSession() {
      try {
        var s = sessionStorage.getItem(SESSION_KEY);
        if (!s) return null;
        var o = JSON.parse(s);
        return (o && o.code) ? o : null;
      } catch (e) { return null; }
    }
    function writeSession(code, raw) {
      // Stash the source click-id VALUE alongside the code so a later page can
      // safely upgrade a <tag>.<value> URL to this durable code ONLY when they
      // match — guards the rare two-ad-clicks-in-one-tab-before-mint-resolves
      // case from swapping in the wrong identity. `fbclid` is written too so a
      // session started by the previous (CDN-cached) build still upgrades.
      try {
        sessionStorage.setItem(SESSION_KEY, JSON.stringify({
          code: code, ts: Date.now(), raw: raw || '', fbclid: raw || ''
        }));
      } catch (e) {}
    }

    // Durable per-browser codeless code (localStorage). Kept separate from the
    // session-scoped click code so a click landing never clobbers it.
    function localStorageOk() {
      try {
        var k = '__bc_ls_probe__';
        localStorage.setItem(k, '1');
        localStorage.removeItem(k);
        return true;
      } catch (e) { return false; }
    }
    function readLocal() {
      try {
        var s = localStorage.getItem(LOCAL_KEY);
        if (!s) return null;
        var o = JSON.parse(s);
        return (o && o.code) ? o : null;
      } catch (e) { return null; }
    }
    function writeLocal(code) {
      try { localStorage.setItem(LOCAL_KEY, JSON.stringify({ code: code, minted_at: Date.now() })); } catch (e) {}
    }

    // Bot guard — webdriver flag or a crawler UA. Skips the whole module.
    function isBot() {
      try {
        if (navigator.webdriver) return true;
        return BOT_RE.test(navigator.userAgent || '');
      } catch (e) { return false; }
    }

    // Codeless always-mint: reuse a durable per-browser code (refreshed every
    // 60 days), else mint a bc_aid-only code. No r. fallback — there's no click
    // id to carry raw, so propagation simply starts once the code arrives.
    // Degrades to a session-scoped mint when localStorage is unavailable.
    function codelessMint() {
      if (localStorageOk()) {
        var stored = readLocal();
        if (stored && stored.minted_at && (Date.now() - stored.minted_at) < CODELESS_TTL_MS) {
          effectiveCode = stored.code;              // reuse — no network
          return;
        }
        fireMint({}, { durable: true });
        return;
      }
      // localStorage blocked (private mode / partitioned): mint once per session.
      if (session && session.code) { effectiveCode = session.code; return; }
      fireMint({}, { durable: false });
    }

    // Reset the sibling modules' once-per-session guards and re-invoke them so
    // a restored id reaches cart.attributes + the CRM snapshot promptly. Both
    // are safe to re-run: the cart sync only POSTs a diff, and the beacon is
    // server-side deduped by hash-of-bag.
    function reSync() {
      try { sessionStorage.removeItem('bc_cart_sync_ts'); } catch (e) {}
      try { syncIdentifiersToCart(currentBcAid(), { force: true }); } catch (e) {}
      // No sentinel to clear any more: the restore above just changed the
      // cookie bag, so the snapshot's content hash no longer matches anything
      // already delivered and fireIdentifierBeacon re-shoots on its own (D17).
      try { fireIdentifierBeacon(); } catch (e) {}
    }

    // Restore an fbclid into the Meta-format _fbc cookie on .beyondcarat.com
    // so fbevents / CAPI / the cart-sync all read it as if fbevents had set
    // it. NEVER overwrite an existing _fbc (the live click on this browser
    // wins). Also mirror the raw id into bc_fbclid — the same cookie the
    // landing capture writes — feeding the Fbclid cart attribute + Click ID
    // Restorer path.
    function applyFbclid(fbclid, originTs) {
      fbclid = sanitizeClickId(fbclid);
      if (!fbclid) return false;
      // Track whether we actually wrote anything: when both cookies are already
      // present (the live click on this browser, or a repeat r. restore in the
      // same tab) nothing changed, so return false and skip the pointless reSync.
      var wrote = false;
      try { if (!readCookie('bc_fbclid')) { writeCookie('bc_fbclid', fbclid); wrote = true; } } catch (e) {}
      try {
        if (!readCookie('_fbc')) {
          // Type-tolerant parse: the backend returns origin_ts as a numeric ms
          // epoch, but defend against an ISO-8601 string too. parseInt('2026-07-03…')
          // would yield 2026 → fb.1.2026000.<fbclid> (~Jan 1970), silently
          // corrupting attribution. Numbers pass straight through; anything else
          // (ISO string, null/undefined) goes via Date.parse.
          var ts = typeof originTs === 'number' ? originTs : Date.parse(originTs);
          if (!ts || isNaN(ts)) ts = Date.now();
          // Promote a seconds-valued epoch to the milliseconds Meta's _fbc expects
          // (leaves a 13-digit ms value or a Date.parse'd ISO result untouched).
          if (ts < 1e12) ts = ts * 1000;
          writeCookie('_fbc', 'fb.1.' + ts + '.' + fbclid, FBC_MAX_AGE);
          wrote = true;
        }
      } catch (e) {}
      return wrote;
    }

    // ---- B. Restore: landing has `bcid` this browser did NOT mint ---------
    // Apply one resolved click id using the family's declared restore action.
    // Meta needs a timestamped `_fbc` reconstruction; everyone else is a plain
    // first-party cookie write to the SAME name the inline landing capture uses,
    // so syncIdentifiersToCart + Stape's Click ID Restorer pick it up. This is
    // the one place per-family behaviour legitimately lives — the manifest
    // SELECTS the action, it does not pretend Meta's special case away.
    // The value that rides the URL for this click bag: highest manifest priority
    // wins, because only one raw id fits.
    function mintTopValue(clickIds) {
      var c = bcCarried();
      for (var i = 0; i < c.length; i++) if (clickIds[c[i].p]) return clickIds[c[i].p];
      return '';
    }

    function applyRestore(fam, value, originTs) {
      try {
        if (!fam || !value) return false;
        if (fam.r === 'fbc') return applyFbclid(value, originTs);
        if (fam.r === 'cookie') {
          // NEVER overwrite: a live click on THIS browser outranks a restored one.
          if (readCookie(fam.c)) return false;
          writeCookie(fam.c, value);
          return true;
        }
        return false;
      } catch (e) { return false; }
    }

    // ---- Traffic-source label repair (HANDOFF-2026-08-06, shipped 2026-08-13)
    // The in-app→Safari handoff replanted click IDs but never the `_bc_src`
    // label, so orders carried a valid Facebook click ID AND `Traffic Source:
    // direct / none` (order #1140; 8/57 orders in 30d labelled direct, 5
    // wrongly). The repair lives HERE, not in the inline classifier — `bcid`
    // rides every URL of every visit, so its presence proves nothing, and the
    // classifier is synchronous and cannot await the resolve.
    // Mirrors theme.liquid's paid vocabulary exactly.
    // Only CARRIED families appear — a capture-only param (rdt_cid, epik,
    // twclid, li_fat_id) can never reach the raw carrier or the resolve bag,
    // so an entry for it would be dead code. A Reddit/Pinterest origin still
    // gets its label through origin_src (the origin's own classifier verdict).
    var FAMILY_SRC_LABELS = {
      gclid: 'google / cpc', gbraid: 'google / cpc', wbraid: 'google / cpc',
      fbclid: 'facebook / cpc', ttclid: 'tiktok / cpc', msclkid: 'bing / cpc'
    };
    function validSrcLabel(v) {
      return (typeof v === 'string' && v.length <= 64 &&
              v.indexOf(' / ') > 0 && /^[a-z0-9][a-z0-9._\- \/]*$/i.test(v)) ? v : '';
    }
    // Trap 4: fireMint seeds the bag from EVERY family's cookie, so a stale
    // week-old bc_gclid next to today's fbclid would let priority order label
    // a Facebook handoff `google / cpc`. Reconstruct from the family ONLY when
    // the resolved bag names exactly one ad platform.
    function singlePlatformLabel(bag) {
      var found = '';
      for (var p in FAMILY_SRC_LABELS) {
        if (Object.prototype.hasOwnProperty.call(FAMILY_SRC_LABELS, p) && bag && bag[p]) {
          var l = FAMILY_SRC_LABELS[p];
          if (found && found !== l) return '';
          found = l;
        }
      }
      return found;
    }
    function repairSrcLabel(label, originTs, carrierEvidence) {
      try {
        label = validSrcLabel(label);
        if (!label) return false;
        // Shared-link recency: codes live 90 days, and a stranger opening a
        // weeks-old shared link must not inherit the sharer's paid label into
        // reporting. A genuine in-app→Safari handoff resolves within seconds
        // to hours — 24h window. FAIL-CLOSED: a resolve without a usable
        // origin_ts repairs nothing (round-2 review — the old null-skips-gate
        // shape would silently drop the bound if a future worker change ever
        // omitted the field). Only carrierEvidence exempts the window: that
        // click id demonstrably rode THIS URL.
        if (!carrierEvidence) {
          var age = Date.now() - Number(originTs);
          if (isNaN(age) || !(age > -3600000 && age < 86400000)) return false;
        }
        var existing = readCookie('_bc_src');
        // Upgrade-only: never trample a real label (a Klaviyo email,
        // google/organic) — a restore is not a live click, so it gets LESS
        // authority than the classifier's own paidBreaksLock. The ONLY
        // same-source widening sanctioned (brief, judgement call 2) is
        // social / in-app-browser → cpc; round-2 review reproduced the wider
        // arm overwriting `google / organic` with `google / cpc` — paid
        // credit booked for an organic visit by a restore.
        var upgradeable = !existing || existing === 'direct / none' ||
          (existing.split(' / ')[0] === label.split(' / ')[0] &&
           label.split(' / ')[1] === 'cpc' &&
           ['social', 'in-app-browser'].indexOf(existing.split(' / ')[1]) !== -1);
        if (!upgradeable || existing === label) return false;
        // Trap 1: mirror theme.liquid's EXACT cookie shape — host-only,
        // path=/, 30-day `expires=`. Routing this through writeCookie() would
        // attach domain=.beyondcarat.com at 730 days: a SECOND `_bc_src` both
        // readers never see (the older host-only cookie sorts first), and a
        // stale shadow label that surfaces months later.
        var exp = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toUTCString();
        document.cookie = '_bc_src=' + encodeURIComponent(label) + ';expires=' + exp + ';path=/';
        return true;
      } catch (e) { return false; }
    }

    function restore(bcid) {
      try {
        // Once-per-code-per-tab guard: a genuine opener browses several pages
        // all carrying the SAME foreign bcid, but the resolve/stitch + reSync
        // only needs to run for the first page that presents a given code. Set
        // the sentinel eagerly (a rare transient resolve failure forgoes one
        // tab's stitch — the load-time beacon still carries identity — vs. a
        // resolve + reSync on every page otherwise). A new/different code runs
        // again. Our own codes never reach here (see mintedByUs).
        var seen = null;
        try { seen = sessionStorage.getItem('bc_handoff_resolved'); } catch (e) {}
        if (seen === bcid) return;
        try { sessionStorage.setItem('bc_handoff_resolved', bcid); } catch (e) {}

        // A typed raw carrier (`fb.` `gc.` `gb.` … or the frozen legacy `r.`)
        // needs no resolve call — the click id is right there in the URL.
        var carried = parseCarrier(bcid);
        if (carried) {
          var wroteClickId = applyRestore(carried.family, carried.value, null);
          // Trap 3: the label repair keys on the EVIDENCE (this click id
          // demonstrably rode this URL), never on the cookie-write result —
          // applyRestore returns false when _fbc (90d) already exists while
          // _bc_src (30d) may have long lapsed back to direct/none.
          var repairedLabel = repairSrcLabel(FAMILY_SRC_LABELS[carried.family.p] || '', null, true);
          if (wroteClickId || repairedLabel) reSync();
          return;
        }
        var aid = currentBcAid();
        var url = ENDPOINTS.handoff + '/resolve?code=' + encodeURIComponent(bcid);
        if (aid) url += '&bc_aid=' + encodeURIComponent(aid);
        else {
          // First page of a reopened link: the bc_aid cookie is issued by the
          // door's OWN response, so it cannot exist yet — which is why 92% of
          // reopens stitched nothing (measured 2026-08-13: the resolve write
          // needs to know WHO resolved, and this call carried no identity;
          // order #1159's opener lost that race by 212ms). Offer the bootstrap
          // candidate exactly like fireMint does — the door promotes it via
          // the same cookie-beats-candidate contract, pins it via Set-Cookie,
          // and echoes it back for adoption below.
          var stitchCand = getBootstrapCandidate();
          if (stitchCand) url += '&bc_aid_candidate=' + encodeURIComponent(stitchCand);
        }
        fetch(url, { method: 'GET', credentials: 'include' })
          .then(function (r) { return (r && r.ok) ? r.json() : null; })
          .then(function (j) {
            if (!j) return; // 404 / malformed — nothing to restore or stitch
            // The door may have just minted our identity from the candidate —
            // adopt it so every later beacon/mint/cart-sync speaks the same id.
            adoptCanonicalAid(j);
            // Current workers return a `click_ids` map; older ones return the
            // flat fields. Read the map when present, else fall back to the
            // response object itself — the door is deployed independently of
            // this CDN-cached asset, in BOTH directions.
            var bag = (j.click_ids && typeof j.click_ids === 'object') ? j.click_ids : j;
            var fams = bcCarried();
            for (var fi = 0; fi < fams.length; fi++) {
              var val = sanitizeClickId(bag[fams[fi].p]);
              if (val) applyRestore(fams[fi], val, j.origin_ts);
            }
            // Traffic-source repair: prefer the ORIGIN browser's own computed
            // label (utm precedence intact — landing_url can't supply it, the
            // theme strips utm_* before the mint reads the URL); else fall
            // back to family reconstruction only for a single-platform bag.
            repairSrcLabel(validSrcLabel(j.origin_src) || singlePlatformLabel(bag), j.origin_ts);
            // A successful 200 IS the stitch — the GET carried our bc_aid, so the
            // backend has linked this opener to the origin visitor. Fire the
            // beacon/cart-sync so the opener's identity snapshot reaches the CRM
            // promptly, even for a stitch-only resolve that wrote no cookies.
            // (cartAttributePayload now carries the label attributes, so this
            // same reSync IS the immediate label re-post — Trap 2.)
            reSync();
          })
          .catch(function () {
            // Transient network failure: clear the eager sentinel so the NEXT
            // pageview retries the resolve — it was permanently forgone
            // per-tab before, at the cost of one lost stitch per blip.
            try { sessionStorage.removeItem('bc_handoff_resolved'); } catch (e) {}
          });
      } catch (e) {}
    }

    // ---- A. Mint: fire-and-forget the identity, cache the code ------------
    // opts.durable → store the returned code in localStorage (codeless, per-
    // browser); otherwise sessionStorage (click-id, per-session).
    //
    // Failure visibility (2026-08-22): the mint's `.catch` used to swallow
    // every failure, which is how a ~93% mint loss on iPhone Facebook in-app
    // collection-page landings stayed invisible. A failed mint now emits ONE
    // best-effort `handoff_mint_failed` observation through the outbox (so it
    // inherits consent gating) and sets the `mint_failed` telemetry flag (so
    // the fact rides every later envelope even if the door drops the event).
    // Capped per page so a broken door cannot turn into a flood.
    var MINT_FAIL_MAX_PER_PAGE = 2;
    var mintFailCount = 0;
    // Coarse page TYPE, never the raw path: a pathname can carry a person
    // (account/reset tokens, search terms, shared-cart ids) and this event is
    // persisted in sessionStorage and shipped to the ledger.
    function mintFailPageType() {
      var p = '';
      try { p = String(location.pathname || ''); } catch (e) { p = ''; }
      if (p === '' || p === '/') return 'home';
      if (p.indexOf('/collections') === 0) return 'collection';
      if (p.indexOf('/products') === 0) return 'product';
      if (p.indexOf('/cart') === 0) return 'cart';
      if (p.indexOf('/account') === 0 || p.indexOf('/challenge') === 0 ||
          p.indexOf('/activate') === 0 || p.indexOf('/reset') === 0) return 'account';
      return 'other';
    }
    function reportMintFailed(status, err, durable) {
      try {
        setTelemetryFlag('mint_failed');
        if (mintFailCount >= MINT_FAIL_MAX_PER_PAGE) return;
        mintFailCount++;
        var st = (typeof status === 'number' && isFinite(status)) ? status : 0;
        var pageType = mintFailPageType();
        BcOutbox.enqueue({
          kind: 'handoff_mint_failed',
          fields: {
            status: st,
            error: scrubTelemetryMessage(err),   // already capped at 120
            durable: !!durable,
            page: pageType
          },
          ts: Date.now(),
          // One entry per (page type, status) per SESSION: a mint that keeps
          // failing across MPA navigations used to add a fresh entry (10 tries
          // each) per page, evicting older identity entries from the queue.
          ctx: { dedup: DEDUP_MINT_FAILED + pageType + '|' + st }
        });
      } catch (e) {}
    }

    function fireMint(clickIds, opts) {
      opts = opts || {};
      try {
        var aid = currentBcAid();
        // `click_ids` map + `fam_v` (the manifest fingerprint). fam_v lets the
        // door COUNT — never reject — theme/worker registry skew, which is the
        // only drift signal that works on DEPLOYED artifacts: this asset is
        // CDN- and browser-cached long after a deploy, so a git hook cannot see it.
        var body = { v: 1, click_ids: {}, fam_v: bcFamVersion() };
        var mintFams = bcCarried();
        // Take the click id from the URL, and FALL BACK to the first-party
        // cookie the landing capture wrote. Without the fallback, a code minted
        // once the URL has lost its click id carries nothing to restore, and the
        // in-app-browser handoff — the entire reason this exists — silently
        // fails for anyone who acts before handing off:
        //
        //   ad click → in-app browser → ADD TO CART (a form submit, which does
        //   not carry query params) → /cart mints a FRESH codeless code with no
        //   click id → "Open in Safari" → resolve returns nothing → no _fbc in
        //   Safari → the purchase is never matched to the ad.
        //
        // The cookie is the browser's durable memory of the click, so seeding
        // from it makes EVERY code restorable regardless of when it was minted.
        // Cookie names come from the click-id registry, so a new platform is
        // covered automatically.
        for (var mi = 0; mi < mintFams.length; mi++) {
          var mv = clickIds[mintFams[mi].p] || sanitizeClickId(readCookie(mintFams[mi].c));
          if (mv) body.click_ids[mintFams[mi].p] = mv;
        }
        var fbp = readCookie('_fbp'); if (fbp) body.click_ids.fbp = fbp;
        if (aid) body.bc_aid = aid;
        else body.bc_aid_candidate = getBootstrapCandidate();
        // The classifier's own Traffic Source verdict rides the mint so a
        // future opener can inherit it via /resolve (part 3 of
        // HANDOFF-2026-08-06). landing_url can NOT supply this server-side:
        // utm_* is stripped from the address bar before this function reads
        // location.href, and mid-session mints send /cart.
        var srcVerdict = readCookie('_bc_src');
        if (srcVerdict) body.origin_src = srcVerdict;
        body.landing_url = (window.__bcLandingUrl ? window.__bcLandingUrl() : location.href);
        body.ts = Date.now();
        // NO `keepalive` here (2026-08-22). WebKit gives each page a 64 KB
        // budget shared by every keepalive fetch and sendBeacon in flight, and
        // on this site that budget is already overflowed at load (Shopify's
        // own monorail beacons are rejected in the console on every page, and
        // collection pages add a ~20 KB view_item_list beacon on top). When the
        // budget is full WebKit rejects a keepalive fetch synchronously, the
        // `.catch` below swallowed it, and the mint silently never happened:
        // ~93% of iPhone Facebook in-app landings on COLLECTION pages lost
        // their mint (product pages, with no list beacon, were 99.5% fine).
        // The mint fires at page load and NEEDS the response to store the
        // code, so keepalive bought nothing — a plain fetch sidesteps the
        // quota entirely.
        // Never mint off the storefront origin (theme preview, password page,
        // sandboxed frame): the door's allowlist is exactly the two storefront
        // origins and answers 403 origin_not_allowed — the only 403 this path
        // ever produced (5/125 handoff_mint_failed, 2026-08-28).
        if (BC_MINT_ORIGINS.indexOf(location.origin) === -1) { mintDone(opts, null); return; }
        // PREFLIGHT-FREE (2026-08-28): 119/125 mint failures were status 0
        // ("Load failed" on WebKit, "Failed to fetch" on Chrome) — the request
        // died at the network layer, never reaching the door. With
        // `application/json` the browser must send an OPTIONS preflight first,
        // and WebKit caps its preflight cache at 600s, so nearly every in-app
        // landing paid two round trips that a quick bounce cancels. The door
        // parses the body as text and IGNORES the content-type (pinned by
        // tests/handoff-codeless-mint.test.ts in bc-tracking), so posting the
        // same JSON as `text/plain` is CORS-simple: one round trip, no OPTIONS.
        // One IMMEDIATE retry, only on a network-level failure (rejected
        // promise) — an HTTP status is a decision from the door and is never
        // retried. No backoff timer: the mint fires at page load and must
        // finish before the shopper leaves, and a timer here would also stall
        // the failure report in test harnesses that stub setTimeout.
        var mintInit = {
          method: 'POST',
          headers: { 'content-type': 'text/plain;charset=UTF-8' },
          body: JSON.stringify(body),
          credentials: 'include'
        };
        var mintAttempt = function () { return fetch(ENDPOINTS.handoff + '/mint', mintInit); };
        mintAttempt()
          .catch(function () { return mintAttempt(); })
          .then(function (r) {
            if (r && r.ok) return r.json();
            reportMintFailed(r ? r.status : 0, 'mint http ' + (r ? r.status : 0), opts.durable);
            return null;
          })
          .then(function (j) {
            if (j) adoptCanonicalAid(j);
            if (!j || !j.code || !isValidCode(String(j.code))) { mintDone(opts, null); return; }
            var code = String(j.code);
            if (opts.durable) writeLocal(code);          // codeless durable code
            // Pair the code with the value that actually rode the URL — the
            // highest-priority carried click id, not fbclid specifically.
            else writeSession(code, mintTopValue(clickIds)); // click-id session code
            effectiveCode = code;      // raw fallback / pending → durable code
            applyToCurrentUrl(code);   // stamp bcid=<code> onto the address bar
            mintDone(opts, code);
          })
          .catch(function (e) {
            // mint failed — raw fallback keeps carrying; but say so this time.
            reportMintFailed(0, e, opts.durable);
            mintDone(opts, null);
          });
      } catch (e) { mintDone(opts, null); }
    }

    // `opts.done(code|null)` — a caller that needs the code the moment it exists
    // (claimOwn below). Fires exactly once per mint; a throwing callback can
    // never break the mint.
    function mintDone(opts, code) {
      try { if (opts && typeof opts.done === 'function') { var cb = opts.done; opts.done = null; cb(code); } } catch (e) {}
    }

    // The code we currently stamp onto the address bar + outgoing links. Held
    // in closure so the async mint can upgrade it live (raw fallback → code).
    var effectiveCode = null;

    function applyToCurrentUrl(code) {
      try {
        if (!code) return;
        var next = setParam(location.href, PARAM, code);
        if (next === location.href) return;
        history.replaceState(history.state, '', next);
      } catch (e) {}
    }

    // Propagate across internal navigation: append bcid to same-host anchors
    // on click (capture phase, before navigation) when absent. Preserves the
    // href's original relative/absolute form + hash.
    function onDocClick(e) {
      try {
        if (!effectiveCode) return;
        var t = e && e.target;
        var a = t && t.closest ? t.closest('a[href]') : null;
        if (!a) return;
        var abs;
        try { abs = new URL(a.href, location.href); } catch (u) { return; }
        if (abs.protocol !== 'http:' && abs.protocol !== 'https:') return; // skip mailto/tel/js
        if (abs.hostname !== location.hostname) return;                    // same-host only
        var href = a.getAttribute('href');
        // Bail on pure-fragment hrefs (#MainContent skip-to-content link, blog
        // TOC anchors read by article-toc.js via getAttribute('href').substring(1),
        // other in-page jumps): setParam would turn "#x" into "?bcid=..#x", forcing
        // a cross-document reload that also drops the page's real query params — for
        // exactly the ad-click cohort. Links that merely carry a fragment
        // (/products/x#section) still decorate.
        if (!href || href.charAt(0) === '#' || hasParam(href, PARAM)) return;
        var next = setParam(href, PARAM, effectiveCode);
        if (next !== href) a.setAttribute('href', next);
      } catch (er) {}
    }

    // Keep bcid on the URL across programmatic address-bar rewrites. Dawn's
    // facets.js updateURLHash rebuilds the URL as `pathname?<facet params>` on
    // every collection filter/sort via history.replaceState — dropping bcid, so
    // an Open-in-Safari / copy-URL mid-browse would hand off a codeless URL,
    // exactly the break this feature prevents. We wrap replaceState/pushState
    // once (idempotent) to re-append the current code, and re-stamp on popstate.
    // Inert until a code exists; reads effectiveCode live so it engages the
    // moment an async mint resolves. We do NOT edit facets.js (uncommitted
    // filter-drawer work touches faceting and we don't own that file).
    function installHistoryHook() {
      try {
        if (history.__bcHandoffWrapped) return;   // idempotent — never double-wrap
        history.__bcHandoffWrapped = true;
        var wrap = function (orig) {
          return function (state, title, url) {
            try {
              if (effectiveCode && url != null) {
                var abs = null;
                try { abs = new URL(url, location.href); } catch (e) { abs = null; }
                // Same-origin only, and only when bcid is absent — then rewrite
                // the URL being written; otherwise fall through untouched so we
                // preserve the caller's exact arguments + .call semantics.
                if (abs && abs.origin === location.origin && !hasParam(String(url), PARAM)) {
                  return orig.call(this, state, title, setParam(String(url), PARAM, effectiveCode));
                }
              }
            } catch (e) {}
            return orig.apply(this, arguments);
          };
        };
        history.replaceState = wrap(history.replaceState);
        history.pushState = wrap(history.pushState);
      } catch (e) {}
    }

    // ---- main flow --------------------------------------------------------
    // Bot guard first: skip ALL minting + decoration for crawlers / webdriver.
    // (Returns from THIS IIFE only — the rest of bc-tracking-async.js is
    // unaffected. window.BcHandoff stays undefined, so share.js won't decorate.)
    if (isBot()) return;

    var params;
    try { params = new URL(location.href).searchParams; } catch (e) { return; }

    var rawBcid = params.get(PARAM) || '';
    var existingBcid = isValidCode(rawBcid) ? rawBcid : '';
    var session = readSession();
    var durable = readLocal();
    // "Mine" = a code THIS browser minted: the session-scoped click code OR the
    // durable per-browser codeless code. Skip restore for it. Without the
    // localStorage arm, a codeless visitor's own code — stamped into the URL on
    // page 1 — would trigger restore→resolve (a DB UPDATE) + reSync on EVERY
    // subsequent pageview for the entire non-click audience. Self-stitch is
    // prevented server-side anyway, so that would be pure waste.
    // Every carried family, in manifest priority order. Adding a platform is a
    // manifest line — this list is never hand-edited again.
    var carriedFams = bcCarried();
    var clickIds = {};
    var hasAnyClick = false;
    var topCarrier = null;                 // highest-priority click id present
    for (var ci = 0; ci < carriedFams.length; ci++) {
      var cv = sanitizeClickId(params.get(carriedFams[ci].p));
      if (!cv) continue;
      clickIds[carriedFams[ci].p] = cv;
      hasAnyClick = true;
      if (!topCarrier) topCarrier = { family: carriedFams[ci], value: cv };
    }

    // The inline core (snippets/datalayer-init.liquid) stamps `<tag>.<value>`
    // during head parse so an "Open in Safari" / share in the first moments of a
    // PDP landing still carries identity. That stamp is OURS, even though it
    // predates this module and so appears in neither storage arm. Recognise it
    // by matching the carrier against a click id present on THIS landing —
    // which also covers the frozen legacy `r.` form on an old link.
    var parsedExisting = existingBcid ? parseCarrier(existingBcid) : null;
    var isOwnInlineStamp = !!parsedExisting &&
      clickIds[parsedExisting.family.p] === parsedExisting.value;

    var mintedByUs = !!existingBcid && (
      (session && session.code === existingBcid) ||
      (durable && durable.code === existingBcid) ||
      isOwnInlineStamp
    );

    // B. Restore only when the URL carries a bcid this tab did NOT mint.
    if (existingBcid && !mintedByUs) restore(existingBcid);

    // Decide what we propagate onward this page load.
    if (existingBcid) {
      // Raw→durable upgrade: if the URL still carries the long r.<fbclid>
      // fallback but this session already minted a durable code for that SAME
      // fbclid (its mint resolved on an earlier page), swap to the short code
      // in the address bar + all onward propagation instead of carrying the
      // raw value forever. applyToCurrentUrl below rewrites bcid=r.<fbclid>→code.
      if (parsedExisting && session && session.code &&
          (session.raw || session.fbclid) === parsedExisting.value) {
        effectiveCode = session.code;
      } else {
        effectiveCode = existingBcid;               // already carrying a code — keep it
        // The inline core can stamp the raw fallback but CANNOT mint (that needs
        // a server round-trip it must not wait for). Without this arm the whole
        // ad-click cohort would keep the long r.<fbclid> in the address bar
        // forever and never reach click_handoffs — the `existingBcid` branch
        // above would swallow the landing before the mint arm below is reached.
        if (isOwnInlineStamp && !(session && session.code)) {
          fireMint(clickIds, { durable: false });
        }
      }
    } else if (hasAnyClick) {
      // Fresh click, no bcid yet → fresh per-session mint. Until the code lands,
      // propagate the raw fbclid (r.<fbclid>) when we have one so a same-tab nav
      // or an "Open in Safari" before the mint returns still carries identity.
      // A click landing wins over any stored codeless code and never overwrites
      // it — this code is session-scoped.
      effectiveCode = topCarrier
        ? topCarrier.family.t + '.' + topCarrier.value
        : ((session && session.code) || null);
      fireMint(clickIds, { durable: false });
    } else if (session && session.code) {
      effectiveCode = session.code;                 // later same-tab page (MPA re-append)
    } else {
      // No click id and no bcid → codeless always-mint (durable per-browser code,
      // refreshed every 60 days) so this visit's shares also stitch back to us.
      codelessMint();
    }

    // Dawn is MPA: every navigation is a full load that re-runs this script
    // and re-appends via replaceState below — no soft-nav hook needed.
    if (effectiveCode) applyToCurrentUrl(effectiveCode);
    document.addEventListener('click', onDocClick, true);
    installHistoryHook();
    // Back/forward can land on a facet history entry that lacks bcid — re-stamp.
    window.addEventListener('popstate', function () {
      try { if (effectiveCode) applyToCurrentUrl(effectiveCode); } catch (e) {}
    });

    // Minimal API for assets/share.js (build-share-URL time).
    window.BcHandoff = {
      getCode: function () { return effectiveCode; },
      /**
       * Make this browser carry a code IT OWNS, and hand it to `cb`.
       *
       * A browser that arrived on someone else's link carries THEIR code for the
       * whole visit (existingBcid above) and never mints one of its own. That is
       * right for attribution and wrong the moment this shopper edits the cart
       * and shares: the door only lets a code's owner overwrite its snapshot,
       * so the recipient's share is refused and the link keeps serving the
       * original cart. Caller (assets/cart-share.js) uses this before any
       * attach from a browser that did not mint the current code. The switch is
       * durable for the browser: address bar and onward links carry the new
       * code from here on, so the same shopper's later edits keep their link
       * fresh. `cb(null)` when a mint is needed and fails — caller falls back.
       */
      claimOwn: function (cb) {
        var done = function (c) { try { cb(c); } catch (e) {} };
        try {
          var own = (session && session.code) || null;
          if (!own) {
            var d = readLocal();
            if (d && d.minted_at && (Date.now() - d.minted_at) < CODELESS_TTL_MS) own = d.code;
          }
          if (effectiveCode && (effectiveCode === own || isOwnInlineStamp)) { done(effectiveCode); return; }
          if (own) { effectiveCode = own; applyToCurrentUrl(own); done(own); return; }
          fireMint({}, { durable: localStorageOk(), done: done });
        } catch (e) { done(null); }
      },
      decorate: function (url) {
        try { return effectiveCode ? setParam(url, PARAM, effectiveCode) : url; }
        catch (e) { return url; }
      },
      // Fire an early mint when we have no code yet but SOME carried click
      // cookie exists — so a share tapped moments later usually has a code
      // ready. Walks every carried family in priority order (was Meta-only).
      ensureCode: function () {
        try {
          if (effectiveCode || (session && session.code)) return;
          var fams = bcCarried(), bag = {}, top = null;
          for (var i = 0; i < fams.length; i++) {
            var cv = sanitizeClickId(readCookie(fams[i].c));
            if (!cv) continue;
            bag[fams[i].p] = cv;
            if (!top) top = { family: fams[i], value: cv };
          }
          if (top) {
            effectiveCode = top.family.t + '.' + top.value; // immediate raw fallback for shares
            fireMint(bag, { durable: false });
          }
        } catch (e) {}
      }
    };
  })();

  // ─── Scroll-depth tiers (25 / 50 / 75 / 100) ──────────────────────────
  // Fire each tier at most once per page load, on ACTUAL scroll only. Short / unscrollable
  // pages never fire (matching the legacy semantics — we don't back-fire
  // satisfied tiers at load). Passive listener + rAF throttle so it never
  // competes with rendering. Pushes { event: 'scroll_depth', depth: N } —
  // field named `depth` because the sGTM BCRM forwarder template relays that key.
  // — Web GTM trigger 61 (.*) routes it to GA4 / sGTM automatically, so no
  // GTM tags/triggers are added here. Only reads scroll position + pushes
  // its own event, so it can't fire on gtm.* or touch other listeners.
  guard('scroll_depth', function setupScrollDepth() {
    var TIERS = [25, 50, 75, 100];
    var fired = {};
    var ticking = false;

    function check() {
      ticking = false;
      var doc = document.documentElement;
      var scrollTop = window.pageYOffset || doc.scrollTop || 0;
      var scrollable = (doc.scrollHeight || 0) - (window.innerHeight || doc.clientHeight || 0);
      if (scrollable <= 0) return; // unscrollable / short page — guard div-by-zero
      var pct = Math.min(100, Math.round((scrollTop / scrollable) * 100));
      for (var i = 0; i < TIERS.length; i++) {
        var t = TIERS[i];
        if (pct >= t && !fired[t]) {
          fired[t] = true;
          var aid = currentBcAid();
          (window.dataLayer = window.dataLayer || []).push(addRawCaptureFields({
            event: 'scroll_depth',
            depth: t,
            bc_aid: aid || null,
            bc_aid_candidate: aid ? null : (getBootstrapCandidate() || null)
          }));
        }
      }
    }

    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(check);
    }, { passive: true });
  })();

  // Note: bc_identify_enabled theme setting removed 2026-05-23 — backend +
  // sGTM + Worker pipeline confirmed live, module now always emits. The
  // setting was registering as `true` in settings_data.json yet evaluating
  // falsey in Liquid (Shopify-side caching anomaly), blocking every real
  // signup from reaching BCRM. Unconditional emit removes that risk.
  // ==========================================================================
  // BcIdentify — Universal Identity Bridge (Phase 4 of identity-bridge-plan)
  //
  // Four concurrent watchers, single emit() helper, shared dedup set. Each
  // watcher detects a person via a different signal:
  //   A) __kla_id cookie poll       → klaviyo_exchange_id (backend resolves)
  //   B) Klaviyo SDK identify hook  → email + (optional) klaviyo_id
  //   C) Shopify customer (Liquid)  → email + phone + name (logged-in only)
  //   D) Crisp chat email capture   → email + (stashed) phone
  //
  // Gate: emit() only fires when at least ONE of email / klaviyo_id /
  // klaviyo_exchange_id is present. Source-agnostic dedup means a person
  // detected by multiple watchers produces exactly one bc_identify per
  // identity tuple per session. The event payload is the canonical Web GTM
  // contract — DLV variables in GTM read these exact field names.
  // ==========================================================================
  guard('identify', function BcIdentify() {
    // ---- correctEmailTypos ----------------------------------------------
    // Catch the most common consumer-provider domain typos before email
    // reaches the identity bridge. Klaviyo accepts any well-formed email
    // address, so these typos slip through, profile-import returns 200,
    // and every subsequent send to the profile hard-bounces forever — a
    // quiet quality leak invisible in our v44 monitor data.
    //
    // Scope is deliberately narrow: only canonical domain corrections for
    // the top consumer providers. Does NOT attempt local-part correction
    // (too risky — "john.smith" vs "jonh.smith" may both be valid). Does
    // not handle business domains. Lookup is exact-match on the substring
    // after the final "@".
    //
    // Source list curated from Klaviyo's published list of top-bouncing
    // domain typos + Shopify Plus typo-correction merchant data. Update
    // pattern below by adding (typo, canonical) pairs only.
    var EMAIL_TYPO_MAP = {
      // gmail
      'gmial.com': 'gmail.com',
      'gmaill.com': 'gmail.com',
      'gnail.com': 'gmail.com',
      'gamil.com': 'gmail.com',
      'gmai.com': 'gmail.com',
      'gmal.com': 'gmail.com',
      'gmsil.com': 'gmail.com',
      'gmail.cm': 'gmail.com',
      'gmail.co': 'gmail.com',
      'gmail.con': 'gmail.com',
      'gmail.cmo': 'gmail.com',
      'gmail.om': 'gmail.com',
      'gmail.comm': 'gmail.com',
      // yahoo
      'yaho.com': 'yahoo.com',
      'yahooo.com': 'yahoo.com',
      'yahoo.cm': 'yahoo.com',
      'yahoo.co': 'yahoo.com',
      'yahoo.con': 'yahoo.com',
      // hotmail
      'hotnail.com': 'hotmail.com',
      'hotmal.com': 'hotmail.com',
      'hotmail.cm': 'hotmail.com',
      'hotmail.co': 'hotmail.com',
      'hotmail.con': 'hotmail.com',
      // outlook
      'outlok.com': 'outlook.com',
      'oulook.com': 'outlook.com',
      'outloo.com': 'outlook.com',
      'outlook.cm': 'outlook.com',
      'outlook.co': 'outlook.com',
      'outlook.con': 'outlook.com',
      // icloud
      'icoud.com': 'icloud.com',
      'iclod.com': 'icloud.com',
      'icloud.cm': 'icloud.com',
      'icloud.co': 'icloud.com',
      'icloud.con': 'icloud.com',
      // aol
      'ao.com': 'aol.com',
      'aoll.com': 'aol.com',
      'aol.cm': 'aol.com',
      'aol.co': 'aol.com',
      'aol.con': 'aol.com',
      // live / msn
      'liv.com': 'live.com',
      'liev.com': 'live.com',
      'msn.cm': 'msn.com',
      'msn.con': 'msn.com',
      // protonmail
      'protonmial.com': 'protonmail.com',
      'protonmail.cm': 'protonmail.com',
      'protonmail.con': 'protonmail.com',
      // comcast
      'comacst.net': 'comcast.net',
      'comcat.net': 'comcast.net',
      'comcast.ne': 'comcast.net',
      'comcast.nt': 'comcast.net'
    };

    function correctEmailTypos(email) {
      var atIdx = email.lastIndexOf('@');
      if (atIdx < 1) return email;
      var local = email.substring(0, atIdx);
      var domain = email.substring(atIdx + 1);
      var canonical = EMAIL_TYPO_MAP[domain];
      return canonical ? (local + '@' + canonical) : email;
    }

    // ---- parseKlaExchangeId ---------------------------------------------
    // __kla_id is base64(JSON), not URL-encoded. Only documented field is
    // $exchange_id — an encrypted opaque token Klaviyo maps to a profile
    // server-side. The undocumented $cuid field from round-1 notes is NOT
    // a stable identifier and is intentionally ignored. The backend
    // /api/identify endpoint resolves exchange_id → profile_id via the
    // Klaviyo Profiles API (filter=equals(_kx,"<exchange_id>")).
    function parseKlaExchangeId(rawCookie) {
      if (!rawCookie) return null;
      try {
        // Some browsers double-URL-encode on read; strip defensively. The
        // surrounding quote strip handles cookie values written with
        // explicit JSON-string wrapping.
        var decoded = atob(decodeURIComponent(rawCookie).replace(/^"|"$/g, ''));
        var json = JSON.parse(decoded);
        return json.$exchange_id || null;
      } catch (e) { return null; }
    }

    // ---- Shared accumulated identity state across all watchers -----------
    // sGTM Tag #194 does a full-PUT on identity_<bc_aid>, so each emit must
    // contain the FULL identity known so far — otherwise a later thinner
    // emit (e.g., Watcher A's exchange-id-only) overwrites a richer earlier
    // doc (e.g., Watcher B's email-bearing). We accumulate every non-empty
    // field across watchers and always push the full union; dedup skips when
    // the accumulated state hasn't changed since the last emit.
    var accumulated = {};
    // The dedup key is the resolved (email|klaviyo_id|phone|...) signature —
    // we only re-emit bc_identify_emit when it actually changes. Without it a
    // visitor who browsed 10 pages produced 10 bc_identify_emit pushes for one
    // unchanged identity, and BCRM (before the 2026-05-22 fix) fractured the
    // dossier timeline into one false session per fire.
    //
    // The marker itself now lives in the outbox, in the one scheme the whole
    // file shares. Its session scope is still the right one: identity should
    // re-emit on a fresh tab/session (new device, a visit a day later,
    // incognito) but NOT on every navigation inside the same tab. Storage
    // blocked (private mode, third-party context) degrades to in-memory.
    // Track what we've already pushed into Crisp session:data so the post-emit
    // sync only fires deltas. Keyed by the same field names we set in Crisp;
    // bc_aid is set once at install time (window.__bcAid is stable) so it's
    // not tracked here. Email/phone flow Crisp→storefront, not the other way.
    var lastCrispPushed = { klaviyo_id: null, klaviyo_exchange_id: null };
    // Lock the source label to the first watcher that contributed a primary
    // identifier (email or klaviyo_id). Without this, a later thinner emit
    // (e.g., Watcher A's kla_cookie adding only exchange_id) would overwrite
    // the source field in Stape to 'kla_cookie' even though the email was
    // contributed by Watcher B's kla_identify. The lock keeps the source
    // label accurate to the watcher that established the canonical identity.
    var establishedSource = null;

    function emit(source, fields) {
      // A RESUME trigger for the pollers (perf item 25): an identify is the
      // single strongest signal that this visitor is mid-flow, and the watchers
      // it re-arms — the __kla_id cookie poll, the chokepoint installer, the
      // Crisp bridge — are exactly the ones that pay off in the next few
      // seconds. Parked polls come back on the 2 s burst cadence.
      try { rearmPolls(); } catch (e) {}
      // bc_aid guard. Cookie is server-owned. If no canonical cookie is
      // readable, emit bc_aid:null with the single guarded bootstrap candidate
      // so the Worker can canonicalize + echo a durable bc_aid.
      var aid = currentBcAid();
      var candidate = aid ? null : getBootstrapCandidate();
      if (!aid && !candidate) return;

      // Merge non-empty fields into accumulated state (never unset).
      if (fields && typeof fields === 'object') {
        for (var f in fields) {
          if (Object.prototype.hasOwnProperty.call(fields, f) && fields[f]) {
            accumulated[f] = fields[f];
          }
        }
      }

      // Normalize email lowercase once it's in, then run a typo guard for
      // the most common consumer-provider domain typos. We auto-correct
      // silently — risk of a real domain matching one of these typos is
      // ~zero (no major mail provider is named "gmial.com"). When we
      // correct, dispatch a `bc_email_typo_corrected` event so the
      // analytics layer can measure prevalence without surfacing the
      // raw before/after email (the data flows via standard tracking
      // events; here we only emit a marker).
      if (accumulated.email) {
        accumulated.email = String(accumulated.email).toLowerCase().trim();
        var corrected = correctEmailTypos(accumulated.email);
        if (corrected !== accumulated.email) {
          try {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: 'bc_email_typo_corrected',
              bc_aid: aid || null,
              bc_aid_candidate: aid ? null : candidate
              // intentionally no raw email payload — see comment above
            });
          } catch (e) { noteModuleError('identify', e); }
          // D7: carry the address AS TYPED alongside the correction. An order
          // placed later may well repeat the same typo, and the ledger's joins
          // are exact-match — correcting silently would make that order
          // unjoinable to the visitor who typed it.
          accumulated.email_raw = accumulated.email;
          accumulated.email = corrected;
        }
      }

      // Gate: at least one actionable identifier required. exchange_id
      // counts because backend resolves it to a profile.
      if (!accumulated.email && !accumulated.klaviyo_id && !accumulated.klaviyo_exchange_id) return;

      // Lock establishedSource the first time we have a primary identifier
      // (email or klaviyo_id). Subsequent emits will keep this label even
      // if a thinner watcher (exchange_id only) fires later.
      if (!establishedSource && (accumulated.email || accumulated.klaviyo_id)) {
        establishedSource = source;
      }
      var effectiveSource = establishedSource || source;

      // Dedup on the full accumulated signature — if nothing's actually
      // changed since the last emit, skip. Re-emit when ANY field gained
      // new info so Tag #194's PUT carries the latest union to Stape.
      var signature = [
        accumulated.email || '',
        accumulated.klaviyo_id || '',
        accumulated.klaviyo_exchange_id || '',
        accumulated.phone || '',
        accumulated.first_name || '',
        accumulated.last_name || '',
        accumulated.crisp_session_id || ''
      ].join('|');
      // Audit 2026-07-24 (#7): the durable marker is committed only once the
      // door has taken the observation. Persisting it BEFORE the send meant a
      // submit-then-navigate that lost the (preflighted, keepalive) fetch
      // suppressed every retry for the rest of the tab session. outboxSeen()
      // covers both halves of that: signatures acked earlier in this session,
      // and signatures already enqueued on THIS page (which is what stops
      // same-page duplicate emits). A queued-but-unacked signature from an
      // earlier page does not suppress — the dataLayer copy below is the old
      // pipeline's own retry, and on the ledger side the queued entry keeps its
      // original event_id, so the door collapses the repeat on its idempotency
      // key rather than on a body hash that a retry would have changed.
      var dedupSignature = DEDUP_IDENTIFY + signature;
      if (BcOutbox.seen(dedupSignature)) return;

      var identityPayload = addRawCaptureFields({
        // Hotfix 2026-05-19: renamed from 'bc_identify' to 'bc_identify_emit'.
        // The downstream Web GTM Tag #185 calls gtag('event','bc_identify',...)
        // which itself dataLayer.push({event:'bc_identify',...}). With the
        // same event name both upstream and downstream of the trigger, Custom
        // Event trigger #184 fires on the tag's own output → infinite loop →
        // tab unresponsive. Renaming the storefront-emitted event breaks the
        // loop while preserving the canonical 'bc_identify' name on the
        // outbound sGTM event. Requires Web GTM trigger #184 to update its
        // event-name match to 'bc_identify_emit'.
        event: 'bc_identify_emit',
        bc_aid: aid || null,
        bc_aid_candidate: aid ? null : candidate,
        email: accumulated.email || null,
        // Present only when the typo map changed something (D7).
        email_raw: accumulated.email_raw || null,
        klaviyo_id: accumulated.klaviyo_id || null,
        klaviyo_exchange_id: accumulated.klaviyo_exchange_id || null,
        phone: accumulated.phone || null,
        first_name: accumulated.first_name || null,
        last_name: accumulated.last_name || null,
        // Crisp conversation id, captured by Watcher D once $crisp is ready.
        // BCRM /api/identify uses this to PATCH the visible Crisp inbox row
        // with the resolved nickname/email/phone — turns "Visitor #1234" into
        // the real name. Requires sGTM Tag #194 to forward the field.
        crisp_session_id: accumulated.crisp_session_id || null,
        bc_identify_source: effectiveSource  // canonical enum: 'kla_cookie' | 'kla_identify' | 'shopify_customer' | 'crisp_email' | 'newsletter_form'
      });
      // DUAL SINK, unchanged (D11): the dataLayer copy feeds the old pipeline
      // (Web GTM #185 → sGTM #194 → BCRM /api/identify) and stays exactly where
      // it was; only the ledger copy moved into the outbox.
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(identityPayload);
      BcOutbox.enqueue({
        kind: 'bc_identify_emit',
        fields: identityPayload,
        ts: Date.now(),
        ctx: { dedup: dedupSignature }
      });

      // Sync any newly-acquired Klaviyo identifiers into Crisp session:data
      // so support agents see klaviyo_id / klaviyo_exchange_id in the live
      // chat dossier without a backend lookup. Only fires if Crisp is
      // already hooked AND the field gained a new value since the last push.
      // Crisp learns email/phone from its own user:email/phone:changed
      // events — we never push those directions.
      if (window.$crisp && window.$crisp.__bcHooked) {
        var crispDelta = [];
        if (accumulated.klaviyo_id && accumulated.klaviyo_id !== lastCrispPushed.klaviyo_id) {
          crispDelta.push(['klaviyo_id', accumulated.klaviyo_id]);
          lastCrispPushed.klaviyo_id = accumulated.klaviyo_id;
        }
        if (accumulated.klaviyo_exchange_id && accumulated.klaviyo_exchange_id !== lastCrispPushed.klaviyo_exchange_id) {
          crispDelta.push(['klaviyo_exchange_id', accumulated.klaviyo_exchange_id]);
          lastCrispPushed.klaviyo_exchange_id = accumulated.klaviyo_exchange_id;
        }
        if (crispDelta.length) {
          try { window.$crisp.push(['set', 'session:data', [crispDelta]]); }
          catch (e) { noteModuleError('identify_crisp', e); }
        }
      }
    }

    // ---- Watcher A: __kla_id cookie poll --------------------------------
    // Safety net for identification flows where the JS SDK hooks below
    // don't fire (e.g. Klaviyo email-link landings that restore __kla_id
    // from server state). Emits exchange_id, NOT profile_id; the backend
    // resolves it. The 2-minute ceiling is gone (D18): the cookie is minted
    // at signup, and a shopper who browses for ten minutes first — #1152 —
    // used to sign up long after this stopped looking.
    pollUntilDone('identify_kla_cookie', function () {
      var exchangeId = parseKlaExchangeId(readCookie('__kla_id'));
      if (!exchangeId) return false;
      emit('kla_cookie', { klaviyo_exchange_id: exchangeId });
      return true;
    });

    // ---- Watcher A2: `_kx` on the landing URL ----------------------------
    // A Klaviyo email click lands with `?_kx=<exchange_id>`. The raw capture
    // keeps it, but it only rode along on identifies some OTHER watcher made;
    // the gate in emit() never counted it. So when __kla_id never surfaced an
    // $exchange_id (iPhone Safari, 2026-10-27 welcome-email landing), nothing
    // identified the visitor. The URL token is the same exchange id the cookie
    // carries, so it goes out under the same 'kla_cookie' label (/api/identify
    // takes a fixed label list; the label is audit-only), with the backend's
    // allowlist.
    // Polls only until a bc_aid (or bootstrap candidate) exists, since emit()
    // silently drops an identify made before then.
    pollUntilDone('identify_kla_link', function () {
      var landingKx = String(rawCapture()._kx || '');
      if (!/^[A-Za-z0-9_\-=.~%]{1,256}$/.test(landingKx)) return true;
      if (!currentBcAid() && !getBootstrapCandidate()) return false;
      emit('kla_cookie', { klaviyo_exchange_id: landingKx });
      return true;
    });

    // ---- Watcher B: Klaviyo SDK chokepoint hook -------------------------
    // Hook the single function every Klaviyo identify/subscribe call routes
    // through: window._klOnsite.push. Confirmed via live Playwright probe
    // on 2026-05-19 — a real popup form submit dispatches:
    //   _klOnsite.push(['sendCachedEvents', companyId, { $email, $first_name,
    //                                                    $last_name, $exchange_id }])
    // and programmatic klaviyo.identify({...}) dispatches:
    //   _klOnsite.push(['identify', { $email, ... }])
    //
    // Why not hook _learnq.push / klaviyo.identify / klaviyo.push directly
    // (the previous strategy):
    //   - Klaviyo's SDK overwrites those methods during init, undoing our
    //     wrappers (verified: __bcHooked: true but toString() showed native
    //     Klaviyo source, not our wrap).
    //   - _klOnsite is the dispatcher core — Klaviyo's own methods call
    //     INTO it; they don't replace it. Hooking it after SDK init is
    //     stable.
    //
    // Why not a DOM submit listener:
    //   - Klaviyo popups use type="button" + React onClick (no <form>.submit
    //     event ever fires). DOM submit listener would never see the
    //     identification.
    function handleIdentifyPayload(p) {
      if (!p || typeof p !== 'object') return;
      // Backfill klaviyo_exchange_id from the __kla_id cookie when the
      // identify payload doesn't carry $exchange_id (e.g., Klaviyo's
      // cached-identify replay on page load doesn't include it). This
      // ensures Watcher B's emit is strictly richer than Watcher A's
      // exchange_id-only emit — so when the new suppress-thinner-overwrite
      // guard in emit() kicks in, we don't lose the exchange_id.
      var cookieExchangeId = parseKlaExchangeId(readCookie('__kla_id'));
      var email = p.$email || p.email || null;
      var firstName = p.$first_name || p.first_name || null;
      var lastName = p.$last_name || p.last_name || null;
      var phone = p.$phone_number || p.phone_number || null;

      // 2026-05-24 fix: Crisp doesn't materialize the server-side session
      // until the visitor opens chat or sends a message. So the REST PATCH
      // path (storefront → sGTM → Worker → /api/identify → tagCrispSession)
      // hits "conversation_not_found" for popup-only signups. The chatbox
      // SDK's `set:user:email` and `set:user:nickname` calls are persisted
      // by Crisp locally and applied when the session materializes — so
      // the inbox row carries the identity from the very first message.
      // Calling these synchronously on every identify is idempotent.
      try {
        if (window.$crisp) {
          if (email) window.$crisp.push(['set', 'user:email', [email]]);
          var nickname = [firstName, lastName].filter(Boolean).join(' ').trim();
          if (nickname) window.$crisp.push(['set', 'user:nickname', [nickname]]);
          else if (email) window.$crisp.push(['set', 'user:nickname', [email]]);
          if (phone) window.$crisp.push(['set', 'user:phone', [phone]]);
        }
      } catch (e) { noteModuleError('identify_crisp', e); }

      emit('kla_identify', {
        email: email,
        phone: phone,
        first_name: firstName,
        last_name: lastName,
        klaviyo_id: p.$id || p.id || p.profile_id || null,
        // A value already collected (e.g. Watcher A2's landing `_kx`) beats the
        // cookie fallback, so a stale __kla_id cannot flip it back.
        klaviyo_exchange_id: p.$exchange_id || accumulated.klaviyo_exchange_id || cookieExchangeId || null
      });
    }

    // ---- Watcher B2: Klaviyo form `submit` / `stepSubmit` window event -----
    // On many submits Klaviyo posts straight to client/subscriptions and never
    // routes the email through _klOnsite.push, so Watcher B sees nothing. That
    // is always the case when static-tracking.klaviyo.com is blocked
    // (EasyPrivacy, used by most iOS content blockers): the form still loads
    // from static.klaviyo.com and submits, but the analytics module that would
    // dispatch identify / sendCachedEvents never runs. Verified in WebKit
    // iPhone emulation 2026-10-27. The `klaviyoForms` event comes from the
    // forms code itself and carries the submitted fields in detail.metaData.
    window.addEventListener('klaviyoForms', function (ev) {
      try {
        var d = ev && ev.detail;
        if (!d || (d.type !== 'submit' && d.type !== 'stepSubmit')) return;
        var m = d.metaData || {};
        // Only the contact fields: raw form properties (a custom `id` field)
        // must not reach handleIdentifyPayload's klaviyo_id lookup.
        var email = m.$email || m.email || null;
        var phone = m.$phone_number || m.phone_number || null;
        if (email || phone) {
          handleIdentifyPayload({
            $email: email,
            $phone_number: phone,
            $first_name: m.$first_name || m.first_name || null,
            $last_name: m.$last_name || m.last_name || null
          });
        }
      } catch (e) { noteModuleError('identify_kla_form_event', e); }
    });

    function installKlaviyoChokepointHook() {
      // The 60s install ceiling is gone (D18). The cadence is slower than the
      // old 250ms tick, which the interaction retry more than covers: any click
      // or keystroke re-attempts the install from a capture-phase listener, so
      // typing in the popup installs the hook well before the submit button's
      // own handler ever calls into the dispatcher.
      pollUntilDone('identify_kla_chokepoint', function () {
        var ko = window._klOnsite;
        // Readiness signal: dispatcher present AND identify method bound
        // (the second clause means Klaviyo's SDK has finished registering
        // its methods on _klOnsite, so our wrap won't get clobbered).
        if (!ko || typeof ko.push !== 'function' || typeof ko.identify !== 'function') {
          return false;
        }
        if (ko.__bcChokepointWrapped) return true;
        ko.__bcChokepointWrapped = true;

        // Outbound: stage bc_aid as a custom property on Klaviyo's session
        // so it lands on any newly-identified profile (form signups,
        // programmatic identify calls). When called BEFORE a profile is
        // identified, Klaviyo stores the property in its cached-events
        // queue and merges it onto the profile created at form-submit
        // time. When called AFTER (already-known visitor), it updates the
        // existing profile in place. Idempotent — re-pushing on subsequent
        // pageloads with the same bc_aid value is a no-op for Klaviyo.
        //
        // Why a custom property (`bc_aid`) and NOT $external_id: per
        // [[klaviyo-phantom-profiles-fix]] memory, mapping external_id to
        // bc_aid creates blank billable profiles for anonymous visitors.
        // A plain custom property carries the visitor link without that
        // side effect. Backend server-side stitcher reads
        // attributes.properties.bc_aid from the Klaviyo Profile API.
        //
        // D16: this writes the legacy slot AND this browser context's own key,
        // so a second context can no longer erase the first. The payload is
        // built by the shared sender — see sendKlaviyoIdentify.
        sendKlaviyoIdentify('chokepoint', { push: ko.push.bind(ko) });

        try {
          var origPush = ko.push.bind(ko);
          ko.push = function (args) {
            try {
              if (Array.isArray(args) && args.length >= 2) {
                var method = args[0];
                // Payload position differs by method (verified via live
                // Playwright probe 2026-05-19):
                //   ['identify', {payload}]                       payload at [1]
                //   ['sendCachedEvents', companyId, {payload}]    payload at [2]
                var p = null;
                if (method === 'identify' && typeof args[1] === 'object' && args[1]) {
                  p = args[1];
                } else if (method === 'sendCachedEvents' && typeof args[2] === 'object' && args[2]) {
                  p = args[2];
                }
                if (p) {
                  var email = p.$email || p.email;
                  var phone = p.$phone_number || p.phone_number;
                  // Only emit when there's an actual identifier — bare
                  // exchange-id-only ticks (sendCachedEvents flushes after
                  // identify) are Watcher A's job, and emit() dedups anyway.
                  if (email || phone) {
                    handleIdentifyPayload(p);
                  }
                }
              }
            } catch (e) { noteModuleError('identify_kla_chokepoint', e); }
            return origPush(args);
          };
        } catch (e) {
          // Wrap failed — revert flag and report NOT done, so the poll (which no
          // longer has a ceiling to run out) tries again. Should never happen.
          try { ko.__bcChokepointWrapped = false; } catch (e2) {}
          return false;
        }
        return true;
      });
    }

    installKlaviyoChokepointHook();

    // D20 canary: Klaviyo's SDK is on the page but our chokepoint never took.
    // The hook depends on `_klOnsite` being both present AND carrying a bound
    // `identify` method — private internals that a Klaviyo release can rename
    // without any error being thrown anywhere. Capture would simply stop, with
    // a clean console, exactly as it did for two weeks before the live probe
    // in 2026-05. Flag only; the server reads it as a rate across sessions.
    try {
      setTimeout(function () {
        try {
          var sdkPresent = !!(window._klOnsite || window.klaviyo);
          var installed = !!(window._klOnsite && window._klOnsite.__bcChokepointWrapped);
          if (sdkPresent && !installed) setTelemetryFlag('canary_chokepoint');
        } catch (e) { noteModuleError('identify_kla_chokepoint', e); }
      }, 300000);
    } catch (e) { /* scheduling itself failed — an environment fault, not a
                     contract fault, so deliberately not counted (see the
                     errors-vs-flags note at setTelemetryFlag). */ }

    // ---- Watcher C: Shopify logged-in customer --------------------------
    // Was Liquid-gated inline; now reads window.__bcCfg.customer (populated
    // by the inline core from the Liquid customer object). Fires immediately
    // on page load with the full identity tuple when a customer is logged in.
    if (customer) {
      emit('shopify_customer', {
        email: customer.email,
        phone: customer.phone,
        first_name: customer.firstName,
        last_name: customer.lastName
      });
    }

    // ---- Watcher D: Crisp chat ------------------------------------------
    // Once Crisp SDK loads, stamp bc_aid + page context into session:data
    // and subscribe to email/phone identification events. We deliberately
    // do NOT subscribe to session:loaded — it fires without an email and
    // would just be gated out by emit(). The bc_aid we stamp into Crisp's
    // session:data is read back server-side by the Crisp webhook handler
    // on session:set_email (see §12.4 of the plan).
    function installCrispBridge() {
      if (!window.$crisp || window.$crisp.__bcHooked) return;
      try {
        // Canonical Crisp syntax for setting multiple session:data fields
        // is THREE opening brackets: the third positional arg is an array
        // wrapping a single tuple-list. Round-2 review's "remove a bracket"
        // suggestion was wrong; round-4 research confirmed triple brackets.
        // Build the tuple list dynamically so Klaviyo identifiers
        // (klaviyo_id / klaviyo_exchange_id) ride along when accumulated
        // state already has them at install time — covers the common case
        // where Klaviyo's watchers fire before Crisp finishes loading. The
        // post-emit sync in emit() handles the reverse order.
        var sessionDataTuples = [
          ['bc_aid', window.__bcAid],
          ['bc_landing_page', location.href],
          ['bc_referrer', document.referrer || '']
        ];
        if (accumulated.klaviyo_id) {
          sessionDataTuples.push(['klaviyo_id', accumulated.klaviyo_id]);
          lastCrispPushed.klaviyo_id = accumulated.klaviyo_id;
        }
        if (accumulated.klaviyo_exchange_id) {
          sessionDataTuples.push(['klaviyo_exchange_id', accumulated.klaviyo_exchange_id]);
          lastCrispPushed.klaviyo_exchange_id = accumulated.klaviyo_exchange_id;
        }
        window.$crisp.push(['set', 'session:data', [sessionDataTuples]]);

        // Capture Crisp's session identifier so BCRM can PATCH the visible
        // inbox row (nickname/email/phone) the moment any watcher resolves
        // an identity. Two capture paths:
        //   - synchronous get() at install time (Crisp had session ready)
        //   - on('session:loaded') for the case where the session is created
        //     after the bridge installs.
        // Both write into accumulated; emit() carries crisp_session_id along
        // with the existing identity payload. We do NOT call emit() purely
        // from the session being created — the gate (email/klaviyo_*) must
        // still pass, otherwise anonymous sessions would spam bc_identify_emit.
        try {
          var initialSid = window.$crisp.get && window.$crisp.get('session:identifier');
          if (initialSid) accumulated.crisp_session_id = initialSid;
        } catch (e) { noteModuleError('identify_crisp', e); }
        window.$crisp.push(['on', 'session:loaded', function (data) {
          try {
            // Crisp passes the session_id directly as a STRING (not wrapped
            // in an array). data[0] on a string would return the first
            // character ("s") — the 2026-05-24 bug that corrupted every
            // downstream system. Always fall through to $crisp.get() which
            // is the documented, type-stable accessor.
            var sid = window.$crisp.get && window.$crisp.get('session:identifier');
            if (!sid && typeof data === 'string') sid = data;
            if (!sid || sid === accumulated.crisp_session_id) return;
            accumulated.crisp_session_id = sid;
            // Only re-emit if we already had an actionable identifier.
            // Otherwise wait for user:email:changed / Klaviyo watchers to
            // contribute one; emit() will then naturally include the sid.
            if (accumulated.email || accumulated.klaviyo_id || accumulated.klaviyo_exchange_id) {
              emit(establishedSource || 'crisp_email', {});
            }
          } catch (e) { noteModuleError('identify_crisp', e); }
        }]);

        // user:email:changed — primary identification trigger. Forward any
        // previously-stashed Crisp phone so the backend gets the full
        // identity tuple in one POST.
        window.$crisp.push(['on', 'user:email:changed', function (email) {
          emit('crisp_email', { email: email, phone: window.__bcCrispPhone || null });
        }]);

        // user:phone:changed — phone alone doesn't satisfy the emit() gate,
        // so stash it for the next email-change emit to pick up via the
        // closure above.
        window.$crisp.push(['on', 'user:phone:changed', function (phone) {
          window.__bcCrispPhone = phone;
        }]);

        window.$crisp.__bcHooked = true;
      } catch (e) { noteModuleError('identify_crisp', e); }
    }

    // Crisp loads async; poll until hooked. The 30s ceiling is gone (D18) —
    // a visitor who opens chat five minutes in is exactly the visitor we most
    // want identified. Stops only once the bridge reports itself hooked, so a
    // half-failed install is retried instead of silently abandoned.
    pollUntilDone('identify_crisp', function () {
      if (!window.$crisp) return false;
      installCrispBridge();
      return !!window.$crisp.__bcHooked;
    });

    // ---- Watcher E: native Shopify newsletter / contact forms -----------
    // Added 2026-07-24 (order #1114 in-app-browser audit). The theme's
    // `{% form 'customer' %}` newsletter blocks (FooterNewsletter,
    // ContactFooter, newsletter/email-signup sections) and `{% form
    // 'contact' %}` all POST `contact[email]` full-page to Shopify —
    // server-side, so NO Klaviyo surface ever sees the browser: Shopify
    // creates the customer, its Klaviyo integration receives the email via
    // Customer Webhook with no device context, and the submitting visitor
    // stays anonymous forever. That is exactly how buyer #1114's Facebook
    // in-app-browser session (the true Facebook-paid first touch) stayed
    // unjoined while the order credited google/organic.
    //
    // Fix: treat a native-form email submit like every other watcher —
    // emit through the shared accumulate/dedup path so the ledger links
    // THIS browser's bc_aid to the email deterministically (the ingress
    // door encrypts `email` at the edge; the consumer's ordinary identity
    // matrix does the join — no new server-side rules).
    //
    // Listener notes:
    //   - capture phase on document: runs ahead of any theme handler that
    //     might stopPropagation, and ahead of the full-page navigation.
    //     the outbox's beacon-first transport survives the unload.
    //   - selector is the INPUT (`[name="contact[email]"]`), not a form id:
    //     covers all four current customer-form renderings plus the plain
    //     contact form, and survives section renames.
    //   - fires on submit ATTEMPT: a server-side validation reject still
    //     means the shopper deliberately typed + submitted this email;
    //     non-normalizable values are dropped fail-soft downstream.
    document.addEventListener('submit', function (ev) {
      try {
        var form = ev.target;
        if (!form || !form.querySelector) return;
        var input = form.querySelector('input[name="contact[email]"]');
        if (!input) return;
        // The per-watcher Shopify.customerPrivacy gate that used to sit here
        // (audit 2026-07-24 #8) is gone (D9). It made this ONE watcher stricter
        // than the four beside it and the abandonment identifier, which is an
        // asymmetry nobody chose. Consent is now one constant at the outbox
        // (CONSENT_GATE, default off per owner) applied to every path equally,
        // and the consent state rides every envelope regardless.
        var email = String(input.value || '').trim();
        // Minimal plausibility gate only — emit() lowercases and runs the
        // typo guard; the ledger's normalizeEmail is the real arbiter.
        if (!email || email.indexOf('@') < 1 || email.length > 320) return;
        emit('newsletter_form', { email: email });
      } catch (e) { noteModuleError('identify_newsletter_form', e); }
    }, true);
  })();

  // ─────────────────────────────────────────────────────────────────────
  // BcKlaviyoFormWatcher — abandonment detection for Klaviyo popup signups.
  // Added 2026-05-23 (post-Playwright investigation).
  //
  // Two jobs:
  //
  // 1. Push `bc_klaviyo_form_engaged` dataLayer event when the user types
  //    in a Klaviyo popup field, capturing the typed-but-not-submitted
  //    state (field_name, field_type, field_raw_value, email_current,
  //    phone_current, form_id). Web GTM Tag #64 (v54) maps these to
  //    event params; sGTM Tag #207 (v58) forwards them to BCRM. Coalesced
  //    to 800ms so rapid typing doesn't N× the dataLayer push.
  //
  // 2. Push the typed email to Klaviyo as an identify ONLY when the user
  //    has clearly walked away from the form, not while they're still
  //    typing. Three triggers:
  //
  //    - `pagehide` / `beforeunload` — user closes tab or navigates away
  //    - `visibilitychange` → hidden — user switches tab or app (mobile)
  //    - Click on the popup's close X / NO THANKS / dismiss buttons
  //
  //    This avoids the failure mode where a half-typed email gets pushed
  //    to Klaviyo just because the user paused for 2 seconds. Klaviyo
  //    identify creates the profile but does NOT subscribe to marketing
  //    (subscription requires the form's submit + consent gate).
  //
  //    For the unload path, `_learnq.push` may not flush before the browser
  //    kills the page. We belt-and-braces with `navigator.sendBeacon` to
  //    Klaviyo's client profile-import endpoint — sendBeacon has a small
  //    reserved budget that survives unload.
  //
  // Per-email session dedupe stops the identify from firing repeatedly
  // for the same email. Tightly scoped to `.klaviyo-form` ancestor —
  // never runs on checkout/PDP/contact forms.
  // ─────────────────────────────────────────────────────────────────────
  guard('klaviyo_form_watcher', function BcKlaviyoFormWatcher() {
    if (window.__bcKlaviyoFormWatcher) return;
    window.__bcKlaviyoFormWatcher = true;
    window.dataLayer = window.dataLayer || [];

    var EMAIL_RE = /^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/;
    var ENGAGED_COALESCE_MS = 800;
    // The Klaviyo company id moved to sendKlaviyoIdentify with the beacon it
    // belongs to — one sender, one copy of the constant.

    var identifiedEmails = new Set();
    var lastEngagedPush = { ts: 0, key: "" };
    // D20 canary state: did this page ever actually see a Klaviyo form, and
    // did the engagement watcher ever capture anything from it?
    var sawKlaviyoForm = false;
    var engagedCaptures = 0;

    function readKlaviyoInputs(formElement) {
      if (!formElement || !formElement.querySelector) {
        return { email: null, phone: null };
      }
      var emailInput = formElement.querySelector(
        'input[type="email"], input[name="email"], input[name*="email" i]'
      );
      var phoneInput = formElement.querySelector(
        'input[type="tel"], input[name="phone-number"], input[name*="phone" i]'
      );
      return {
        email: emailInput && emailInput.value ? String(emailInput.value).trim() : null,
        phone: phoneInput && phoneInput.value ? String(phoneInput.value).trim() : null,
      };
    }

    function findKlaviyoForm(target) {
      // gtm.element is sometimes the form, sometimes a child field.
      // Walk up until we hit a .klaviyo-form ancestor or document.body.
      var node = target;
      while (node && node !== document.body) {
        if (node.classList && node.classList.contains && node.classList.contains("klaviyo-form")) {
          return node;
        }
        node = node.parentNode;
      }
      // Fallback: first visible Klaviyo form on the page.
      return document.querySelector(".klaviyo-form");
    }

    // Every path that looks for the form routes through here, so "did a form
    // ever exist on this page" is answered without a poll of its own.
    function noteFormSeen(formEl) {
      if (formEl) sawKlaviyoForm = true;
      return formEl;
    }

    function pushEngaged(detail) {
      // Coalesce repeated pushes for the same field+value within
      // ENGAGED_COALESCE_MS. Web GTM's auto-event listener already
      // debounces gtm.formInteract but we belt-and-braces.
      var now = Date.now();
      var key = (detail.field_name || "") + ":" + (detail.field_raw_value || "");
      if (now - lastEngagedPush.ts < ENGAGED_COALESCE_MS && lastEngagedPush.key === key) return;
      lastEngagedPush = { ts: now, key: key };
      engagedCaptures++;

      var aid = currentBcAid();
      window.dataLayer.push(addRawCaptureFields({
        event: "bc_klaviyo_form_engaged",
        bc_aid: aid || null,
        bc_aid_candidate: aid ? null : (getBootstrapCandidate() || null),
        field_name: detail.field_name || null,
        field_type: detail.field_type || null,
        field_raw_value: detail.field_raw_value || null,
        email_current: detail.email_current || null,
        phone_current: detail.phone_current || null,
        form_id: detail.form_id || null,
      }));
    }

    function autoIdentify(email, opts) {
      if (!email) return;
      var lower = email.toLowerCase();
      if (identifiedEmails.has(lower)) return;
      if (!EMAIL_RE.test(email)) return;
      identifiedEmails.add(lower);
      var useBeacon = !!(opts && opts.useBeacon);

      // Primary path: _learnq queue. Klaviyo's SDK drains async. Works
      // fine when the user is still on the page (popup dismiss case).
      // bc_aid rides as a custom profile property so BCRM's
      // klaviyo-new-profiles cron can stitch the (visitor_id ↔ identity) link
      // via extractBcAid() — without it the cron creates an identity by email
      // only, never calls backfillEvents, and the pre-signup browsing history
      // stays orphaned at identity_id=NULL. The Worker forward-POSTs
      // bc_identify to BCRM's /api/identify for the realtime path; this
      // property is the belt-and-braces fallback for when that POST fails.
      sendKlaviyoIdentify('learnq', { email: email });

      // Mirror the popup email straight into Crisp so the inbox row renames
      // from "Visitor #1234" instantly, instead of waiting for the storefront
      // → sGTM → BCRM → Crisp round-trip. The CRM-side tagCrispSession is
      // still the canonical write (it also stamps identity_id + segments);
      // this is just optimistic UI for the rep watching the inbox.
      try {
        if (window.$crisp) {
          window.$crisp.push(["set", "user:email", [email]]);
        }
      } catch (e) { noteModuleError('klaviyo_form_watcher', e); }

      // Unload-safe path: sendBeacon to Klaviyo's client profile-import
      // endpoint. sendBeacon has a small browser-reserved budget that
      // survives pagehide/beforeunload — a regular fetch from _learnq
      // would be killed. Only used on unload paths, since it incurs a
      // duplicate POST when _learnq already worked.
      if (useBeacon) sendKlaviyoIdentify('beacon', { email: email });
    }

    // `preread` carries values captured BEFORE the form left the DOM. The
    // structural dismiss fallback below has to wait 2s to learn whether the
    // form actually disappeared, by which point reading it is impossible —
    // so it hands us what the shopper had typed at click time.
    function trySubmitAbandonedEmail(reason, preread) {
      var formEl = noteFormSeen(document.querySelector(".klaviyo-form"));
      if (!formEl && !preread) return;
      var current = preread || readKlaviyoInputs(formEl);
      if (!current.email || !EMAIL_RE.test(current.email)) return;

      // Emit a final engaged event so the BCRM dossier shows the exact
      // abandonment trigger. `field_name = 'abandonment_trigger'` is a
      // sentinel value the cron can use to distinguish from regular
      // typing-time engagements.
      var formId = current.form_id || (formEl && (
        formEl.getAttribute("data-testid") ||
        formEl.getAttribute("data-form-id") ||
        formEl.id
      )) || null;
      // Bypass the per-field coalesce since this is a distinct signal.
      var aid = currentBcAid();
      window.dataLayer.push(addRawCaptureFields({
        event: "bc_klaviyo_form_engaged",
        bc_aid: aid || null,
        bc_aid_candidate: aid ? null : (getBootstrapCandidate() || null),
        field_name: "abandonment_trigger",
        field_type: reason,
        field_raw_value: null,
        email_current: current.email,
        phone_current: current.phone,
        form_id: formId,
      }));

      // Push to Klaviyo. Use beacon when leaving the page; regular
      // _learnq.push when the user is just dismissing the popup.
      var useBeacon =
        reason === "pagehide" ||
        reason === "beforeunload" ||
        reason === "visibility_hidden";
      autoIdentify(current.email, { useBeacon: useBeacon });
    }

    function handleFormInteract(payload) {
      var classes = payload && payload["gtm.elementClasses"];
      if (typeof classes !== "string" || classes.indexOf("klaviyo-form") === -1) return;

      var fieldEl = payload["gtm.interactedFormField"];
      var fieldName = payload["gtm.interactedFormFieldName"] || null;
      var fieldType = payload["gtm.interactedFormFieldType"] || null;
      // Web GTM puts the typed value on `rawValue` of gtm.interactedFormField.
      var fieldRawValue =
        (fieldEl && (fieldEl.rawValue ||
          (fieldEl.value !== undefined ? fieldEl.value : null))) || null;

      var formEl = noteFormSeen(findKlaviyoForm(payload["gtm.element"]));
      var current = readKlaviyoInputs(formEl);
      var formId =
        (formEl &&
          (formEl.getAttribute && (
            formEl.getAttribute("data-testid") ||
            formEl.getAttribute("data-form-id") ||
            formEl.id
          ))) || null;

      pushEngaged({
        field_name: fieldName,
        field_type: fieldType,
        field_raw_value: fieldRawValue,
        email_current: current.email,
        phone_current: current.phone,
        form_id: formId,
      });

      // No identify firing here — the identify only happens on actual
      // abandonment (popup close / page unload / tab hidden), not on
      // every keystroke. A half-typed email mid-session should NEVER
      // create a Klaviyo profile.
    }

    // Patch dataLayer.push to observe gtm.formInteract events. The
    // existing storefront script already patches dataLayer for its
    // own purposes; this is a SECOND independent patch that wraps
    // whatever is current at the time of run. Safe because we never
    // shadow the original — we only observe and pass through.
    if (window.dataLayer && !window.dataLayer.__bcKlaviyoWatcherPatched) {
      var orig = window.dataLayer.push.bind(window.dataLayer);
      window.dataLayer.push = function () {
        for (var i = 0; i < arguments.length; i++) {
          var p = arguments[i];
          if (p && p.event === "gtm.formInteract") {
            try { handleFormInteract(p); } catch (e) { /* never break dataLayer */ }
          }
        }
        return orig.apply(null, arguments);
      };
      window.dataLayer.__bcKlaviyoWatcherPatched = true;
    }

    // Abandonment triggers — fire identify only when the user has
    // clearly walked away from the popup.

    // Delegated click handler for the popup's close X / NO THANKS /
    // dismiss buttons. Klaviyo doesn't expose a "popup dismissed"
    // hook, so we sniff the rendered controls.
    document.addEventListener(
      "click",
      function (e) {
        var t = e && e.target;
        if (!t || !t.closest) return;
        var btn = t.closest("button, a");
        if (!btn) return;
        var formEl = noteFormSeen(btn.closest(".klaviyo-form"));
        if (!formEl) return;
        var ariaLabel = btn.getAttribute("aria-label");
        var label = (ariaLabel || btn.textContent || "").toLowerCase().trim();
        var isDismiss =
          label === "close" ||
          label.indexOf("close dialog") !== -1 ||
          label.indexOf("no, thanks") !== -1 ||
          label.indexOf("no thanks") !== -1 ||
          label.indexOf("dismiss") !== -1;
        if (isDismiss) {
          try { trySubmitAbandonedEmail("popup_dismiss"); }
          catch (err) { noteModuleError('klaviyo_form_watcher', err); }
          return;
        }
        // D20 structural fallback. The sniff above reads ENGLISH labels off a
        // third-party DOM — one Klaviyo copy change, one localised storefront,
        // and every dismiss goes unnoticed with nothing thrown. So: a labelled
        // control inside the form that we did NOT recognise still counts as a
        // dismiss if the form is gone from the DOM 2s later. Structure, not
        // vocabulary. The typed values are read NOW, because by the time we
        // know the form vanished there is nothing left to read.
        if (!ariaLabel) return;
        var preread = readKlaviyoInputs(formEl);
        if (!preread.email) return;
        preread.form_id = (formEl.getAttribute("data-testid") ||
          formEl.getAttribute("data-form-id") || formEl.id || null);
        setTimeout(function () {
          try {
            var stillPresent = document.contains
              ? document.contains(formEl)
              : !!document.querySelector(".klaviyo-form");
            if (stillPresent) return;   // not a dismiss — the form is still up
            setTelemetryFlag('canary_dismiss_structural');
            trySubmitAbandonedEmail("popup_dismiss_structural", preread);
          } catch (err) { noteModuleError('klaviyo_form_watcher', err); }
        }, 2000);
      },
      true,
    );

    // Page unload signals. `pagehide` is the modern reliable one
    // (including bfcache navigation). `beforeunload` is a legacy
    // fallback that some browsers still need. visibilitychange catches
    // mobile tab/app switches.
    //
    // pagehide also settles the form-watch canary (D20): a Klaviyo form was on
    // the page and the engagement watcher captured NOTHING from it. Read as a
    // rate across sessions, not a per-visitor verdict — plenty of shoppers
    // ignore a popup entirely, which looks identical from here. What the rate
    // catches is the day gtm.formInteract or `.klaviyo-form` stops matching and
    // the figure goes to 100%.
    window.addEventListener("pagehide", function () {
      try {
        noteFormSeen(document.querySelector(".klaviyo-form"));
        if (sawKlaviyoForm && engagedCaptures === 0) setTelemetryFlag('canary_form_watch');
      } catch (e) { noteModuleError('klaviyo_form_watcher', e); }
      try { trySubmitAbandonedEmail("pagehide"); }
      catch (e) { noteModuleError('klaviyo_form_watcher', e); }
    });
    window.addEventListener("beforeunload", function () {
      try { trySubmitAbandonedEmail("beforeunload"); }
      catch (e) { noteModuleError('klaviyo_form_watcher', e); }
    });
    document.addEventListener("visibilitychange", function () {
      if (document.visibilityState === "hidden") {
        try { trySubmitAbandonedEmail("visibility_hidden"); }
        catch (e) { noteModuleError('klaviyo_form_watcher', e); }
      }
    });
  })();
})();
